import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button, Drawer, Space, Typography, Tooltip, Spin, message, Slider, Divider, Select, Dropdown, Input, Popover, InputNumber } from 'antd';
import {
    CloseOutlined,
    PlusOutlined,
    MinusOutlined,
    UndoOutlined,
    RedoOutlined,
    DownloadOutlined,
    ExpandOutlined,
    CompressOutlined,
    AimOutlined,
    BorderOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    DeleteOutlined,
    GroupOutlined,
    UngroupOutlined,
    GatewayOutlined,
    PictureOutlined,
    ScissorOutlined,
    FolderOpenOutlined,
    LockOutlined,
    UnlockOutlined,
    FullscreenOutlined,
    DragOutlined,
    FileTextOutlined,
    CheckOutlined,
    VerticalAlignTopOutlined,
    StarOutlined,
    RadiusSettingOutlined,
    HighlightOutlined,
    ClearOutlined,
    FontSizeOutlined
} from '@ant-design/icons';
import { Canvas, FabricImage, FabricObject, Textbox, util, Circle, Polygon, Path, Line, ActiveSelection } from 'fabric';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import App from 'antd/es/app';
import { useQuery } from '@tanstack/react-query';
import { useInfiniteMediaGenerations, useInfiniteStorageFiles } from '../../../api/canvasApi';
import { LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';
import { useGeneration } from '../../../context/GenerationContext';
import PinterestPanel from './PinterestPanel';
import MidjourneyPanel from './MidjourneyPanel';
import SrefStylesTab from './SrefStylesTab';
import MediaPreviewModal from './MediaPreviewModal';
import GooEditorPanel from './GooEditorPanel';
import EffectsPanel from './EffectsPanel';
import HalftoneEditorPanel from './HalftoneEditorPanel';
import DitherEditorPanel from './DitherEditorPanel';
import GradientMapEditorPanel from './GradientMapEditorPanel';
import ChromaticAberrationEditorPanel from './ChromaticAberrationEditorPanel';
import GrainEditorPanel from './GrainEditorPanel';
import GlitchEditorPanel from './GlitchEditorPanel';
import AsciiEditorPanel from './AsciiEditorPanel';
import PatternEditorPanel from './PatternEditorPanel';
import TopoLineEditorPanel from './TopoLineEditorPanel';
import TypeControlsPanel from './TypeControlsPanel';
import TypographyPresetsPanel from './TypographyPresetsPanel';
import { useCanvasMedia } from '../../../api/canvasApi';
import useCanvasHistory from '../hooks/useCanvasHistory';

const { Text } = Typography;

// Shared constant - must match Editor.js header height
const HEADER_HEIGHT = 64;

// Scrapbook Tool Modes
const TOOLS = {
    SELECT: 'select',
    POINT_SELECT: 'point_select',
    TEXT_SELECT: 'text_select',
    BOX_SELECT: 'box_select',
    DEPTH_ERASER: 'depth_eraser',
    BORDER_PUNCH: 'border_punch',
    STAMP_PUNCH: 'stamp_punch',
    GOO_PUNCH: 'goo_punch',
    INPAINT: 'inpaint',
    TYPE: 'type'
};

// Punch shape definitions
const PUNCH_SHAPES = {
    // Stamp punch shapes (centered at 0,0)
    circle: { type: 'circle', label: 'Circle' },
    star: { type: 'star', label: 'Star', points: 5 },
    heart: { type: 'heart', label: 'Heart' },
    hexagon: { type: 'polygon', label: 'Hexagon', sides: 6 },
    triangle: { type: 'polygon', label: 'Triangle', sides: 3 },
    // Border punch patterns
    wavy: { type: 'border', label: 'Wavy Edge', pattern: 'wavy' },
    scalloped: { type: 'border', label: 'Scalloped Edge', pattern: 'scalloped' },
    zigzag: { type: 'border', label: 'Zigzag Edge', pattern: 'zigzag' },
    torn: { type: 'border', label: 'Torn Paper', pattern: 'torn' },
    postage: { type: 'border', label: 'Postage Stamp', pattern: 'postage' }
};

/**
 * Apply a black/white mask to an image using canvas compositing
 * 
 * @param originalUrl - URL of the original image
 * @param maskUrl - URL of the mask image
 * @param token - Auth token for proxy requests
 * @param invertMaskLogic - If true, treat white as subject (Moondream format). 
 *                          If false, treat transparent/black as subject (SAM2 format).
 * 
 * SAM2 mask format: transparent = subject, white/opaque = background
 * Moondream mask format: white = subject, black = background
 * 
 * Returns a data URL of the cutout PNG
 */
const applyMaskToImage = async (originalUrl, maskUrl, token, invertMaskLogic = false) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

    // Helper to load image via backend proxy to bypass CORS
    // Data URLs are handled directly (no proxy needed)
    const loadImageViaProxy = async (url) => {
        // Data URLs can be loaded directly without CORS issues
        if (url.startsWith('data:')) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = () => reject(new Error('Failed to load data URL image'));
                img.src = url;
            });
        }

        // Use our backend proxy which adds proper CORS headers
        const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(url)}`;
        const response = await fetch(proxyUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!response.ok) throw new Error(`Proxy failed: ${response.statusText}`);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(blobUrl);
                resolve(img);
            };
            img.onerror = () => {
                URL.revokeObjectURL(blobUrl);
                reject(new Error('Failed to load image'));
            };
            img.src = blobUrl;
        });
    };

    // Load both images via proxy
    const [originalImg, maskImg] = await Promise.all([
        loadImageViaProxy(originalUrl),
        loadImageViaProxy(maskUrl)
    ]);

    // Create canvas matching original image size
    const canvas = document.createElement('canvas');
    canvas.width = originalImg.width;
    canvas.height = originalImg.height;
    const ctx = canvas.getContext('2d');

    // Both SAM2 and Moondream return: white=subject, black=background
    // invertMaskLogic=true (standard): 
    //   Subject cutout: destination-in (keeps white areas = subject)
    //   Background cutout: destination-out (removes white = removes subject)
    // invertMaskLogic=false (legacy): opposite behavior

    const subjectOp = invertMaskLogic ? 'destination-in' : 'destination-out';
    const backgroundOp = invertMaskLogic ? 'destination-out' : 'destination-in';

    // CRITICAL: Moondream masks are RGB (white/black) where both colors are OPAQUE
    // Canvas compositing uses alpha channel, NOT color values
    // We need to convert: white pixels → opaque, black pixels → transparent

    // Create processed mask with proper alpha channel
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = originalImg.width;
    maskCanvas.height = originalImg.height;
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.drawImage(maskImg, 0, 0, originalImg.width, originalImg.height);

    // Get pixel data and convert color to alpha
    const maskImageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    const data = maskImageData.data;
    for (let i = 0; i < data.length; i += 4) {
        // Use the red channel (or any, since it's grayscale) as alpha
        // White (255) → opaque (255 alpha), Black (0) → transparent (0 alpha)
        const brightness = data[i]; // Red channel value
        data[i + 3] = brightness; // Set alpha to brightness
    }
    maskCtx.putImageData(maskImageData, 0, 0);

    // SUBJECT cutout
    ctx.drawImage(originalImg, 0, 0);
    ctx.globalCompositeOperation = subjectOp;
    ctx.drawImage(maskCanvas, 0, 0);
    const normalCutout = canvas.toDataURL('image/png');

    // BACKGROUND cutout
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(originalImg, 0, 0);
    ctx.globalCompositeOperation = backgroundOp;
    ctx.drawImage(maskCanvas, 0, 0);
    const invertedCutout = canvas.toDataURL('image/png');

    return { normalCutout, invertedCutout };
};

/**
 * Trim transparent pixels from an image to get tight bounding box
 * Returns a new data URL with only the visible content
 */
const trimTransparent = (imageDataUrl) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // Find bounds of non-transparent pixels
            let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
            let hasContent = false;

            for (let y = 0; y < canvas.height; y++) {
                for (let x = 0; x < canvas.width; x++) {
                    const i = (y * canvas.width + x) * 4;
                    const alpha = data[i + 3];

                    // Consider pixel visible if alpha > threshold
                    if (alpha > 10) {
                        hasContent = true;
                        minX = Math.min(minX, x);
                        minY = Math.min(minY, y);
                        maxX = Math.max(maxX, x);
                        maxY = Math.max(maxY, y);
                    }
                }
            }

            if (!hasContent) {
                // No content found, return original
                resolve({ dataUrl: imageDataUrl, width: img.width, height: img.height, offsetX: 0, offsetY: 0 });
                return;
            }

            // Add 1px padding to avoid edge clipping
            const padding = 1;
            minX = Math.max(0, minX - padding);
            minY = Math.max(0, minY - padding);
            maxX = Math.min(canvas.width - 1, maxX + padding);
            maxY = Math.min(canvas.height - 1, maxY + padding);

            const trimmedWidth = maxX - minX + 1;
            const trimmedHeight = maxY - minY + 1;

            // Create trimmed canvas
            const trimmedCanvas = document.createElement('canvas');
            trimmedCanvas.width = trimmedWidth;
            trimmedCanvas.height = trimmedHeight;
            const trimmedCtx = trimmedCanvas.getContext('2d');

            // Draw only the content region
            trimmedCtx.drawImage(
                canvas,
                minX, minY, trimmedWidth, trimmedHeight,
                0, 0, trimmedWidth, trimmedHeight
            );

            resolve({
                dataUrl: trimmedCanvas.toDataURL('image/png'),
                width: trimmedWidth,
                height: trimmedHeight,
                offsetX: minX,
                offsetY: minY,
                originalWidth: img.width,
                originalHeight: img.height
            });
        };
        img.src = imageDataUrl;
    });
};
const ScrapbookPanel = ({
    open,
    onClose,
    initialImage,
    canvasId: propCanvasId,
    onAddToCanvas
}) => {
    const { projectId: routeCanvasId } = useParams();
    const canvasId = propCanvasId || routeCanvasId;
    const { message: messageApi } = App.useApp();
    const { currentUser } = useAuth();
    const [token, setToken] = useState(null);

    // Get auth token for proxied image requests
    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setToken);
        }
    }, [currentUser]);

    const canvasRef = useRef(null);
    const fabricRef = useRef(null);
    const containerRef = useRef(null);
    const { trackGeneration } = useGeneration();

    // State
    const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
    const activeToolRef = useRef(TOOLS.SELECT); // Ref to track current tool for event handlers
    const [layers, setLayers] = useState([]);
    const [selectedLayer, setSelectedLayer] = useState(null);
    const [selectedLayerIds, setSelectedLayerIds] = useState([]); // Support multi-select highlighting
    const [selectedObjects, setSelectedObjects] = useState([]);

    // Canvas history (undo/redo)
    const { saveSnapshot, undo, redo, canUndo, canRedo, isRestoring } = useCanvasHistory(fabricRef, setLayers, setSelectedLayer);
    const [loading, setLoading] = useState(false);
    const [loadingLayers, setLoadingLayers] = useState({}); // Per-layer loading state: { [layerId]: true/false }
    const [segmentPoints, setSegmentPoints] = useState([]);
    const [maskPreview, setMaskPreview] = useState(null);
    const [invertedMaskPreview, setInvertedMaskPreview] = useState(null);
    const [isMaskInverted, setIsMaskInverted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [showAssets, setShowAssets] = useState(false);
    const [promptText, setPromptText] = useState(''); // For Moondream text segmentation
    const [segModel, setSegModel] = useState('moondream'); // 'moondream' | 'sam3' | 'qwenLayered'
    const [sam3MaxMasks, setSam3MaxMasks] = useState(3); // 1–32
    const [numLayers, setNumLayers] = useState(4); // 1–10 for Qwen Image Layered
    const [canvasAspectRatio, setCanvasAspectRatio] = useState('free'); // 'free', '1:1', '16:9', '9:16', '4:5', '4:3'
    const [assetTypeFilter, setAssetTypeFilter] = useState('all'); // 'all', 'images', 'videos', 'audio'
    const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'generated', 'uploads', 'cutouts'
    const [canvasFilter, setCanvasFilter] = useState('all'); // 'all' or specific canvasId
    const [assetsTab, setAssetsTab] = useState('library'); // 'library' or 'inspiration'
    const [inspirationMedia, setInspirationMedia] = useState([]);

    // Sref Preview Modal State
    const [srefPreviewVisible, setSrefPreviewVisible] = useState(false);
    const [srefPreviewIndex, setSrefPreviewIndex] = useState(0);
    const [srefPreviewList, setSrefPreviewList] = useState([]);

    // Resource panels collapse state (start collapsed)
    const [pinterestCollapsed, setPinterestCollapsed] = useState(true);
    const [midjourneyCollapsed, setMidjourneyCollapsed] = useState(true);
    const [resourceAssetsCollapsed, setResourceAssetsCollapsed] = useState(true);
    const [selectedAsset, setSelectedAsset] = useState(null); // For Assets panel preview
    const [inspirationFolderFilter, setInspirationFolderFilter] = useState('all');
    const inspirationFolders = [
        { value: 'all', label: 'All Folders' },
        { value: 'branding', label: 'Branding' },
        { value: 'effects', label: 'Effects' },
        { value: 'midjourney', label: 'Midjourney' },
        { value: 'people', label: 'People' },
        { value: 'places', label: 'Places' },
        { value: 'things', label: 'Things' }
    ];
    const clipboardRef = useRef(null); // For copy/paste layer operations

    // Depth Eraser state
    const [depthMapUrl, setDepthMapUrl] = useState(null);
    const [depthThreshold, setDepthThreshold] = useState(1.0); // 1.0 = all visible, 0 = erase all background
    const [depthMapLoading, setDepthMapLoading] = useState(false);
    const [depthMapData, setDepthMapData] = useState(null); // Cached depth pixel data

    // Punch tool state
    const [selectedPunchShape, setSelectedPunchShape] = useState('circle');
    const [punchShapeSize, setPunchShapeSize] = useState(100);
    const [punchRotation, setPunchRotation] = useState(0); // Rotation in degrees
    const [selectedBorderEdges, setSelectedBorderEdges] = useState({ top: false, right: false, bottom: true, left: false });
    const [selectedBorderPattern, setSelectedBorderPattern] = useState('scalloped'); // Border punch pattern (wavy, scalloped, zigzag, torn, postage)
    const [punchPreviewPos, setPunchPreviewPos] = useState(null); // {x, y} for stamp cursor preview
    const punchPreviewRef = useRef(null); // Ref to preview shape on canvas
    const borderPreviewRef = useRef(null); // Ref to border preview lines
    const depthPreviewRef = useRef(null); // Ref to depth mask preview object
    const depthWorkerRef = useRef(null); // Web Worker for depth mask pixel processing
    const depthGenerationRef = useRef(0); // Generation counter to discard stale worker results
    const lastPunchPosRef = useRef(null); // Track last mouse position for preview updates
    const gooPreviewCacheRef = useRef({ id: null, img: null }); // Cache loaded goo SVG for preview
    const snapGuideLinesRef = useRef([]); // Snapping guide lines rendered on canvas
    const [snappingEnabled, setSnappingEnabled] = useState(true); // Toggle snapping on/off
    const [customGuides, setCustomGuides] = useState([]); // [{type:'h'|'v', pos: number}]
    const customGuidesRef = useRef([]); // Always-current ref for snap handler
    customGuidesRef.current = customGuides; // Sync ref on every render
    const customGuideLinesRef = useRef([]); // Persistent guide line objects on canvas

    // Grid overlay state
    const [gridEnabled, setGridEnabled] = useState(false);
    const [gridSpacing, setGridSpacing] = useState(50);
    const [gridOpacity, setGridOpacity] = useState(0.15);
    const gridSettingsRef = useRef({ enabled: false, spacing: 50, opacity: 0.15 });
    gridSettingsRef.current = { enabled: gridEnabled, spacing: gridSpacing, opacity: gridOpacity };

    // Inpaint tool state
    // Goo Editor state
    const [gooEditorVisible, setGooEditorVisible] = useState(false);
    const [patternStandaloneVisible, setPatternStandaloneVisible] = useState(false);
    const [topoLineVisible, setTopoLineVisible] = useState(false);
    const [effectsPanelVisible, setEffectsPanelVisible] = useState(false);
    const [activeEffect, setActiveEffect] = useState(null); // 'halftone' | 'dither' | 'gradient-map' | 'chromatic' | 'grain' | null
    const [selectedTextObject, setSelectedTextObject] = useState(null);
    const [savedGooShapes, setSavedGooShapes] = useState([]); // Array of { id, svgString, thumbnailUrl, dbId }

    // Typography panel & typographic grid state
    const [typographyPanelVisible, setTypographyPanelVisible] = useState(false);
    const [typoGrid, setTypoGrid] = useState({
        enabled: false, cols: 3, rows: 4, colGutter: 20, rowGutter: 20,
        marginTop: 40, marginRight: 40, marginBottom: 40, marginLeft: 40,
        color: '#a78bfa', opacity: 0.25, snap: true,
    });
    const typoGridRef = useRef(null);
    typoGridRef.current = typoGrid;

    // Load custom font families from backend and register as @font-face
    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/font-families');
                const families = Array.isArray(res.data) ? res.data : [];
                console.log('[Scrapbook] Font families:', families.length);
                for (const fam of families) {
                    if (!fam.fontFileUrl) continue;
                    const familyName = fam.metadata?.familyName || fam.name || 'Untitled';
                    try {
                        const font = new FontFace(familyName, `url(${fam.fontFileUrl})`);
                        const loaded = await font.load();
                        document.fonts.add(loaded);
                        console.log(`[Scrapbook] Loaded font: ${familyName}`);
                    } catch (err) {
                        console.warn(`[Scrapbook] Failed to load font "${familyName}":`, err);
                    }
                }
            } catch (err) {
                console.error('[Scrapbook] Failed to fetch font families:', err);
            }
        })();
    }, []);

    // Load saved goo punch shapes from backend
    useEffect(() => {
        if (!canvasId) return;
        (async () => {
            try {
                const res = await api.get(`/media-generations?canvas_id=${canvasId}`);
                const gooPunches = (res.data || [])
                    .filter(m => m.model === 'goo-punch')
                    .map(m => ({
                        id: m.generation_id || m.id,
                        svgString: m.metadata?.svgString || '',
                        thumbnailUrl: m.url,
                        dbId: m.id
                    }));
                if (gooPunches.length > 0) {
                    setSavedGooShapes(gooPunches);
                }
            } catch (e) {
                console.warn('Failed to load goo punch shapes:', e);
            }
        })();
    }, [canvasId]);

    const [inpaintPrompt, setInpaintPrompt] = useState('');
    const [inpaintBrushSize, setInpaintBrushSize] = useState(40);
    const [isDrawingMask, setIsDrawingMask] = useState(false);
    const inpaintMaskCanvasRef = useRef(null); // Hidden canvas for mask data
    const inpaintMaskDataRef = useRef(null); // Store mask image data URL
    const inpaintBrushPreviewRef = useRef(null); // Circle cursor preview

    // Aspect ratio / artboard presets with fixed pixel dimensions
    const ASPECT_PRESETS = [
        // General
        { key: 'free', label: 'Free', ratio: null, group: 'General' },
        { key: '1:1', label: '1:1 Square', w: 1080, h: 1080, group: 'General' },
        { key: '16:9', label: '16:9 Landscape', w: 1920, h: 1080, group: 'General' },
        { key: '9:16', label: '9:16 Portrait', w: 1080, h: 1920, group: 'General' },
        // Social
        { key: 'ig-post', label: 'IG Post', w: 1080, h: 1080, group: 'Social' },
        { key: 'ig-story', label: 'IG Story', w: 1080, h: 1920, group: 'Social' },
        { key: 'ig-portrait', label: 'IG Portrait', w: 1080, h: 1350, group: 'Social' },
        { key: 'x-header', label: 'X Header', w: 1500, h: 500, group: 'Social' },
        { key: 'yt-thumb', label: 'YT Thumbnail', w: 1280, h: 720, group: 'Social' },
        { key: 'fb-cover', label: 'FB Cover', w: 820, h: 312, group: 'Social' },
        // Print
        { key: 'a4', label: 'A4 (300dpi)', w: 2480, h: 3508, group: 'Print' },
        { key: 'letter', label: 'US Letter', w: 2550, h: 3300, group: 'Print' },
        { key: 'poster', label: 'Poster 18×24', w: 5400, h: 7200, group: 'Print' },
        // Screen
        { key: 'hd', label: '1920×1080 HD', w: 1920, h: 1080, group: 'Screen' },
        { key: '4k', label: '3840×2160 4K', w: 3840, h: 2160, group: 'Screen' },
        { key: 'iphone', label: 'iPhone Wallpaper', w: 1290, h: 2796, group: 'Screen' },
    ];

    // Per-layer loading helpers
    const setLayerLoading = useCallback((layerId, isLoading) => {
        setLoadingLayers(prev => ({ ...prev, [layerId]: isLoading }));
    }, []);

    const isLayerLoading = useCallback((layerId) => {
        return loadingLayers[layerId] || false;
    }, [loadingLayers]);

    // Map plural filter keys to singular Firestore type values
    const mediaTypeParam = useMemo(() => {
        const map = { images: 'image', videos: 'video', audio: 'audio' };
        return map[assetTypeFilter] || null;
    }, [assetTypeFilter]);

    // Infinite query for media generations (paginated)
    const {
        data: mediaPages,
        fetchNextPage: fetchNextMedia,
        hasNextPage: hasMoreMedia,
        isFetchingNextPage: isFetchingMoreMedia
    } = useInfiniteMediaGenerations(canvasFilter === 'all' ? null : canvasFilter, 24, mediaTypeParam);

    // Fetch available canvases for filtering
    const { data: canvasesData } = useQuery({
        queryKey: ['canvases'],
        queryFn: async () => {
            const response = await api.get('/canvases');
            return response.data;
        },
        staleTime: 60000,
        enabled: open
    });

    // Flatten paginated media into a single array
    const allMedia = useMemo(() => {
        if (!mediaPages?.pages) return [];
        return mediaPages.pages.flatMap(page => page.data || []);
    }, [mediaPages]);

    // Infinite query for inspiration media (paginated)
    const {
        data: inspirationPages,
        fetchNextPage: fetchNextInspiration,
        hasNextPage: hasMoreInspiration,
        isFetchingNextPage: isFetchingMoreInspiration
    } = useInfiniteStorageFiles('inspiration-from-the-web', 24);

    // Flatten and normalize inspiration data
    const inspirationData = useMemo(() => {
        if (!inspirationPages?.pages) return [];
        return inspirationPages.pages.flatMap(page => (page.data || []).map(file => {
            const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
            let type = 'image';
            if (['mp4', 'webm', 'mov'].includes(ext)) type = 'video';
            return {
                id: file.path,
                type,
                url: file.url,
                src: file.url,
                label: file.name,
                path: file.path,
                folder: file.folder || 'uncategorized',
                notes: file.customMetadata?.notes || ''
            };
        }));
    }, [inspirationPages]);

    // Filter inspiration media by folder
    const filteredInspiration = useMemo(() => {
        return inspirationData.filter(item => inspirationFolderFilter === 'all' || item.folder === inspirationFolderFilter);
    }, [inspirationData, inspirationFolderFilter]);

    // Infinite scroll hook using callback refs
    const useInfiniteScroll = (fetchNext, hasMore, isFetching) => {
        const [node, setNode] = useState(null);
        const sentinelRef = useCallback((el) => setNode(el), []);

        useEffect(() => {
            if (!node || !hasMore || isFetching) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) fetchNext();
                },
                { threshold: 0, rootMargin: '0px 0px 400px 0px' }
            );

            observer.observe(node);
            return () => observer.disconnect();
        }, [node, fetchNext, hasMore, isFetching]);

        return sentinelRef;
    };

    const mediaSentinelRef = useInfiniteScroll(fetchNextMedia, hasMoreMedia, isFetchingMoreMedia);
    const inspirationSentinelRef = useInfiniteScroll(fetchNextInspiration, hasMoreInspiration, isFetchingMoreInspiration);

    // Filter by canvas
    const canvasFilteredMedia = allMedia.filter(item => {
        if (canvasFilter === 'all') return true;
        return item.canvas_id === canvasFilter;
    });

    // Filter by source
    const sourceFilteredMedia = canvasFilteredMedia.filter(item => {
        if (sourceFilter === 'all') return true;
        if (sourceFilter === 'generated') return item.source === 'generated' || item.provider;
        if (sourceFilter === 'uploads') return item.source === 'upload' || (!item.provider && !item.source);
        if (sourceFilter === 'cutouts') return item.provider === 'scrapbook' || item.model?.includes('punch') || item.prompt?.toLowerCase().includes('cutout');
        return true;
    });

    // Filter by type
    const filteredMedia = sourceFilteredMedia.filter(item => {
        if (assetTypeFilter === 'all') return item.url;
        if (assetTypeFilter === 'images') return item.type === 'image' && item.url;
        if (assetTypeFilter === 'videos') return item.type === 'video' && item.url;
        if (assetTypeFilter === 'audio') return item.type === 'audio' && item.url;
        return item.url;
    });

    // Keep imageAssets for backward compatibility
    const imageAssets = allMedia.filter(item => item.type === 'image' && item.url);

    // Handle adding image from resource panels to scrapbook canvas
    const handleAddToScrapbookCanvas = useCallback(async (imageData) => {
        if (!fabricRef.current) return;

        const canvas = fabricRef.current;
        const imageUrl = imageData.src || imageData.url;

        if (!imageUrl) {
            messageApi.warning('No image URL provided');
            return;
        }

        try {
            setLoading(true);

            // Proxy the image if it's an external URL
            const API_BASE = api.defaults.baseURL;
            const proxyUrl = imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')
                ? imageUrl
                : `${API_BASE}/storage/proxy?url=${encodeURIComponent(imageUrl)}${token ? `&token=${token}` : ''}`;

            const img = await FabricImage.fromURL(proxyUrl, { crossOrigin: 'anonymous' });

            if (img) {
                // Scale to fit canvas while maintaining aspect ratio
                const maxSize = Math.min(canvas.width, canvas.height) * 0.6;
                const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
                img.scale(scale);

                // Center on canvas
                img.set({
                    left: canvas.width / 2 - (img.width * scale) / 2,
                    top: canvas.height / 2 - (img.height * scale) / 2,
                    selectable: true
                });

                canvas.add(img);
                canvas.setActiveObject(img);
                canvas.renderAll();

                // Add as layer
                const layerId = `layer_${Date.now()}`;
                const layer = {
                    id: layerId,
                    name: imageData.label || imageData.title || 'Imported Image',
                    object: img,
                    visible: true,
                    locked: false,
                    opacity: 1,
                    sourceUrl: imageUrl
                };
                setLayers(prev => [...prev, layer]);
                setSelectedLayer(layerId);

                messageApi.success('Added to canvas!');
            }
        } catch (error) {
            console.error('Failed to add image to canvas:', error);
            messageApi.error('Failed to add image');
        } finally {
            setLoading(false);
        }
    }, [token, messageApi]);

    // Initialize Fabric.js canvas
    useEffect(() => {
        if (!open || !canvasRef.current) return;

        // Clean up previous canvas
        if (fabricRef.current) {
            fabricRef.current.dispose();
        }

        // Create new canvas
        const canvas = new Canvas(canvasRef.current, {
            width: containerRef.current?.clientWidth || 800,
            height: containerRef.current?.clientHeight || 600,
            backgroundColor: '#1a1a2e',
            selection: activeTool === TOOLS.SELECT
        });

        fabricRef.current = canvas;

        // Load initial image if provided
        if (initialImage) {
            loadImage(initialImage);
        }

        // Handle canvas click for point selection
        canvas.on('mouse:down', handleCanvasClick);

        // Sync canvas selection with layers panel
        const handleSelectionChange = () => {
            const selected = canvas.getActiveObjects();
            setSelectedObjects([...selected]); // Create new array to force update

            if (selected.length > 0) {
                const selectedObject = selected[0];
                // Detect text objects
                if (selectedObject instanceof Textbox) {
                    setSelectedTextObject(selectedObject);
                } else {
                    setSelectedTextObject(null);
                }
                // Find the layer that contains this object
                setLayers(currentLayers => {
                    const ids = [];
                    // Find all layers corresponding to selected objects
                    selected.forEach(obj => {
                        const l = currentLayers.find(layer => layer.object === obj);
                        if (l) ids.push(l.id);
                    });

                    setSelectedLayerIds(ids);

                    // Maintain single selection state for compatibility (use first or none)
                    if (ids.length === 1) {
                        setSelectedLayer(ids[0]);
                    } else if (ids.length > 1) {
                        // If multiple, maybe clear single select or keep first?
                        // Keeping first allows some single-layer panels to work on the primary selection
                        setSelectedLayer(ids[0]);
                    } else {
                        setSelectedLayer(null);
                    }

                    return currentLayers;
                });
            }
        };

        const handleSelectionCleared = () => {
            setSelectedLayer(null);
            setSelectedLayerIds([]);
            setSelectedTextObject(null);
            setSelectedObjects([]);
        };

        canvas.on('selection:created', handleSelectionChange);
        canvas.on('selection:updated', handleSelectionChange);
        canvas.on('selection:cleared', handleSelectionCleared);

        // Mouse wheel zoom (centered on cursor)
        const handleMouseWheel = (opt) => {
            const delta = opt.e.deltaY;
            let zoom = canvas.getZoom();
            zoom *= 0.999 ** delta;
            // Clamp zoom between 0.1 and 10
            zoom = Math.min(Math.max(0.1, zoom), 10);

            // Zoom to point (cursor position)
            const point = { x: opt.e.offsetX, y: opt.e.offsetY };
            canvas.zoomToPoint(point, zoom);

            opt.e.preventDefault();
            opt.e.stopPropagation();
        };
        canvas.on('mouse:wheel', handleMouseWheel);

        // Pan with Alt+drag or middle mouse button
        let isPanning = false;
        let lastPosX = 0;
        let lastPosY = 0;

        const handlePanStart = (opt) => {
            const evt = opt.e;
            // Alt+click or middle mouse button
            if (evt.altKey || evt.button === 1) {
                isPanning = true;
                canvas.selection = false;
                lastPosX = evt.clientX;
                lastPosY = evt.clientY;
                canvas.setCursor('grabbing');
            }
        };

        const handlePanMove = (opt) => {
            if (isPanning) {
                const evt = opt.e;
                const vpt = canvas.viewportTransform;
                vpt[4] += evt.clientX - lastPosX;
                vpt[5] += evt.clientY - lastPosY;
                lastPosX = evt.clientX;
                lastPosY = evt.clientY;
                canvas.requestRenderAll();
            }
        };

        const handlePanEnd = () => {
            if (isPanning) {
                isPanning = false;
                canvas.selection = activeTool === TOOLS.SELECT;
                canvas.setCursor('default');
            }
        };

        canvas.on('mouse:down', handlePanStart);
        canvas.on('mouse:move', handlePanMove);
        canvas.on('mouse:up', handlePanEnd);
        // ─── Smart Snapping Guides ───
        const SNAP_DIST = 10; // px threshold for snapping
        const GUIDE_COLOR = '#ff00ff';

        const clearSnapGuides = () => {
            snapGuideLinesRef.current.forEach(line => {
                try { canvas.remove(line); } catch (e) { /* ignore */ }
            });
            snapGuideLinesRef.current = [];
        };

        const addGuideLine = (x1, y1, x2, y2) => {
            const line = new Line([x1, y1, x2, y2], {
                stroke: GUIDE_COLOR,
                strokeWidth: 1,
                strokeDashArray: [4, 4],
                selectable: false,
                evented: false,
                excludeFromExport: true,
                opacity: 0.8,
            });
            canvas.add(line);
            snapGuideLinesRef.current.push(line);
        };

        const handleObjectMoving = (e) => {
            const obj = e.target;
            if (!obj) return;
            if (obj._isCustomGuide) return; // Don't snap guide lines themselves

            clearSnapGuides();

            const cw = canvas.width;
            const ch = canvas.height;

            // Use getCenterPoint + getScaledWidth/Height for stable snap (no feedback-loop shaking)
            const w = obj.getScaledWidth();
            const h = obj.getScaledHeight();
            const center = obj.getCenterPoint();
            const objL = center.x - w / 2;
            const objR = center.x + w / 2;
            const objT = center.y - h / 2;
            const objB = center.y + h / 2;

            // Build set of objects being moved (includes ActiveSelection members)
            const movingSet = new Set();
            movingSet.add(obj);
            if (obj.type === 'activeselection' && obj._objects) {
                obj._objects.forEach(o => movingSet.add(o));
            }

            // Build snap targets
            const targetsX = [0, cw / 2, cw];
            const targetsY = [0, ch / 2, ch];

            // Include custom guides as snap targets (read from ref to avoid stale closure)
            customGuidesRef.current.forEach(g => {
                if (g.type === 'v') targetsX.push(g.pos);
                if (g.type === 'h') targetsY.push(g.pos);
            });

            // Include typographic grid edges as snap targets
            const tg = typoGridRef.current;
            if (tg && tg.enabled && tg.snap) {
                // Use media bounds for grid snap targets
                const mediaObjs = canvas.getObjects().filter(o => {
                    if (o._isCustomGuide || o.excludeFromExport) return false;
                    if (o.type === 'textbox' || o.type === 'text' || o.type === 'i-text') return false;
                    if (!o.visible) return false;
                    return true;
                });
                let mbX = 0, mbY = 0, mbW = cw, mbH = ch;
                if (mediaObjs.length > 0) {
                    let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
                    mediaObjs.forEach(o => {
                        const sw = o.getScaledWidth();
                        const sh = o.getScaledHeight();
                        const c2 = o.getCenterPoint();
                        const l = c2.x - sw / 2, r = c2.x + sw / 2;
                        const t = c2.y - sh / 2, b = c2.y + sh / 2;
                        if (l < mnX) mnX = l; if (t < mnY) mnY = t;
                        if (r > mxX) mxX = r; if (b > mxY) mxY = b;
                    });
                    mbX = mnX; mbY = mnY; mbW = mxX - mnX; mbH = mxY - mnY;
                }
                const mL = tg.marginLeft, mR = tg.marginRight, mT = tg.marginTop, mB = tg.marginBottom;
                const innerX = mbX + mL, innerY = mbY + mT;
                const innerW = mbW - mL - mR, innerH = mbH - mT - mB;
                // Margin lines
                targetsX.push(mbX + mL, mbX + mbW - mR);
                targetsY.push(mbY + mT, mbY + mbH - mB);
                // Column edges
                const totalColGut = Math.max(0, tg.cols - 1) * tg.colGutter;
                const colW = (innerW - totalColGut) / tg.cols;
                for (let i = 0; i < tg.cols; i++) {
                    const x = innerX + i * (colW + tg.colGutter);
                    targetsX.push(x, x + colW);
                }
                // Row edges
                const totalRowGut = Math.max(0, tg.rows - 1) * tg.rowGutter;
                const rowH = (innerH - totalRowGut) / tg.rows;
                for (let i = 0; i < tg.rows; i++) {
                    const y = innerY + i * (rowH + tg.rowGutter);
                    targetsY.push(y, y + rowH);
                }
            }

            canvas.getObjects().forEach(other => {
                if (movingSet.has(other) || other.excludeFromExport || other._isCustomGuide || !other.selectable) return;
                const ow = other.getScaledWidth();
                const oh = other.getScaledHeight();
                const oc = other.getCenterPoint();
                targetsX.push(oc.x - ow / 2, oc.x, oc.x + ow / 2);
                targetsY.push(oc.y - oh / 2, oc.y, oc.y + oh / 2);
            });

            // Find best X snap
            let bestXDelta = null;
            let bestXGuide = null;
            for (const objEdge of [objL, center.x, objR]) {
                for (const t of targetsX) {
                    const d = t - objEdge;
                    if (Math.abs(d) < SNAP_DIST && (bestXDelta === null || Math.abs(d) < Math.abs(bestXDelta))) {
                        bestXDelta = d;
                        bestXGuide = t;
                    }
                }
            }

            // Find best Y snap
            let bestYDelta = null;
            let bestYGuide = null;
            for (const objEdge of [objT, center.y, objB]) {
                for (const t of targetsY) {
                    const d = t - objEdge;
                    if (Math.abs(d) < SNAP_DIST && (bestYDelta === null || Math.abs(d) < Math.abs(bestYDelta))) {
                        bestYDelta = d;
                        bestYGuide = t;
                    }
                }
            }

            // Apply snaps and draw guides
            if (bestXDelta !== null) {
                obj.set('left', obj.left + bestXDelta);
                addGuideLine(bestXGuide, 0, bestXGuide, ch);
            }
            if (bestYDelta !== null) {
                obj.set('top', obj.top + bestYDelta);
                addGuideLine(0, bestYGuide, cw, bestYGuide);
            }

            if (bestXDelta !== null || bestYDelta !== null) {
                obj.setCoords();
            }
        };

        const handleObjectModified = () => {
            clearSnapGuides();
        };

        canvas.on('object:moving', handleObjectMoving);
        canvas.on('object:modified', handleObjectModified);
        // Also clear on selection cleared
        canvas.on('selection:cleared', clearSnapGuides);

        // History: save snapshots on meaningful actions
        const handleHistoryModified = () => { saveSnapshot(); };
        canvas.on('object:modified', handleHistoryModified);
        canvas.on('object:added', handleHistoryModified);
        canvas.on('object:removed', handleHistoryModified);

        // Save initial snapshot
        setTimeout(() => saveSnapshot(), 500);

        return () => {
            if (fabricRef.current) {
                fabricRef.current.off('mouse:down', handleCanvasClick);
                fabricRef.current.off('selection:created', handleSelectionChange);
                fabricRef.current.off('selection:updated', handleSelectionChange);
                fabricRef.current.off('selection:cleared', handleSelectionCleared);
                fabricRef.current.off('mouse:wheel', handleMouseWheel);
                fabricRef.current.off('mouse:down', handlePanStart);
                fabricRef.current.off('mouse:move', handlePanMove);
                fabricRef.current.off('mouse:up', handlePanEnd);
                fabricRef.current.off('object:moving', handleObjectMoving);
                fabricRef.current.off('object:modified', handleObjectModified);
                fabricRef.current.off('selection:cleared', clearSnapGuides);
                fabricRef.current.off('object:modified', handleHistoryModified);
                fabricRef.current.off('object:added', handleHistoryModified);
                fabricRef.current.off('object:removed', handleHistoryModified);
                fabricRef.current.dispose();
                fabricRef.current = null;
            }
        };
    }, [open]);

    // Depth mask Web Worker lifecycle
    useEffect(() => {
        try {
            depthWorkerRef.current = new Worker(
                new URL('../workers/depthMaskWorker.js', import.meta.url)
            );
        } catch (err) {
            console.warn('Failed to create depth mask worker, will fall back to main thread:', err);
        }
        return () => {
            if (depthWorkerRef.current) {
                depthWorkerRef.current.terminate();
                depthWorkerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (fabricRef.current) {
            fabricRef.current.requestRenderAll();
        }
    }, [gridEnabled, gridSpacing, gridOpacity]);

    // Grid overlay rendering via after:render (draws on context, not Fabric objects = export safe)
    useEffect(() => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        if (!canvas) return;

        // Trigger render when settings change
        canvas.requestRenderAll();

        const drawGrid = () => {
            const { enabled, spacing, opacity } = gridSettingsRef.current;
            if (!enabled) return;

            const ctx = canvas.getContext();
            const vpt = canvas.viewportTransform;
            const cw = canvas.width;
            const ch = canvas.height;

            ctx.save();
            ctx.strokeStyle = '#8b949e';
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = opacity;

            // Account for viewport transform (zoom/pan)
            const zoom = vpt[0];
            const panX = vpt[4];
            const panY = vpt[5];

            // Calculate visible area in canvas coordinates
            const startX = Math.floor(-panX / zoom / spacing) * spacing;
            const startY = Math.floor(-panY / zoom / spacing) * spacing;
            const endX = Math.ceil((cw - panX) / zoom / spacing) * spacing;
            const endY = Math.ceil((ch - panY) / zoom / spacing) * spacing;

            ctx.beginPath();
            // Vertical lines
            for (let x = startX; x <= endX; x += spacing) {
                const sx = x * zoom + panX;
                ctx.moveTo(sx, 0);
                ctx.lineTo(sx, ch);
            }
            // Horizontal lines
            for (let y = startY; y <= endY; y += spacing) {
                const sy = y * zoom + panY;
                ctx.moveTo(0, sy);
                ctx.lineTo(cw, sy);
            }
            ctx.stroke();
            ctx.restore();
        };

        // ─── Typographic Grid Overlay (columns / rows / gutters / margins) ───
        // Helper: compute bounding box of media (non-text, non-guide) objects
        const getMediaBounds = () => {
            const objects = canvas.getObjects().filter(o => {
                if (o._isCustomGuide || o.excludeFromExport) return false;
                if (o.type === 'textbox' || o.type === 'text' || o.type === 'i-text') return false;
                if (!o.visible) return false;
                return true;
            });
            if (objects.length === 0) return { x: 0, y: 0, w: canvas.width, h: canvas.height };

            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            objects.forEach(o => {
                const sw = o.getScaledWidth();
                const sh = o.getScaledHeight();
                const center = o.getCenterPoint();
                const l = center.x - sw / 2;
                const t = center.y - sh / 2;
                const r = center.x + sw / 2;
                const b = center.y + sh / 2;
                if (l < minX) minX = l;
                if (t < minY) minY = t;
                if (r > maxX) maxX = r;
                if (b > maxY) maxY = b;
            });
            return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
        };

        const drawTypoGrid = () => {
            const grid = typoGridRef.current;
            if (!grid || !grid.enabled) return;

            const ctx = canvas.getContext();
            const vpt = canvas.viewportTransform;
            const zoom = vpt[0];
            const panX = vpt[4];
            const panY = vpt[5];

            // Get media bounds as the reference frame
            const mb = getMediaBounds();
            const baseX = mb.x;
            const baseY = mb.y;
            const baseW = mb.w;
            const baseH = mb.h;

            ctx.save();

            // Canvas-space transform helpers
            const tx = (x) => x * zoom + panX;
            const ty = (y) => y * zoom + panY;

            const mT = grid.marginTop;
            const mR = grid.marginRight;
            const mB = grid.marginBottom;
            const mL = grid.marginLeft;

            // Inner area after margins (relative to media bounds)
            const innerX = baseX + mL;
            const innerY = baseY + mT;
            const innerW = baseW - mL - mR;
            const innerH = baseH - mT - mB;

            // Draw margin lines (dashed)
            ctx.strokeStyle = grid.color;
            ctx.globalAlpha = grid.opacity * 0.6;
            ctx.lineWidth = 1;
            ctx.setLineDash([6, 4]);
            ctx.beginPath();
            // Top margin
            ctx.moveTo(tx(baseX), ty(baseY + mT));
            ctx.lineTo(tx(baseX + baseW), ty(baseY + mT));
            // Bottom margin
            ctx.moveTo(tx(baseX), ty(baseY + baseH - mB));
            ctx.lineTo(tx(baseX + baseW), ty(baseY + baseH - mB));
            // Left margin
            ctx.moveTo(tx(baseX + mL), ty(baseY));
            ctx.lineTo(tx(baseX + mL), ty(baseY + baseH));
            // Right margin
            ctx.moveTo(tx(baseX + baseW - mR), ty(baseY));
            ctx.lineTo(tx(baseX + baseW - mR), ty(baseY + baseH));
            ctx.stroke();
            ctx.setLineDash([]);

            // Draw column fills
            const cols = grid.cols;
            const totalColGutter = Math.max(0, cols - 1) * grid.colGutter;
            const colW = (innerW - totalColGutter) / cols;

            ctx.globalAlpha = grid.opacity * 0.35;
            ctx.fillStyle = grid.color;
            for (let i = 0; i < cols; i++) {
                const colX = innerX + i * (colW + grid.colGutter);
                ctx.fillRect(tx(colX), ty(innerY), colW * zoom, innerH * zoom);
            }

            // Draw row fills
            const rows = grid.rows;
            const totalRowGutter = Math.max(0, rows - 1) * grid.rowGutter;
            const rowH = (innerH - totalRowGutter) / rows;

            ctx.globalAlpha = grid.opacity * 0.15;
            for (let i = 0; i < rows; i++) {
                const rowY = innerY + i * (rowH + grid.rowGutter);
                ctx.fillRect(tx(innerX), ty(rowY), innerW * zoom, rowH * zoom);
            }

            // Draw column edge lines (solid)
            ctx.globalAlpha = grid.opacity;
            ctx.strokeStyle = grid.color;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            for (let i = 0; i <= cols; i++) {
                const x = i < cols
                    ? innerX + i * (colW + grid.colGutter)
                    : innerX + innerW;
                ctx.moveTo(tx(x), ty(innerY));
                ctx.lineTo(tx(x), ty(innerY + innerH));
                // Also draw right edge of each column
                if (i < cols) {
                    const xRight = innerX + i * (colW + grid.colGutter) + colW;
                    ctx.moveTo(tx(xRight), ty(innerY));
                    ctx.lineTo(tx(xRight), ty(innerY + innerH));
                }
            }
            ctx.stroke();

            // Draw row edge lines
            ctx.beginPath();
            for (let i = 0; i <= rows; i++) {
                const y = i < rows
                    ? innerY + i * (rowH + grid.rowGutter)
                    : innerY + innerH;
                ctx.moveTo(tx(innerX), ty(y));
                ctx.lineTo(tx(innerX + innerW), ty(y));
                if (i < rows) {
                    const yBottom = innerY + i * (rowH + grid.rowGutter) + rowH;
                    ctx.moveTo(tx(innerX), ty(yBottom));
                    ctx.lineTo(tx(innerX + innerW), ty(yBottom));
                }
            }
            ctx.stroke();

            ctx.restore();
        };

        canvas.on('after:render', drawGrid);
        canvas.on('after:render', drawTypoGrid);
        return () => {
            canvas.off('after:render', drawGrid);
            canvas.off('after:render', drawTypoGrid);
        };
    }, [open]);

    // Render persistent custom guide lines whenever customGuides changes
    useEffect(() => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        // Remove ALL custom guide objects from canvas (sweep ensures no ghosts)
        canvas.getObjects().filter(o => o._isCustomGuide).forEach(o => {
            try { canvas.remove(o); } catch (e) { /* ignore */ }
        });
        customGuideLinesRef.current = [];

        const cw = canvas.width;
        const ch = canvas.height;

        customGuides.forEach((g, i) => {
            const isH = g.type === 'h';
            // Use huge range so guides extend across the entire visible area at any zoom
            const coords = isH
                ? [-10000, g.pos, 10000, g.pos]
                : [g.pos, -10000, g.pos, 10000];

            const line = new Line(coords, {
                stroke: g.locked ? '#4a9da8' : '#00bcd4',
                strokeWidth: 1,
                strokeDashArray: g.locked ? [2, 4] : [6, 3],
                excludeFromExport: true,
                opacity: g.locked ? 0.4 : 0.6,
                // Draggable only when unlocked
                selectable: !g.locked,
                evented: !g.locked,
                hasControls: false,
                hasBorders: false,
                lockMovementX: isH,
                lockMovementY: !isH,
                hoverCursor: g.locked ? 'default' : (isH ? 'ns-resize' : 'ew-resize'),
                moveCursor: g.locked ? 'default' : (isH ? 'ns-resize' : 'ew-resize'),
                strokeUniform: true,
                padding: g.locked ? 0 : 6,
            });
            // Store metadata for identification
            line._guideIndex = i;
            line._guideType = g.type;
            line._isCustomGuide = true;

            canvas.add(line);
            customGuideLinesRef.current.push(line);
        });

        // Handler: sync guide position back to state after drag
        const handleGuideMoved = (e) => {
            const obj = e.target;
            if (!obj || !obj._isCustomGuide) return;

            const idx = obj._guideIndex;
            const type = obj._guideType;
            const center = obj.getCenterPoint();

            const newPos = Math.round(type === 'h' ? center.y : center.x);

            const newGuides = customGuidesRef.current.map((gg, gi) =>
                gi === idx ? { ...gg, pos: newPos } : gg
            );
            // Update ref IMMEDIATELY so snap handler sees new position before React re-renders
            customGuidesRef.current = newGuides;
            setCustomGuides(newGuides);
        };

        canvas.on('object:modified', handleGuideMoved);

        canvas.renderAll();

        return () => {
            canvas.off('object:modified', handleGuideMoved);
        };
    }, [customGuides, open]);

    // Update canvas selection mode when active tool changes
    useEffect(() => {
        // Sync ref for fabric event handlers
        activeToolRef.current = activeTool;

        if (!fabricRef.current) return;

        const canvas = fabricRef.current;
        const isSelectMode = activeTool === TOOLS.SELECT || activeTool === TOOLS.TYPE;

        // Update canvas selection mode
        canvas.selection = isSelectMode;

        // Update all objects selectability
        canvas.getObjects().forEach(obj => {
            obj.set({
                selectable: isSelectMode,
                evented: isSelectMode
            });
        });

        // Deselect all when switching to point selection (need to click on canvas)
        // TEXT_SELECT should preserve selection so we know which image to segment
        if (activeTool === TOOLS.POINT_SELECT) {
            canvas.discardActiveObject();
        }

        canvas.renderAll();
    }, [activeTool]);

    // Punch tool preview effect - shows preview shape/edges
    useEffect(() => {
        if (!fabricRef.current) return;
        const canvas = fabricRef.current;

        // Helper to create preview shape for stamp punch
        const createStampPreview = (x, y) => {
            // Remove old preview
            if (punchPreviewRef.current) {
                canvas.remove(punchPreviewRef.current);
                punchPreviewRef.current = null;
            }

            if (activeTool !== TOOLS.STAMP_PUNCH) return;

            const size = punchShapeSize;
            let preview;

            switch (selectedPunchShape) {
                case 'circle':
                    preview = new Circle({
                        radius: size / 2,
                        left: x,
                        top: y,
                        originX: 'center',
                        originY: 'center',
                        fill: 'transparent',
                        stroke: '#ffffff',
                        strokeWidth: 2,
                        strokeDashArray: [5, 5],
                        selectable: false,
                        evented: false
                    });
                    break;
                case 'star':
                    // Create star points
                    const starPoints = [];
                    const spikes = 5;
                    const outerRadius = size / 2;
                    const innerRadius = size / 4;
                    let rot = Math.PI / 2 * 3;
                    const step = Math.PI / spikes;
                    for (let i = 0; i < spikes; i++) {
                        starPoints.push({ x: Math.cos(rot) * outerRadius, y: Math.sin(rot) * outerRadius });
                        rot += step;
                        starPoints.push({ x: Math.cos(rot) * innerRadius, y: Math.sin(rot) * innerRadius });
                        rot += step;
                    }
                    preview = new Polygon(starPoints, {
                        left: x,
                        top: y,
                        originX: 'center',
                        originY: 'center',
                        fill: 'transparent',
                        stroke: '#ffffff',
                        strokeWidth: 2,
                        strokeDashArray: [5, 5],
                        selectable: false,
                        evented: false
                    });
                    break;
                case 'heart':
                    // Approximate heart with bezier path (centered at origin)
                    const heartPath = `M 0 ${-size * 0.25} C ${size * 0.5} ${-size * 0.5} ${size * 0.5} 0 0 ${size * 0.5} C ${-size * 0.5} 0 ${-size * 0.5} ${-size * 0.5} 0 ${-size * 0.25} Z`;
                    preview = new Path(heartPath, {
                        left: x,
                        top: y,
                        originX: 'center',
                        originY: 'center',
                        fill: 'transparent',
                        stroke: '#ffffff',
                        strokeWidth: 2,
                        strokeDashArray: [5, 5],
                        selectable: false,
                        evented: false
                    });
                    break;
                case 'hexagon':
                case 'triangle':
                    const sides = selectedPunchShape === 'hexagon' ? 6 : 3;
                    const polyPoints = [];
                    for (let i = 0; i < sides; i++) {
                        const angle = (i * 2 * Math.PI / sides) - Math.PI / 2;
                        polyPoints.push({ x: Math.cos(angle) * size / 2, y: Math.sin(angle) * size / 2 });
                    }
                    preview = new Polygon(polyPoints, {
                        left: x,
                        top: y,
                        originX: 'center',
                        originY: 'center',
                        fill: 'transparent',
                        stroke: '#ffffff',
                        strokeWidth: 2,
                        strokeDashArray: [5, 5],
                        selectable: false,
                        evented: false
                    });
                    break;
                default:
                    // Handle saved goo shapes preview
                    if (selectedPunchShape.startsWith('goo') && gooPreviewCacheRef.current.img && gooPreviewCacheRef.current.id === selectedPunchShape) {
                        const cachedImg = gooPreviewCacheRef.current.img;
                        const aspect = cachedImg.width / cachedImg.height;
                        const drawW = aspect >= 1 ? size : size * aspect;
                        const drawH = aspect >= 1 ? size / aspect : size;
                        const fabricImg = new FabricImage(cachedImg, {
                            left: x,
                            top: y,
                            originX: 'center',
                            originY: 'center',
                            scaleX: drawW / cachedImg.width,
                            scaleY: drawH / cachedImg.height,
                            opacity: 0.5,
                            selectable: false,
                            evented: false,
                            strokeWidth: 0
                        });
                        preview = fabricImg;
                    }
                    break;
            }

            if (preview) {
                preview.set({ angle: punchRotation }); // Apply rotation
                punchPreviewRef.current = preview;
                canvas.add(preview);
                canvas.renderAll();
            }
        };

        // Helper to create border preview with actual pattern
        const createBorderPreview = () => {
            // Remove old border preview
            if (borderPreviewRef.current) {
                borderPreviewRef.current.forEach(shape => canvas.remove(shape));
                borderPreviewRef.current = null;
            }

            if (activeTool !== TOOLS.BORDER_PUNCH) return;

            // Find the selected layer's object bounds
            const baseLayer = selectedLayer
                ? layers.find(l => l.id === selectedLayer)
                : layers[0];
            if (!baseLayer?.object) return;

            const obj = baseLayer.object;
            const bounds = obj.getBoundingRect();
            const shapes = [];
            const edgeColor = '#ffffff';
            const patternSize = 20;
            const pattern = selectedPunchShape; // wavy, scalloped, zigzag, torn, postage

            // Helper to generate pattern path for an edge
            const generateEdgePatternPath = (edge) => {
                let pathD = '';
                let x, y, length, isHorizontal;

                switch (edge) {
                    case 'top':
                        x = bounds.left;
                        y = bounds.top;
                        length = bounds.width;
                        isHorizontal = true;
                        break;
                    case 'bottom':
                        x = bounds.left;
                        y = bounds.top + bounds.height;
                        length = bounds.width;
                        isHorizontal = true;
                        break;
                    case 'left':
                        x = bounds.left;
                        y = bounds.top;
                        length = bounds.height;
                        isHorizontal = false;
                        break;
                    case 'right':
                        x = bounds.left + bounds.width;
                        y = bounds.top;
                        length = bounds.height;
                        isHorizontal = false;
                        break;
                    default:
                        return null;
                }

                const count = Math.floor(length / patternSize);

                switch (pattern) {
                    case 'wavy':
                        pathD = `M ${x} ${y}`;
                        for (let i = 0; i < count; i++) {
                            const offset = i * patternSize;
                            if (isHorizontal) {
                                pathD += ` Q ${x + offset + patternSize / 4} ${y - 8}, ${x + offset + patternSize / 2} ${y}`;
                                pathD += ` Q ${x + offset + patternSize * 3 / 4} ${y + 8}, ${x + offset + patternSize} ${y}`;
                            } else {
                                pathD += ` Q ${x - 8} ${y + offset + patternSize / 4}, ${x} ${y + offset + patternSize / 2}`;
                                pathD += ` Q ${x + 8} ${y + offset + patternSize * 3 / 4}, ${x} ${y + offset + patternSize}`;
                            }
                        }
                        break;
                    case 'scalloped':
                        pathD = `M ${x} ${y}`;
                        for (let i = 0; i < count; i++) {
                            const offset = i * patternSize;
                            if (isHorizontal) {
                                pathD += ` A ${patternSize / 2} ${patternSize / 2} 0 0 1 ${x + offset + patternSize} ${y}`;
                            } else {
                                pathD += ` A ${patternSize / 2} ${patternSize / 2} 0 0 1 ${x} ${y + offset + patternSize}`;
                            }
                        }
                        break;
                    case 'zigzag':
                        pathD = `M ${x} ${y}`;
                        for (let i = 0; i < count; i++) {
                            const offset = i * patternSize;
                            if (isHorizontal) {
                                pathD += ` L ${x + offset + patternSize / 2} ${y + (i % 2 === 0 ? -10 : 10)}`;
                                pathD += ` L ${x + offset + patternSize} ${y}`;
                            } else {
                                pathD += ` L ${x + (i % 2 === 0 ? -10 : 10)} ${y + offset + patternSize / 2}`;
                                pathD += ` L ${x} ${y + offset + patternSize}`;
                            }
                        }
                        break;
                    case 'torn':
                        pathD = `M ${x} ${y}`;
                        for (let i = 0; i < count; i++) {
                            const offset = i * patternSize;
                            const rand1 = (Math.sin(i * 12.9898) * 43758.5453) % 1 * 8 - 4;
                            const rand2 = (Math.sin(i * 78.233) * 43758.5453) % 1 * 8 - 4;
                            if (isHorizontal) {
                                pathD += ` L ${x + offset + patternSize / 3} ${y + rand1}`;
                                pathD += ` L ${x + offset + patternSize * 2 / 3} ${y + rand2}`;
                                pathD += ` L ${x + offset + patternSize} ${y}`;
                            } else {
                                pathD += ` L ${x + rand1} ${y + offset + patternSize / 3}`;
                                pathD += ` L ${x + rand2} ${y + offset + patternSize * 2 / 3}`;
                                pathD += ` L ${x} ${y + offset + patternSize}`;
                            }
                        }
                        break;
                    case 'postage':
                        pathD = `M ${x} ${y}`;
                        for (let i = 0; i < count; i++) {
                            const offset = i * patternSize;
                            if (isHorizontal) {
                                // Small semicircle indents
                                pathD += ` L ${x + offset + patternSize / 4} ${y}`;
                                pathD += ` A 4 4 0 0 0 ${x + offset + patternSize * 3 / 4} ${y}`;
                                pathD += ` L ${x + offset + patternSize} ${y}`;
                            } else {
                                pathD += ` L ${x} ${y + offset + patternSize / 4}`;
                                pathD += ` A 4 4 0 0 0 ${x} ${y + offset + patternSize * 3 / 4}`;
                                pathD += ` L ${x} ${y + offset + patternSize}`;
                            }
                        }
                        break;
                    default:
                        // Simple line fallback
                        if (isHorizontal) {
                            pathD = `M ${x} ${y} L ${x + length} ${y}`;
                        } else {
                            pathD = `M ${x} ${y} L ${x} ${y + length}`;
                        }
                }

                return new Path(pathD, {
                    fill: 'transparent',
                    stroke: edgeColor,
                    strokeWidth: 2,
                    selectable: false,
                    evented: false
                });
            };

            // Create pattern preview for selected edges
            if (selectedBorderEdges.top) {
                const path = generateEdgePatternPath('top');
                if (path) shapes.push(path);
            }
            if (selectedBorderEdges.right) {
                const path = generateEdgePatternPath('right');
                if (path) shapes.push(path);
            }
            if (selectedBorderEdges.bottom) {
                const path = generateEdgePatternPath('bottom');
                if (path) shapes.push(path);
            }
            if (selectedBorderEdges.left) {
                const path = generateEdgePatternPath('left');
                if (path) shapes.push(path);
            }

            if (shapes.length > 0) {
                borderPreviewRef.current = shapes;
                shapes.forEach(shape => canvas.add(shape));
                canvas.renderAll();
            }
        };

        // Mouse move handler for stamp preview
        const handlePreviewMove = (opt) => {
            if (activeTool !== TOOLS.STAMP_PUNCH) return;
            const pointer = canvas.getScenePoint(opt.e);
            lastPunchPosRef.current = { x: pointer.x, y: pointer.y }; // Store position
            createStampPreview(pointer.x, pointer.y);
        };

        // Set up event handler
        canvas.on('mouse:move', handlePreviewMove);

        // Create border preview when tool is active
        if (activeTool === TOOLS.BORDER_PUNCH) {
            createBorderPreview();
        }

        // Re-render stamp preview at last position when rotation/size changes
        if (activeTool === TOOLS.STAMP_PUNCH && lastPunchPosRef.current) {
            createStampPreview(lastPunchPosRef.current.x, lastPunchPosRef.current.y);
        }

        // Cleanup
        return () => {
            canvas.off('mouse:move', handlePreviewMove);
            if (punchPreviewRef.current) {
                canvas.remove(punchPreviewRef.current);
                punchPreviewRef.current = null;
            }
            if (borderPreviewRef.current) {
                borderPreviewRef.current.forEach(line => canvas.remove(line));
                borderPreviewRef.current = null;
            }
        };
    }, [activeTool, selectedPunchShape, punchShapeSize, punchRotation, selectedBorderEdges, selectedLayer, layers, savedGooShapes]);

    // Pre-load goo SVG for preview when a goo shape is selected (white silhouette)
    useEffect(() => {
        if (!selectedPunchShape.startsWith('goo')) {
            gooPreviewCacheRef.current = { id: null, img: null };
            return;
        }
        const gooShape = savedGooShapes.find(s => s.id === selectedPunchShape);
        if (!gooShape) return;

        const svgSrc = gooShape.svgString
            ? 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(gooShape.svgString)))
            : gooShape.thumbnailUrl;

        const img = new Image();
        img.onload = () => {
            // Render SVG to temp canvas, then composite to white silhouette
            const c = document.createElement('canvas');
            c.width = img.width;
            c.height = img.height;
            const ctx = c.getContext('2d');
            // Draw the SVG (black shapes with alpha)
            ctx.drawImage(img, 0, 0);
            // Composite: fill white only where there are existing pixels
            ctx.globalCompositeOperation = 'source-in';
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, c.width, c.height);

            // Convert to an image element for FabricImage
            const silhouette = new Image();
            silhouette.onload = () => {
                gooPreviewCacheRef.current = { id: selectedPunchShape, img: silhouette };
            };
            silhouette.src = c.toDataURL();
        };
        img.src = svgSrc;
    }, [selectedPunchShape, savedGooShapes, activeTool]);

    // Inpaint tool - mask painting
    useEffect(() => {
        if (!fabricRef.current || activeTool !== TOOLS.INPAINT) {
            // Cleanup when tool changes
            if (inpaintBrushPreviewRef.current && fabricRef.current) {
                fabricRef.current.remove(inpaintBrushPreviewRef.current);
                inpaintBrushPreviewRef.current = null;
            }
            // Also remove mask overlay when switching away from inpaint tool
            if (fabricRef.current) {
                const overlay = fabricRef.current.getObjects().find(o => o.customId === 'inpaint-mask-overlay');
                if (overlay) {
                    fabricRef.current.remove(overlay);
                    fabricRef.current.renderAll();
                }
            }
            return;
        }

        const canvas = fabricRef.current;
        const baseLayer = selectedLayer ? layers.find(l => l.id === selectedLayer) : layers[0];
        if (!baseLayer || !baseLayer.object) return;

        const obj = baseLayer.object;

        // Use full canvas dimensions for the mask (since we export the full canvas)
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Initialize mask canvas if needed - must match full canvas size
        if (!inpaintMaskCanvasRef.current ||
            inpaintMaskCanvasRef.current.width !== canvasWidth ||
            inpaintMaskCanvasRef.current.height !== canvasHeight) {
            const maskCanvas = document.createElement('canvas');
            maskCanvas.width = canvasWidth;
            maskCanvas.height = canvasHeight;
            const ctx = maskCanvas.getContext('2d');
            // Start with black background (black = keep, white = inpaint)
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);
            inpaintMaskCanvasRef.current = maskCanvas;
            inpaintMaskDataRef.current = null;
        }

        // Create brush preview circle
        const createBrushPreview = (x, y) => {
            if (inpaintBrushPreviewRef.current) {
                inpaintBrushPreviewRef.current.set({ left: x, top: y });
            } else {
                const circle = new Circle({
                    radius: inpaintBrushSize / 2,
                    left: x,
                    top: y,
                    originX: 'center',
                    originY: 'center',
                    fill: 'transparent',
                    stroke: 'white',
                    strokeWidth: 2,
                    strokeDashArray: [5, 5],
                    selectable: false,
                    evented: false
                });
                canvas.add(circle);
                inpaintBrushPreviewRef.current = circle;
            }
            // Update radius in case brush size changed
            inpaintBrushPreviewRef.current.set({ radius: inpaintBrushSize / 2 });
            canvas.renderAll();
        };

        // Paint on mask canvas and update overlay
        const paintOnMask = (x, y) => {
            if (!inpaintMaskCanvasRef.current) return;

            const ctx = inpaintMaskCanvasRef.current.getContext('2d');

            // Paint directly at canvas coordinates (mask matches canvas size now)
            ctx.fillStyle = 'white';
            ctx.beginPath();
            ctx.arc(x, y, inpaintBrushSize / 2, 0, Math.PI * 2);
            ctx.fill();

            // Update mask data URL
            inpaintMaskDataRef.current = inpaintMaskCanvasRef.current.toDataURL('image/png');

            // Update overlay
            updateMaskOverlay();
        };

        // Update visual overlay on canvas
        const updateMaskOverlay = () => {
            // Create a tinted version of the mask (purple where white, transparent where black)
            const tintCanvas = document.createElement('canvas');
            tintCanvas.width = inpaintMaskCanvasRef.current.width;
            tintCanvas.height = inpaintMaskCanvasRef.current.height;
            const tintCtx = tintCanvas.getContext('2d');

            // Get mask data
            const maskCtx = inpaintMaskCanvasRef.current.getContext('2d');
            const maskData = maskCtx.getImageData(0, 0, tintCanvas.width, tintCanvas.height);
            const tintData = tintCtx.createImageData(tintCanvas.width, tintCanvas.height);

            // Tint white areas purple, make black areas transparent
            for (let i = 0; i < maskData.data.length; i += 4) {
                if (maskData.data[i] > 128) { // White area (mask)
                    tintData.data[i] = 168;     // R (purple)
                    tintData.data[i + 1] = 85;  // G
                    tintData.data[i + 2] = 247; // B
                    tintData.data[i + 3] = 128; // A (50% opacity)
                } else {
                    tintData.data[i + 3] = 0;   // Transparent
                }
            }
            tintCtx.putImageData(tintData, 0, 0);

            const tintedDataUrl = tintCanvas.toDataURL('image/png');

            // Find or create overlay
            const overlayId = 'inpaint-mask-overlay';
            let overlay = canvas.getObjects().find(o => o.customId === overlayId);

            if (!overlay) {
                // Create overlay from tinted mask - positioned at canvas origin
                FabricImage.fromURL(tintedDataUrl, { crossOrigin: 'anonymous' }).then(img => {
                    if (!img) return;
                    img.set({
                        left: 0,
                        top: 0,
                        originX: 'left',
                        originY: 'top',
                        selectable: false,
                        evented: false,
                        customId: overlayId
                    });
                    canvas.add(img);
                    canvas.renderAll();
                });
            } else {
                // Update existing overlay by replacing with new tinted image
                FabricImage.fromURL(tintedDataUrl, { crossOrigin: 'anonymous' }).then(newImg => {
                    if (!newImg || !overlay) return;
                    overlay.setElement(newImg.getElement());
                    canvas.renderAll();
                });
            }
        };

        // Use a local ref for drawing state to avoid dependency issues
        let isCurrentlyDrawing = false;

        const handleMouseMove = (opt) => {
            const pointer = canvas.getScenePoint(opt.e);
            createBrushPreview(pointer.x, pointer.y);

            if (isCurrentlyDrawing && activeTool === TOOLS.INPAINT) {
                paintOnMask(pointer.x, pointer.y);
            }
        };

        const handleMouseDown = (opt) => {
            if (activeTool !== TOOLS.INPAINT) return;
            isCurrentlyDrawing = true;
            const pointer = canvas.getScenePoint(opt.e);
            paintOnMask(pointer.x, pointer.y);
        };

        const handleMouseUp = () => {
            isCurrentlyDrawing = false;
        };

        canvas.on('mouse:down', handleMouseDown);
        canvas.on('mouse:move', handleMouseMove);
        canvas.on('mouse:up', handleMouseUp);

        return () => {
            canvas.off('mouse:down', handleMouseDown);
            canvas.off('mouse:move', handleMouseMove);
            canvas.off('mouse:up', handleMouseUp);

            // Remove brush preview only
            if (inpaintBrushPreviewRef.current) {
                canvas.remove(inpaintBrushPreviewRef.current);
                inpaintBrushPreviewRef.current = null;
            }

            // Note: Don't remove mask overlay here - let it persist until tool changes or clear is clicked
        };
    }, [activeTool, selectedLayer, layers, inpaintBrushSize]);

    // Keyboard handlers for copy/paste
    useEffect(() => {
        if (!open) return;

        const handleKeyDown = async (e) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const modKey = isMac ? e.metaKey : e.ctrlKey;

            // Undo: Cmd/Ctrl + Z
            if (modKey && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                undo();
                return;
            }
            // Redo: Cmd/Ctrl + Shift + Z
            if (modKey && e.key === 'z' && e.shiftKey) {
                e.preventDefault();
                redo();
                return;
            }

            // Copy: Cmd/Ctrl + C
            if (modKey && e.key === 'c' && !e.shiftKey) {
                const canvas = fabricRef.current;
                if (!canvas) return;

                const activeObject = canvas.getActiveObject();
                if (activeObject) {
                    const isTextObject = activeObject instanceof Textbox;

                    if (isTextObject) {
                        // Clone Textbox objects to preserve editability
                        const cloned = await activeObject.clone();
                        clipboardRef.current = {
                            type: 'textbox',
                            clonedObject: cloned,
                            layerName: layers.find(l => l.object === activeObject)?.name || 'Text Layer'
                        };
                    } else {
                        // Store image objects as DataURL
                        const scaleX = activeObject.scaleX || 1;
                        const scaleY = activeObject.scaleY || 1;
                        const multiplier = Math.max(1 / scaleX, 1 / scaleY, 1);

                        clipboardRef.current = {
                            type: 'fabricObject',
                            objectData: activeObject.toDataURL({ format: 'png', multiplier }),
                            originalWidth: activeObject.width,
                            originalHeight: activeObject.height,
                            scaleX: scaleX,
                            scaleY: scaleY,
                            layerName: layers.find(l => l.object === activeObject)?.name || 'Copied Layer'
                        };
                    }
                    messageApi.success('Layer copied!');
                    e.preventDefault();
                }
            }

            // Paste: Cmd/Ctrl + V
            if (modKey && e.key === 'v' && !e.shiftKey) {
                if (!clipboardRef.current || !fabricRef.current) return;

                const canvas = fabricRef.current;
                const clipData = clipboardRef.current;
                const offset = 20;

                if (clipData.type === 'textbox') {
                    try {
                        // Clone again so we can paste multiple times
                        const pastedObj = await clipData.clonedObject.clone();
                        pastedObj.set({
                            left: (pastedObj.left || canvas.width / 2) + offset,
                            top: (pastedObj.top || canvas.height / 2) + offset,
                            selectable: true,
                            evented: true,
                        });

                        canvas.add(pastedObj);
                        canvas.setActiveObject(pastedObj);
                        canvas.renderAll();

                        const newLayer = {
                            id: `layer_${Date.now()}`,
                            name: `${clipData.layerName} Copy`,
                            object: pastedObj,
                            visible: true,
                            locked: false,
                            opacity: pastedObj.opacity || 1
                        };
                        setLayers(prev => [...prev, newLayer]);
                        setSelectedLayer(newLayer.id);
                        setSelectedTextObject(pastedObj);

                        messageApi.success('Text pasted!');
                        e.preventDefault();
                    } catch (err) {
                        console.error('Paste text failed:', err);
                        messageApi.error('Failed to paste text');
                    }
                } else if (clipData.type === 'fabricObject') {
                    try {
                        const img = await FabricImage.fromURL(clipData.objectData);

                        const scaleX = clipData.scaleX || 1;
                        const scaleY = clipData.scaleY || 1;
                        img.set({
                            scaleX: scaleX,
                            scaleY: scaleY,
                            left: canvas.width / 2 + offset,
                            top: canvas.height / 2 + offset,
                            originX: 'center',
                            originY: 'center',
                            selectable: true
                        });

                        canvas.add(img);
                        canvas.setActiveObject(img);
                        canvas.renderAll();

                        const newLayer = {
                            id: `layer_${Date.now()}`,
                            name: `${clipData.layerName} Copy`,
                            object: img,
                            visible: true,
                            locked: false,
                            opacity: 1
                        };
                        setLayers(prev => [...prev, newLayer]);
                        setSelectedLayer(newLayer.id);

                        messageApi.success('Layer pasted!');
                        e.preventDefault();
                    } catch (err) {
                        console.error('Paste failed:', err);
                        messageApi.error('Failed to paste layer');
                    }
                }
            }

            // Delete: Backspace or Delete
            if ((e.key === 'Backspace' || e.key === 'Delete') && !e.target.closest('input, textarea')) {
                const canvas = fabricRef.current;
                if (!canvas) return;

                const activeObject = canvas.getActiveObject();
                if (activeObject) {
                    const layer = layers.find(l => l.object === activeObject);
                    if (layer && !layer.locked) {
                        canvas.remove(activeObject);
                        canvas.renderAll();
                        setLayers(prev => prev.filter(l => l.id !== layer.id));
                        setSelectedLayer(null);
                        messageApi.info('Layer deleted');
                        e.preventDefault();
                    }
                }
            }

            // Arrow keys for stamp punch tool
            if (activeTool === TOOLS.STAMP_PUNCH && !e.target.closest('input, textarea')) {
                if (e.key === 'ArrowLeft') {
                    setPunchRotation(prev => prev - 15); // Rotate left 15°
                    e.preventDefault();
                } else if (e.key === 'ArrowRight') {
                    setPunchRotation(prev => prev + 15); // Rotate right 15°
                    e.preventDefault();
                } else if (e.key === 'ArrowUp') {
                    setPunchShapeSize(prev => Math.min(prev + 10, 500)); // Increase size
                    e.preventDefault();
                } else if (e.key === 'ArrowDown') {
                    setPunchShapeSize(prev => Math.max(prev - 10, 20)); // Decrease size
                    e.preventDefault();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, layers, messageApi, activeTool]);
    // Load image into canvas
    const loadImage = async (imageUrl) => {
        if (!fabricRef.current) return;

        try {
            setLoading(true);
            const img = await FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' });

            const canvas = fabricRef.current;
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;

            // Scale image to fit canvas
            const scale = Math.min(
                (canvasWidth * 0.9) / img.width,
                (canvasHeight * 0.9) / img.height
            );

            img.scale(scale);
            img.set({
                left: (canvasWidth - img.width * scale) / 2,
                top: (canvasHeight - img.height * scale) / 2,
                selectable: activeTool === TOOLS.SELECT
            });

            canvas.add(img);
            canvas.renderAll();

            // Add to layers with sourceUrl for SAM2 access
            setLayers([{
                id: `layer_${Date.now()}`,
                name: 'Background',
                object: img,
                sourceUrl: imageUrl, // Store original URL for SAM2
                visible: true,
                locked: false,
                opacity: 1
            }]);
        } catch (error) {
            console.error('Failed to load image:', error);
            messageApi.error('Failed to load image');
        } finally {
            setLoading(false);
        }
    };

    // Fit all content into view (reset zoom and center)
    const fitToContent = () => {
        if (!fabricRef.current) return;

        const canvas = fabricRef.current;
        const objects = canvas.getObjects();

        if (objects.length === 0) {
            // No objects, just reset to default view
            canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
            canvas.renderAll();
            return;
        }

        // Calculate bounding box of all objects
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        objects.forEach(obj => {
            const bounds = obj.getBoundingRect();
            minX = Math.min(minX, bounds.left);
            minY = Math.min(minY, bounds.top);
            maxX = Math.max(maxX, bounds.left + bounds.width);
            maxY = Math.max(maxY, bounds.top + bounds.height);
        });

        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;
        const contentCenterX = minX + contentWidth / 2;
        const contentCenterY = minY + contentHeight / 2;

        // Calculate zoom to fit content with padding
        const padding = 50;
        const availableWidth = canvas.width - padding * 2;
        const availableHeight = canvas.height - padding * 2;
        const zoom = Math.min(
            availableWidth / contentWidth,
            availableHeight / contentHeight,
            1 // Don't zoom in beyond 100%
        );

        // Reset viewport and apply new transform
        const canvasCenterX = canvas.width / 2;
        const canvasCenterY = canvas.height / 2;

        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]); // Reset first
        canvas.zoomToPoint({ x: canvasCenterX, y: canvasCenterY }, zoom);

        // Pan to center content
        const vpt = canvas.viewportTransform;
        vpt[4] = canvasCenterX - contentCenterX * zoom;
        vpt[5] = canvasCenterY - contentCenterY * zoom;

        canvas.setViewportTransform(vpt);
        canvas.renderAll();
    };

    // Helper to trim transparent pixels from an image and return cropped data URL
    const trimTransparentPixels = (imgElement) => {
        return new Promise((resolve) => {
            const w = imgElement.width || imgElement.naturalWidth;
            const h = imgElement.height || imgElement.naturalHeight;
            const srcCanvas = document.createElement('canvas');
            srcCanvas.width = w;
            srcCanvas.height = h;
            const ctx = srcCanvas.getContext('2d');
            ctx.drawImage(imgElement, 0, 0);

            const imageData = ctx.getImageData(0, 0, w, h);
            const pixelBuf = imageData.data.buffer.slice(0);

            const buildResult = (minX, minY, maxX, maxY) => {
                const padding = 2;
                const cropX = Math.max(0, minX - padding);
                const cropY = Math.max(0, minY - padding);
                const cropW = Math.min(w - cropX, maxX - minX + 1 + padding * 2);
                const cropH = Math.min(h - cropY, maxY - minY + 1 + padding * 2);
                const cropped = document.createElement('canvas');
                cropped.width = cropW; cropped.height = cropH;
                cropped.getContext('2d').drawImage(srcCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
                return { canvas: cropped, cropX, cropY };
            };

            let worker;
            try {
                worker = new Worker(
                    new URL('../workers/trimWorker.js', import.meta.url)
                );
            } catch (err) {
                // Worker failed — do main-thread fallback
                const data = imageData.data;
                let minX = w, minY = h, maxX = -1, maxY = -1;
                for (let y = 0; y < h; y++) {
                    const row = y * w * 4;
                    for (let x = 0; x < w; x++) {
                        if (data[row + x * 4 + 3] > 0) {
                            if (x < minX) minX = x;
                            if (x > maxX) maxX = x;
                            if (y < minY) minY = y;
                            if (y > maxY) maxY = y;
                        }
                    }
                }
                if (maxX < 0) { resolve(null); return; }
                resolve(buildResult(minX, minY, maxX, maxY));
                return;
            }

            worker.onmessage = (ev) => {
                worker.terminate();
                if (ev.data.empty) { resolve(null); return; }
                resolve(buildResult(ev.data.minX, ev.data.minY, ev.data.maxX, ev.data.maxY));
            };

            worker.postMessage({ pixels: pixelBuf, width: w, height: h }, [pixelBuf]);
        });
    };

    // Add image as a new layer (for click or drag-and-drop)
    const addImageAsLayer = async (imageUrl, layerName = null, isCutout = false) => {
        if (!fabricRef.current) {
            console.error('Canvas not initialized');
            return;
        }

        try {
            setLoading(true);

            // Use proxy URL to bypass CORS for storage images
            let loadUrl = imageUrl;
            if (imageUrl.includes('storage.googleapis.com') || imageUrl.includes('firebasestorage')) {
                const tokenParam = token ? `&token=${token}` : '';
                loadUrl = `${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(imageUrl)}${tokenParam}`;
            }

            // Load the HTML image once
            const htmlImg = new Image();
            htmlImg.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
                htmlImg.onload = resolve;
                htmlImg.onerror = reject;
                htmlImg.src = loadUrl;
            });

            // For cutouts, trim transparent pixels first (fast — no PNG encoding)
            let img;
            if (isCutout) {
                try {
                    const result = await trimTransparentPixels(htmlImg);
                    if (result) {
                        img = new FabricImage(result.canvas);
                    }
                } catch (trimErr) {
                    console.warn('Trim failed, using original:', trimErr);
                }
            }
            // Fallback / non-cutout
            if (!img) {
                img = new FabricImage(htmlImg);
            }

            if (!img || !img.width) {
                throw new Error('Image failed to load - empty dimensions');
            }

            const canvas = fabricRef.current;
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;

            // Scale image to fit 80% of canvas
            const scale = Math.min(
                (canvasWidth * 0.8) / img.width,
                (canvasHeight * 0.8) / img.height,
                1 // Don't upscale
            );

            img.scale(scale);
            img.set({
                left: canvasWidth / 2,
                top: canvasHeight / 2,
                originX: 'center',
                originY: 'center',
                selectable: true
            });

            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();

            // Add to layers - store original URL for SAM2 (needs cloud-accessible URL)
            const newLayer = {
                id: `layer_${Date.now()}`,
                name: layerName || `Layer ${layers.length + 1}`,
                object: img,
                sourceUrl: imageUrl, // Store original URL for SAM2 access
                visible: true,
                locked: false,
                opacity: 1
            };
            setLayers(prev => [...prev, newLayer]);
            setSelectedLayer(newLayer.id);

            messageApi.success('Image added!');
        } catch (error) {
            console.error('Failed to add image:', error, 'URL:', imageUrl);
            messageApi.error('Failed to add image - check console for details');
        } finally {
            setLoading(false);
        }
    };

    // Handle drag events with counter to prevent flicker
    const dragCounterRef = useRef(0);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current++;
        if (dragCounterRef.current === 1) {
            setIsDragging(true);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current--;
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);

        // Check for JSON data from Assets drawer
        const jsonData = e.dataTransfer.getData('application/json');
        if (jsonData) {
            try {
                const item = JSON.parse(jsonData);
                const imageUrl = item.url || item.src;
                if (imageUrl && item.type === 'image') {
                    // Detect if this is a cutout by checking provider/model/prompt
                    const isCutout = item.provider === 'scrapbook' ||
                        item.model?.includes('punch') ||
                        item.prompt?.toLowerCase().includes('cutout');
                    addImageAsLayer(imageUrl, item.name || 'Asset Image', isCutout);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse dropped JSON:', err);
            }
        }

        // Check for URL in dataTransfer
        const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
        if (url && (url.startsWith('http') || url.startsWith('blob'))) {
            addImageAsLayer(url);
            return;
        }

        // Check for dropped files
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                const objectUrl = URL.createObjectURL(file);
                addImageAsLayer(objectUrl, file.name);
            }
        }
    };

    // Ref for stamp punch function (to avoid initialization order issues)
    const applyStampPunchRef = useRef(null);


    // Handle canvas click for point-based segmentation and stamp punch
    const handleCanvasClick = useCallback((e) => {
        if (!fabricRef.current) return;

        const canvas = fabricRef.current;
        // Fabric.js v6 uses getScenePoint instead of getPointer
        const pointer = canvas.getScenePoint(e.e);

        // Handle stamp punch tool
        if (activeToolRef.current === TOOLS.STAMP_PUNCH) {
            console.log('Stamp punch at:', pointer.x, pointer.y);
            if (applyStampPunchRef.current) {
                applyStampPunchRef.current(pointer.x, pointer.y);
            }
            return;
        }

        // Handle TYPE tool — create new Textbox on empty canvas click
        if (activeToolRef.current === TOOLS.TYPE) {
            // Only create text if clicking on empty area (not on an existing object)
            if (!e.target) {
                const textbox = new Textbox('Type here', {
                    left: pointer.x,
                    top: pointer.y,
                    fontFamily: 'Inter',
                    fontSize: 48,
                    fontWeight: 700,
                    fill: '#ffffff',
                    charSpacing: 0,
                    lineHeight: 1.1,
                    textAlign: 'left',
                    width: 300,
                    selectable: true,
                    evented: true,
                    editable: true,
                    _textTransform: 'none',
                });
                canvas.add(textbox);
                canvas.setActiveObject(textbox);
                textbox.enterEditing();
                textbox.selectAll();
                canvas.renderAll();

                // Add as a layer
                const layerId = `text_${Date.now()}`;
                setLayers(prev => [...prev, {
                    id: layerId,
                    name: 'Text',
                    visible: true,
                    locked: false,
                    object: textbox,
                    sourceUrl: null,
                    type: 'text',
                }]);
                setSelectedLayer(layerId);
                setSelectedTextObject(textbox);
            }
            return;
        }

        // Handle point select for segmentation
        if (activeToolRef.current !== TOOLS.POINT_SELECT) return;

        console.log('Point placed at:', pointer.x, pointer.y);

        // Add point (left click = positive, right click would be negative)
        const isPositive = !e.e.shiftKey; // Shift+click for negative points
        const newPoint = {
            x: Math.round(pointer.x),
            y: Math.round(pointer.y),
            label: isPositive ? 1 : 0
        };

        setSegmentPoints(prev => [...prev, newPoint]);
    }, []);

    // Call SAM2 API for segmentation
    const performSegmentation = async () => {
        if (segmentPoints.length === 0) {
            messageApi.warning('Click on the image to select points first');
            return;
        }

        // Get the base image - use selected layer or first layer
        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        if (!baseLayer) {
            messageApi.error('No image layer found');
            return;
        }

        // Use stored sourceUrl (original cloud URL) for SAM2 access
        const imageUrl = baseLayer.sourceUrl || initialImage;

        if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.includes('localhost')) {
            messageApi.error('Image must be uploaded to cloud storage for segmentation');
            return;
        }

        try {
            setLoading(true);
            setSegmentPoints([]); // Clear points immediately

            // Map canvas coordinates to image coordinates
            const img = baseLayer.object;
            const scaleX = img.scaleX || 1;
            const scaleY = img.scaleY || 1;

            // Account for center origin - calculate actual top-left corner of image
            const imgWidth = img.width * scaleX;
            const imgHeight = img.height * scaleY;
            const originX = img.originX || 'left';
            const originY = img.originY || 'top';

            let imgLeft = img.left || 0;
            let imgTop = img.top || 0;

            // Adjust for center origin
            if (originX === 'center') {
                imgLeft -= imgWidth / 2;
            }
            if (originY === 'center') {
                imgTop -= imgHeight / 2;
            }

            const mappedPrompts = segmentPoints.map(p => ({
                x: Math.round((p.x - imgLeft) / scaleX),
                y: Math.round((p.y - imgTop) / scaleY),
                label: p.label
            }));

            const response = await api.post('/sam2/segment', {
                image_url: imageUrl,
                prompts: mappedPrompts,
                apply_mask: true
            });

            const { request_id } = response.data;
            const modelId = 'fal-ai/sam2/image';

            // Use standard trackGeneration pattern
            trackGeneration(request_id, modelId, {
                prompt: 'SAM2 Segmentation',
                type: 'Cutout',
                canvas_id: canvasId,
                promptProp: `Cutout with ${mappedPrompts.length} points`
            }, {
                onComplete: async (result) => {
                    try {
                        console.log('SAM2 Full Result:', JSON.stringify(result, null, 2));

                        // SAM2 returns a black/white mask - we need to apply it to the original image
                        const imageData = result?.data?.image || result?.image;
                        console.log('Extracted mask imageData:', imageData);

                        if (imageData?.url) {
                            const rawMaskUrl = imageData.url;

                            // Proxy mask through our storage to avoid CORS
                            let maskUrl = rawMaskUrl;
                            try {
                                const proxyRes = await api.post('/storage/upload-url', {
                                    url: rawMaskUrl,
                                    bucket: 'media-generations'
                                });
                                if (proxyRes.data?.url) maskUrl = proxyRes.data.url;
                            } catch (e) {
                                console.warn('Could not proxy mask, trying direct URL');
                            }

                            // Apply mask to original image using canvas compositing
                            try {
                                const { normalCutout, invertedCutout } = await applyMaskToImage(imageUrl, maskUrl, token, true);

                                // Upload both cutouts to storage
                                const [normalUpRes, invertedUpRes] = await Promise.all([
                                    api.post('/storage/upload-url', {
                                        url: normalCutout,
                                        bucket: 'media-generations'
                                    }),
                                    api.post('/storage/upload-url', {
                                        url: invertedCutout,
                                        bucket: 'media-generations'
                                    })
                                ]);

                                const normalUrl = normalUpRes.data?.url || normalCutout;
                                const invertedUrl = invertedUpRes.data?.url || invertedCutout;

                                // Create media_generations entry for normal cutout
                                await api.post('/media-generations', {
                                    generation_id: request_id,
                                    canvas_id: canvasId,
                                    type: 'image',
                                    url: normalUrl,
                                    provider: 'fal',
                                    model: 'fal-ai/sam2/image',
                                    prompt: 'SAM2 Cutout',
                                    metadata: {
                                        width: imageData.width || 1024,
                                        height: imageData.height || 1024,
                                        format: 'png'
                                    }
                                });

                                // Store both for toggling, reset to normal view
                                setMaskPreview(normalUrl);
                                setInvertedMaskPreview(invertedUrl);
                                setIsMaskInverted(false);
                                messageApi.success('Cutout ready! Toggle to switch between subject and background.');
                            } catch (e) {
                                console.error('Mask compositing failed:', e);
                                messageApi.error('Failed to create cutout: ' + e.message);
                            }
                        } else {
                            console.error('Unexpected result structure:', result);
                            messageApi.error('No mask image in result');
                        }
                    } catch (e) {
                        console.error('Error processing result:', e);
                        messageApi.error('Error processing result');
                    }
                    setLoading(false);
                },
                onFail: (err) => {
                    console.error('Segmentation failed:', err);
                    messageApi.error('Segmentation failed');
                    setLoading(false);
                }
            });

        } catch (error) {
            console.error('Segmentation failed:', error);
            messageApi.error('Segmentation failed: ' + (error.response?.data?.error || error.message));
            setLoading(false);
        }
    };

    // Call Moondream API for text-based segmentation (uses queue like SAM2)
    const performPromptSegmentation = async () => {
        if (!promptText.trim()) {
            messageApi.warning('Enter a text description of what to segment');
            return;
        }

        // Get the base image
        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        console.log('Moondream segmentation - selectedLayer:', selectedLayer);
        console.log('Moondream segmentation - baseLayer:', baseLayer?.id, 'sourceUrl:', baseLayer?.sourceUrl);
        console.log('Moondream segmentation - all layers:', layers.map(l => ({ id: l.id, sourceUrl: l.sourceUrl })));

        if (!baseLayer) {
            messageApi.error('No image layer found');
            return;
        }

        const imageUrl = baseLayer.sourceUrl || initialImage;
        console.log('Moondream segmentation - using imageUrl:', imageUrl);

        if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.includes('localhost')) {
            messageApi.error('Image must be uploaded to cloud storage for segmentation');
            return;
        }

        try {
            setLoading(true);
            const segmentPrompt = promptText;
            setPromptText(''); // Clear input

            const response = await api.post('/fal/segmentation', {
                image_url: imageUrl,
                object: segmentPrompt
            });

            const { request_id, model_id } = response.data;
            const modelId = model_id || 'fal-ai/moondream3-preview/segment';

            // Use standard trackGeneration pattern for polling
            trackGeneration(request_id, modelId, {
                prompt: segmentPrompt,
                type: 'Cutout',
                canvas_id: canvasId,
                promptProp: `Moondream: "${segmentPrompt}"`
            }, {
                onComplete: async (result) => {
                    try {
                        console.log('Moondream Full Result:', JSON.stringify(result, null, 2));

                        // FAL SDK wraps result in data property: result.data.image.url
                        // trackGeneration passes r.data which includes this wrapper
                        const imageUrl_mask = result?.data?.image?.url;
                        if (imageUrl_mask) {
                            console.log('Using mask image URL:', imageUrl_mask);

                            // Proxy mask through our storage to avoid CORS
                            let maskDataUrl = imageUrl_mask;
                            try {
                                const proxyRes = await api.post('/storage/upload-url', {
                                    url: imageUrl_mask,
                                    bucket: 'media-generations'
                                });
                                if (proxyRes.data?.url) maskDataUrl = proxyRes.data.url;
                            } catch (e) {
                                console.warn('Could not proxy mask, using direct URL');
                            }

                            // Apply mask to original image - use invertMaskLogic=true for Moondream (white=subject)
                            const { normalCutout, invertedCutout } = await applyMaskToImage(imageUrl, maskDataUrl, token, true);

                            // Upload both cutouts to storage
                            const [normalUpRes, invertedUpRes] = await Promise.all([
                                api.post('/storage/upload-url', { url: normalCutout, bucket: 'media-generations' }),
                                api.post('/storage/upload-url', { url: invertedCutout, bucket: 'media-generations' })
                            ]);

                            const normalUrl = normalUpRes.data?.url || normalCutout;
                            const invertedUrl = invertedUpRes.data?.url || invertedCutout;

                            // Create media_generations entry for the cutout
                            await api.post('/media-generations', {
                                generation_id: request_id,
                                canvas_id: canvasId,
                                type: 'image',
                                url: normalUrl,
                                provider: 'fal',
                                model: 'fal-ai/moondream3-preview/segment',
                                prompt: `Moondream Cutout: "${segmentPrompt}"`,
                                metadata: {
                                    width: baseLayer.object?.width || 1024,
                                    height: baseLayer.object?.height || 1024,
                                    format: 'png'
                                }
                            });

                            // Store both for toggling, reset to normal view
                            setMaskPreview(normalUrl);
                            setInvertedMaskPreview(invertedUrl);
                            setIsMaskInverted(false);
                            messageApi.success(`Segmented "${segmentPrompt}" - toggle to switch between subject and background`);
                        } else {
                            console.error('No mask image in result:', result);
                            messageApi.error('No mask image in result');
                        }
                    } catch (e) {
                        console.error('Error processing result:', e);
                        messageApi.error('Error processing result');
                    }
                    setLoading(false);
                },
                onFail: (err) => {
                    console.error('Moondream segmentation failed:', err);
                    messageApi.error('Segmentation failed');
                    setLoading(false);
                }
            });

        } catch (error) {
            console.error('Prompt segmentation failed:', error);
            messageApi.error('Segmentation failed: ' + (error.response?.data?.error || error.message));
            setLoading(false);
        }
    };

    // Call SAM 3 API for text-based segmentation with multiple mask support
    const performSam3Segmentation = async () => {
        if (!promptText.trim()) {
            messageApi.warning('Enter a text description of what to segment');
            return;
        }

        // Get the base image
        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        if (!baseLayer) {
            messageApi.error('No image layer found');
            return;
        }

        const imageUrl = baseLayer.sourceUrl || initialImage;

        if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.includes('localhost')) {
            messageApi.error('Image must be uploaded to cloud storage for segmentation');
            return;
        }

        try {
            setLoading(true);
            const segmentPrompt = promptText;
            setPromptText(''); // Clear input

            const response = await api.post('/sam3/segment', {
                image_url: imageUrl,
                prompt: segmentPrompt,
                return_multiple_masks: true,
                max_masks: sam3MaxMasks,
                apply_mask: false,
                include_scores: true,
            });

            const { request_id, model_id } = response.data;
            const modelId = model_id || 'fal-ai/sam-3/image';

            trackGeneration(request_id, modelId, {
                prompt: segmentPrompt,
                type: 'Cutout',
                canvas_id: canvasId,
                promptProp: `SAM3: "${segmentPrompt}" (max ${sam3MaxMasks} masks)`
            }, {
                onComplete: async (result) => {
                    try {
                        console.log('SAM3 Full Result:', JSON.stringify(result, null, 2));

                        // SAM 3 returns masks[] array when return_multiple_masks is true
                        const masks = result?.data?.masks || result?.masks || [];
                        const scores = result?.data?.scores || result?.scores || [];

                        if (masks.length === 0) {
                            // Fallback: check for single image result
                            const singleImage = result?.data?.image || result?.image;
                            if (singleImage?.url) {
                                // Treat as single mask
                                masks.push(singleImage);
                            } else {
                                console.error('No masks in SAM3 result:', result);
                                messageApi.error('No masks returned from SAM 3');
                                setLoading(false);
                                return;
                            }
                        }

                        messageApi.info(`Processing ${masks.length} mask(s)...`);

                        // Process each mask into an individual cutout
                        const cutoutPromises = masks.map(async (mask, idx) => {
                            const maskUrl = mask.url;
                            if (!maskUrl) return null;

                            // Proxy mask URL to our storage
                            let proxiedMaskUrl = maskUrl;
                            try {
                                const proxyRes = await api.post('/storage/upload-url', {
                                    url: maskUrl,
                                    bucket: 'media-generations'
                                });
                                if (proxyRes.data?.url) proxiedMaskUrl = proxyRes.data.url;
                            } catch (e) {
                                console.warn(`Could not proxy mask ${idx}, using direct URL`);
                            }

                            // Apply mask to original image (SAM3: white=subject)
                            const { normalCutout } = await applyMaskToImage(imageUrl, proxiedMaskUrl, token, true);

                            // Upload cutout to storage
                            const uploadRes = await api.post('/storage/upload-url', {
                                url: normalCutout,
                                bucket: 'media-generations'
                            });
                            const cutoutUrl = uploadRes.data?.url || normalCutout;

                            // Create media_generations record
                            const score = scores[idx] !== undefined ? scores[idx] : null;
                            await api.post('/media-generations', {
                                generation_id: `${request_id}_mask_${idx}`,
                                canvas_id: canvasId,
                                type: 'image',
                                url: cutoutUrl,
                                provider: 'fal',
                                model: 'fal-ai/sam-3/image',
                                prompt: `SAM3 Cutout ${idx + 1}: "${segmentPrompt}"${score !== null ? ` (score: ${score.toFixed(3)})` : ''}`,
                                metadata: {
                                    width: mask.width || baseLayer.object?.width || 1024,
                                    height: mask.height || baseLayer.object?.height || 1024,
                                    format: 'png',
                                    mask_index: idx,
                                    confidence_score: score,
                                }
                            });

                            return { url: cutoutUrl, index: idx };
                        });

                        const cutouts = (await Promise.all(cutoutPromises)).filter(Boolean);

                        // Add each cutout as a layer on the canvas
                        for (const cutout of cutouts) {
                            await addImageAsLayer(cutout.url, `SAM3 Cutout ${cutout.index + 1}`, true);
                        }

                        messageApi.success(`SAM 3: ${cutouts.length} cutout(s) created`);
                    } catch (e) {
                        console.error('Error processing SAM3 result:', e);
                        messageApi.error('Error processing SAM 3 result');
                    }
                    setLoading(false);
                },
                onFail: (err) => {
                    console.error('SAM3 segmentation failed:', err);
                    messageApi.error('SAM 3 segmentation failed');
                    setLoading(false);
                }
            });

        } catch (error) {
            console.error('SAM3 segmentation failed:', error);
            messageApi.error('SAM 3 segmentation failed: ' + (error.response?.data?.error || error.message));
            setLoading(false);
        }
    };

    // Qwen Image Layered — decompose image into RGBA layers
    const performQwenLayered = async () => {
        // Get the base image
        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        if (!baseLayer) {
            messageApi.error('No image layer found');
            return;
        }

        const imageUrl = baseLayer.sourceUrl || initialImage;

        if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.includes('localhost')) {
            messageApi.error('Image must be uploaded to cloud storage for layering');
            return;
        }

        try {
            setLoading(true);

            const response = await api.post('/fal/qwen-layered', {
                image_url: imageUrl,
                num_layers: numLayers,
                output_format: 'png',
            });

            const { request_id, model_id } = response.data;
            const modelId = model_id || 'fal-ai/qwen-image-layered';

            trackGeneration(request_id, modelId, {
                prompt: `Qwen Layered (${numLayers} layers)`,
                type: 'Cutout',
                canvas_id: canvasId,
                promptProp: `Qwen Layers: ${numLayers}`
            }, {
                onComplete: async (result) => {
                    try {
                        console.log('Qwen Layered Full Result:', JSON.stringify(result, null, 2));

                        // Qwen Image Layered returns images[] as RGBA layers directly
                        const images = result?.data?.images || result?.images || [];

                        if (images.length === 0) {
                            messageApi.error('No layers returned from Qwen Image Layered');
                            setLoading(false);
                            return;
                        }

                        messageApi.info(`Processing ${images.length} layer(s)...`);

                        const layerPromises = images.map(async (img, idx) => {
                            const layerUrl = img.url;
                            if (!layerUrl) return null;

                            // Upload layer to our storage
                            let finalUrl = layerUrl;
                            try {
                                const uploadRes = await api.post('/storage/upload-url', {
                                    url: layerUrl,
                                    bucket: 'media-generations'
                                });
                                if (uploadRes.data?.url) finalUrl = uploadRes.data.url;
                            } catch (e) {
                                console.warn(`Could not upload layer ${idx}, using direct URL`);
                            }

                            // Create media_generations record
                            await api.post('/media-generations', {
                                generation_id: `${request_id}_layer_${idx}`,
                                canvas_id: canvasId,
                                type: 'image',
                                url: finalUrl,
                                provider: 'fal',
                                model: 'fal-ai/qwen-image-layered',
                                prompt: `Qwen Layer ${idx + 1} of ${images.length}`,
                                metadata: {
                                    width: img.width || baseLayer.object?.width || 1024,
                                    height: img.height || baseLayer.object?.height || 1024,
                                    format: 'png',
                                    layer_index: idx,
                                    total_layers: images.length,
                                }
                            });

                            return { url: finalUrl, index: idx };
                        });

                        const processedLayers = (await Promise.all(layerPromises)).filter(Boolean);

                        // Add each layer to the scrapbook canvas
                        for (const layer of processedLayers) {
                            await addImageAsLayer(layer.url, `Layer ${layer.index + 1}`, true);
                        }

                        messageApi.success(`Qwen Layered: ${processedLayers.length} layer(s) created`);
                    } catch (e) {
                        console.error('Error processing Qwen Layered result:', e);
                        messageApi.error('Error processing Qwen Layered result');
                    }
                    setLoading(false);
                },
                onFail: (err) => {
                    console.error('Qwen Layered failed:', err);
                    messageApi.error('Qwen Layered failed');
                    setLoading(false);
                }
            });

        } catch (error) {
            console.error('Qwen Layered failed:', error);
            messageApi.error('Qwen Layered failed: ' + (error.response?.data?.error || error.message));
            setLoading(false);
        }
    };

    // Cache original image data for a layer (used by depth eraser)
    const cacheOriginalImageData = async (baseLayer) => {
        const imageUrl = baseLayer.sourceUrl || initialImage;
        const isDataUrl = imageUrl.startsWith('data:');

        try {
            let blobUrl;
            if (isDataUrl) {
                blobUrl = imageUrl;
            } else {
                const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(imageUrl)}`;
                const response = await fetch(proxyUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!response.ok) throw new Error('Failed to fetch image');
                const blob = await response.blob();
                blobUrl = URL.createObjectURL(blob);
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = img.width;
                tempCanvas.height = img.height;
                const ctx = tempCanvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                baseLayer.originalImageData = ctx.getImageData(0, 0, img.width, img.height);
                if (!isDataUrl) URL.revokeObjectURL(blobUrl);
                console.log('Cached original image data:', { width: img.width, height: img.height });
            };
            img.onerror = () => {
                console.error('Failed to cache original image');
                if (!isDataUrl) URL.revokeObjectURL(blobUrl);
            };
            img.src = blobUrl;
        } catch (err) {
            console.error('Failed to cache original image:', err);
        }
    };

    // Generate depth map for selected layer using Marigold
    const generateDepthMap = async () => {
        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        if (!baseLayer) {
            messageApi.error('No image layer selected');
            return;
        }

        const imageUrl = baseLayer.sourceUrl || initialImage;
        if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.includes('localhost')) {
            messageApi.error('Image must be uploaded to cloud storage for depth map');
            return;
        }

        try {
            setDepthMapLoading(true);
            setDepthThreshold(1.0); // Reset to fully visible
            setDepthMapData(null);

            const response = await api.post('/fal/marigold-depth', {
                image_url: imageUrl
            });

            const { request_id, model_id } = response.data;

            // Track the generation (using GenerationContext if available)
            // For simplicity, do a polling approach
            let attempts = 0;
            const maxAttempts = 60;
            const pollInterval = 2000;

            const pollForResult = async () => {
                try {
                    // Use correct queue status endpoint with model_id
                    const statusResp = await api.get('/fal/queue/status', {
                        params: { model_id, request_id }
                    });
                    const status = statusResp.data;

                    if (status.status === 'COMPLETED' && status.response_url) {
                        // Get the actual result from the response URL
                        const resultResp = await api.get('/fal/queue/result', {
                            params: { model_id, request_id }
                        });
                        const result = resultResp.data;
                        // Handle both nested (data.image.url) and flat (image.url) response patterns
                        const depthUrl = result?.data?.image?.url || result?.image?.url;
                        setDepthMapUrl(depthUrl);

                        // Pre-load and cache depth map pixel data using authenticated fetch
                        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                        const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(depthUrl)}`;

                        try {
                            const imgResponse = await fetch(proxyUrl, {
                                headers: token ? { Authorization: `Bearer ${token}` } : {}
                            });
                            if (!imgResponse.ok) throw new Error('Failed to fetch depth map');
                            const blob = await imgResponse.blob();
                            const blobUrl = URL.createObjectURL(blob);

                            const img = new Image();
                            img.crossOrigin = 'anonymous';
                            img.onload = () => {
                                const tempCanvas = document.createElement('canvas');
                                tempCanvas.width = img.width;
                                tempCanvas.height = img.height;
                                const ctx = tempCanvas.getContext('2d');
                                ctx.drawImage(img, 0, 0);
                                const imageData = ctx.getImageData(0, 0, img.width, img.height);
                                setDepthMapData(imageData);

                                // Also cache original image data now so preview works immediately
                                const baseLayer = selectedLayer
                                    ? layers.find(l => l.id === selectedLayer)
                                    : layers[0];
                                if (baseLayer && !baseLayer.originalImageData) {
                                    // Cache the layer's original image data for depth masking
                                    cacheOriginalImageData(baseLayer);
                                }

                                messageApi.success('Depth map ready! Use slider to erase background.');
                                URL.revokeObjectURL(blobUrl);
                            };
                            img.onerror = () => {
                                messageApi.error('Failed to load depth map image');
                                URL.revokeObjectURL(blobUrl);
                            };
                            img.src = blobUrl;
                        } catch (err) {
                            console.error('Failed to load depth map:', err);
                            messageApi.error('Failed to load depth map image');
                        }

                        setDepthMapLoading(false);
                        return;
                    } else if (status.status === 'FAILED') {
                        messageApi.error('Depth map generation failed');
                        setDepthMapLoading(false);
                        return;
                    }

                    // Still processing
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(pollForResult, pollInterval);
                    } else {
                        messageApi.error('Depth map generation timed out');
                        setDepthMapLoading(false);
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                    messageApi.error('Error checking depth map status');
                    setDepthMapLoading(false);
                }
            };

            setTimeout(pollForResult, pollInterval);

        } catch (error) {
            console.error('Depth map generation failed:', error);
            messageApi.error('Depth map failed: ' + (error.response?.data?.error || error.message));
            setDepthMapLoading(false);
        }
    };

    // Apply depth mask to layer based on threshold
    const applyDepthMask = useCallback(async (threshold) => {
        if (!depthMapData || !fabricRef.current) {
            return;
        }

        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        if (!baseLayer || !baseLayer.originalImageData) {
            // Cache original image data on first use
            if (baseLayer && !baseLayer.originalImageData) {
                const imageUrl = baseLayer.sourceUrl || initialImage;
                const isDataUrl = imageUrl.startsWith('data:');

                try {
                    let blobUrl;
                    if (isDataUrl) {
                        blobUrl = imageUrl;
                    } else {
                        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                        const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(imageUrl)}`;
                        const response = await fetch(proxyUrl, {
                            headers: token ? { Authorization: `Bearer ${token}` } : {}
                        });
                        if (!response.ok) throw new Error('Failed to fetch image');
                        const blob = await response.blob();
                        blobUrl = URL.createObjectURL(blob);
                    }

                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = img.width;
                        tempCanvas.height = img.height;
                        const ctx = tempCanvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        baseLayer.originalImageData = ctx.getImageData(0, 0, img.width, img.height);
                        if (!isDataUrl) URL.revokeObjectURL(blobUrl);
                        // Now apply the mask
                        applyDepthMaskInternal(baseLayer, threshold);
                    };
                    img.onerror = () => {
                        console.error('Failed to load image for depth mask');
                        if (!isDataUrl) URL.revokeObjectURL(blobUrl);
                    };
                    img.src = blobUrl;
                } catch (err) {
                    console.error('Failed to fetch image for depth mask:', err);
                }
                return;
            }
            return;
        }

        applyDepthMaskInternal(baseLayer, threshold);
    }, [depthMapData, layers, selectedLayer, initialImage, token]);

    // Debounced version for real-time preview while sliding (preview only, no save)
    const debounceTimerRef = useRef(null);
    const previewThresholdRef = useRef(null);

    // Use ref-based callback to always have access to fresh state
    const debouncedApplyDepthMask = useCallback((val) => {
        previewThresholdRef.current = val;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            // Access fresh state values via closure over the outer scope
            const threshold = previewThresholdRef.current;

            if (!depthMapData || !fabricRef.current) return;

            const baseLayer = selectedLayer
                ? layers.find(l => l.id === selectedLayer)
                : layers[0];

            if (!baseLayer || !baseLayer.originalImageData) {

                return;
            }

            applyDepthMaskInternal(baseLayer, threshold, true);
        }, 150);
    }, [depthMapData, selectedLayer, layers]);

    // ─── Helper: render masked pixels onto the Fabric canvas ───
    const applyMaskedResultToCanvas = async (maskedPixels, width, height, baseLayer, previewOnly) => {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const ctx = tempCanvas.getContext('2d');
        const imageData = new ImageData(new Uint8ClampedArray(maskedPixels), width, height);
        ctx.putImageData(imageData, 0, 0);

        // Encode to data URL (or blob via OffscreenCanvas when available)
        let maskedDataUrl;
        if (typeof OffscreenCanvas !== 'undefined' && !previewOnly) {
            try {
                const osc = new OffscreenCanvas(width, height);
                const oscCtx = osc.getContext('2d');
                oscCtx.putImageData(imageData, 0, 0);
                const blob = await osc.convertToBlob({ type: 'image/png' });
                maskedDataUrl = URL.createObjectURL(blob);
            } catch {
                maskedDataUrl = tempCanvas.toDataURL('image/png');
            }
        } else {
            maskedDataUrl = tempCanvas.toDataURL('image/png');
        }

        try {
            const newImg = await FabricImage.fromURL(maskedDataUrl);
            // Revoke blob URL after image is loaded
            if (maskedDataUrl.startsWith('blob:')) URL.revokeObjectURL(maskedDataUrl);

            const canvas = fabricRef.current;
            if (!canvas) return;
            const origObj = baseLayer.object;

            newImg.set({
                left: origObj.left,
                top: origObj.top,
                scaleX: origObj.scaleX,
                scaleY: origObj.scaleY,
                originX: origObj.originX,
                originY: origObj.originY,
                selectable: false,
                evented: false
            });

            if (previewOnly) {
                if (depthPreviewRef.current) {
                    canvas.remove(depthPreviewRef.current);
                }
                origObj.set({ opacity: 0 });
                canvas.add(newImg);
                canvas.bringObjectToFront(newImg);
                depthPreviewRef.current = newImg;
                canvas.renderAll();
            } else {
                if (depthPreviewRef.current) {
                    canvas.remove(depthPreviewRef.current);
                    depthPreviewRef.current = null;
                }
                origObj.set({ opacity: 1 });

                const objects = canvas.getObjects();
                const zIndex = objects.indexOf(origObj);
                canvas.remove(origObj);

                newImg.set({ selectable: true, evented: true });

                if (zIndex >= 0) {
                    canvas.insertAt(zIndex, newImg);
                } else {
                    canvas.add(newImg);
                }
                canvas.renderAll();

                setLayers(prev => prev.map(l =>
                    l.id === baseLayer.id ? { ...l, object: newImg } : l
                ));
            }
        } catch (err) {
            console.error('Failed to apply depth mask:', err);
        }
    };

    // ─── Main-thread fallback for depth mask (used when worker unavailable) ───
    const applyDepthMaskMainThread = (origData, depthData, threshold) => {
        const width = origData.width;
        const height = origData.height;
        const origPixels = origData.data;
        const depthPixels = depthData.data;
        const output = new Uint8ClampedArray(width * height * 4);

        const scaleX = depthData.width / width;
        const scaleY = depthData.height / height;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const origIdx = (y * width + x) * 4;
                const depthX = Math.floor(x * scaleX);
                const depthY = Math.floor(y * scaleY);
                const depthIdx = (depthY * depthData.width + depthX) * 4;
                const depthValue = depthPixels[depthIdx] / 255;

                if (depthValue <= threshold) {
                    output[origIdx] = origPixels[origIdx];
                    output[origIdx + 1] = origPixels[origIdx + 1];
                    output[origIdx + 2] = origPixels[origIdx + 2];
                    output[origIdx + 3] = origPixels[origIdx + 3];
                }
            }
        }
        return output.buffer;
    };

    // previewOnly: if true, only update canvas visually, don't update layers state (avoids triggering saves)
    const applyDepthMaskInternal = async (baseLayerArg, threshold, previewOnly = false) => {
        const baseLayer = baseLayerArg || (selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0]);

        if (!depthMapData || !baseLayer?.originalImageData) {
            return;
        }

        const origData = baseLayer.originalImageData;
        const depthData = depthMapData;
        const width = origData.width;
        const height = origData.height;

        // Bump generation so stale worker results are discarded
        const generation = ++depthGenerationRef.current;

        const worker = depthWorkerRef.current;

        if (worker) {
            // ── Worker path (off main thread) ──
            // Copy pixel data into transferable ArrayBuffers
            const origBuf = origData.data.buffer.slice(0);
            const depthBuf = depthData.data.buffer.slice(0);

            // One-shot handler for this generation
            worker.onmessage = (ev) => {
                if (ev.data.generation !== depthGenerationRef.current) return; // stale
                applyMaskedResultToCanvas(ev.data.maskedPixels, ev.data.width, ev.data.height, baseLayer, previewOnly);
            };

            worker.postMessage(
                {
                    origPixels: origBuf,
                    depthPixels: depthBuf,
                    width,
                    height,
                    depthWidth: depthData.width,
                    depthHeight: depthData.height,
                    threshold,
                    generation,
                },
                [origBuf, depthBuf],
            );
        } else {
            // ── Fallback: run on main thread (same behaviour as before) ──
            const maskedPixels = applyDepthMaskMainThread(origData, depthData, threshold);
            await applyMaskedResultToCanvas(maskedPixels, width, height, baseLayer, previewOnly);
        }
    };

    // Helper to save cutout to storage and database
    const saveCutoutToStorage = useCallback(async (dataUrl, name, type = 'stamp') => {
        try {
            // Upload to storage
            const uploadRes = await api.post('/storage/upload-url', {
                url: dataUrl,
                bucket: 'media-generations'
            });
            const url = uploadRes.data?.url || dataUrl;

            // Create media-generations entry
            await api.post('/media-generations', {
                generation_id: `${type}-punch-${Date.now()}`,
                canvas_id: canvasId,
                type: 'image',
                url,
                provider: 'scrapbook',
                model: `${type}-punch`,
                prompt: `${name} Cutout`,
                metadata: { format: 'png' }
            });

            return url;
        } catch (error) {
            console.error('Failed to save cutout to storage:', error);
            return dataUrl; // Fall back to data URL
        }
    }, [canvasId]);

    // Apply border punch pattern to selected edges
    const applyBorderPunch = useCallback(async () => {
        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        if (!baseLayer) {
            messageApi.error('No layer selected');
            return;
        }

        const canvas = fabricRef.current;
        const obj = baseLayer.object;
        if (!canvas || !obj) return;

        try {
            setLoading(true);

            // Get image URL - prefer sourceUrl, fall back to initialImage
            const imageUrl = baseLayer.sourceUrl || initialImage;

            // Get image - data URLs can be used directly, remote URLs need proxy
            let blobUrl;
            const isDataUrl = imageUrl.startsWith('data:');

            if (isDataUrl) {
                // Data URLs can be used directly
                blobUrl = imageUrl;
            } else {
                // Remote URLs need to go through authenticated proxy
                const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(imageUrl)}`;

                const response = await fetch(proxyUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!response.ok) throw new Error('Failed to fetch image');
                const blob = await response.blob();
                blobUrl = URL.createObjectURL(blob);
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = async () => {
                const width = img.width;
                const height = img.height;
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = width;
                tempCanvas.height = height;
                const ctx = tempCanvas.getContext('2d');

                // Draw original image
                ctx.drawImage(img, 0, 0);

                // Apply punch pattern to selected edges
                const patternSize = 20; // Size of each punch pattern element
                ctx.globalCompositeOperation = 'destination-out';
                ctx.fillStyle = '#000';

                const pattern = selectedBorderPattern;

                if (selectedBorderEdges.top) {
                    applyEdgePattern(ctx, 'top', width, height, pattern, patternSize);
                }
                if (selectedBorderEdges.right) {
                    applyEdgePattern(ctx, 'right', width, height, pattern, patternSize);
                }
                if (selectedBorderEdges.bottom) {
                    applyEdgePattern(ctx, 'bottom', width, height, pattern, patternSize);
                }
                if (selectedBorderEdges.left) {
                    applyEdgePattern(ctx, 'left', width, height, pattern, patternSize);
                }

                // Trim transparent pixels from the punched image for tight bounding box
                const punchedImageData = ctx.getImageData(0, 0, width, height);
                const punchedData = punchedImageData.data;
                let pMinX = width, pMinY = height, pMaxX = 0, pMaxY = 0;

                for (let y = 0; y < height; y++) {
                    for (let x = 0; x < width; x++) {
                        const alpha = punchedData[(y * width + x) * 4 + 3];
                        if (alpha > 0) {
                            if (x < pMinX) pMinX = x;
                            if (x > pMaxX) pMaxX = x;
                            if (y < pMinY) pMinY = y;
                            if (y > pMaxY) pMaxY = y;
                        }
                    }
                }

                // Create trimmed punched image
                let maskedDataUrl;
                let punchOffsetX = 0, punchOffsetY = 0;

                if (pMaxX >= pMinX && pMaxY >= pMinY) {
                    const trimW = pMaxX - pMinX + 1;
                    const trimH = pMaxY - pMinY + 1;
                    const trimmedPunchCanvas = document.createElement('canvas');
                    trimmedPunchCanvas.width = trimW;
                    trimmedPunchCanvas.height = trimH;
                    const trimmedPunchCtx = trimmedPunchCanvas.getContext('2d');
                    trimmedPunchCtx.drawImage(tempCanvas, pMinX, pMinY, trimW, trimH, 0, 0, trimW, trimH);
                    maskedDataUrl = trimmedPunchCanvas.toDataURL('image/png');
                    punchOffsetX = pMinX;
                    punchOffsetY = pMinY;
                } else {
                    maskedDataUrl = tempCanvas.toDataURL('image/png');
                }

                // === CREATE EDGE STRIP CUTOUTS AS SEPARATE LAYERS ===
                // Strategy: Original image MINUS punched image = the decorative strips
                const edgeCutouts = [];
                const stripWidth = patternSize * 2;

                // Create the difference canvas (what was punched out)
                const diffCanvas = document.createElement('canvas');
                diffCanvas.width = width;
                diffCanvas.height = height;
                const diffCtx = diffCanvas.getContext('2d');

                // Draw original image
                diffCtx.drawImage(img, 0, 0);

                // Load the punched result and subtract it
                const punchedImg = new Image();
                punchedImg.onload = async () => {
                    // Subtract the punched image using destination-out
                    // This removes everything that's still in the punched image,
                    // leaving only what was punched out (the decorative edge)
                    diffCtx.globalCompositeOperation = 'destination-out';
                    diffCtx.drawImage(punchedImg, 0, 0);

                    // Now extract each edge's strip from the difference
                    for (const [edge, selected] of Object.entries(selectedBorderEdges)) {
                        if (!selected) continue;

                        // Define the edge strip region
                        let clipX = 0, clipY = 0, clipW = width, clipH = height;
                        switch (edge) {
                            case 'top':
                                clipH = stripWidth;
                                break;
                            case 'bottom':
                                clipY = height - stripWidth;
                                clipH = stripWidth;
                                break;
                            case 'left':
                                clipW = stripWidth;
                                break;
                            case 'right':
                                clipX = width - stripWidth;
                                clipW = stripWidth;
                                break;
                            default:
                                break;
                        }

                        // Extract this edge's portion from the diff
                        const stripCanvas = document.createElement('canvas');
                        stripCanvas.width = clipW;
                        stripCanvas.height = clipH;
                        const stripCtx = stripCanvas.getContext('2d');
                        stripCtx.drawImage(diffCanvas, clipX, clipY, clipW, clipH, 0, 0, clipW, clipH);

                        // Trim transparent pixels for tight bounding box
                        const imageData = stripCtx.getImageData(0, 0, clipW, clipH);
                        const data = imageData.data;
                        let minX = clipW, minY = clipH, maxX = 0, maxY = 0;

                        for (let y = 0; y < clipH; y++) {
                            for (let x = 0; x < clipW; x++) {
                                const alpha = data[(y * clipW + x) * 4 + 3];
                                if (alpha > 0) {
                                    if (x < minX) minX = x;
                                    if (x > maxX) maxX = x;
                                    if (y < minY) minY = y;
                                    if (y > maxY) maxY = y;
                                }
                            }
                        }

                        // Create trimmed canvas or use full strip
                        let stripDataUrl;
                        let offsetX = clipX, offsetY = clipY;

                        if (maxX >= minX && maxY >= minY) {
                            const trimW = maxX - minX + 1;
                            const trimH = maxY - minY + 1;
                            const trimmedCanvas = document.createElement('canvas');
                            trimmedCanvas.width = trimW;
                            trimmedCanvas.height = trimH;
                            const trimmedCtx = trimmedCanvas.getContext('2d');
                            trimmedCtx.drawImage(stripCanvas, minX, minY, trimW, trimH, 0, 0, trimW, trimH);

                            stripDataUrl = trimmedCanvas.toDataURL('image/png');
                            offsetX = clipX + minX;
                            offsetY = clipY + minY;

                            edgeCutouts.push({ edge, dataUrl: stripDataUrl, offsetX, offsetY });
                        }
                    }

                    // ---- finishBorderPunch inlined here ----
                    // Update the fabric object (punched image) with correct position after trimming
                    const newImg = await FabricImage.fromURL(maskedDataUrl);

                    // Calculate new position accounting for trim offset
                    const origLeft = obj.originX === 'center' ? obj.left - (obj.width * obj.scaleX / 2) : obj.left;
                    const origTop = obj.originY === 'center' ? obj.top - (obj.height * obj.scaleY / 2) : obj.top;

                    newImg.set({
                        left: origLeft + (punchOffsetX * obj.scaleX),
                        top: origTop + (punchOffsetY * obj.scaleY),
                        scaleX: obj.scaleX,
                        scaleY: obj.scaleY,
                        originX: 'left',
                        originY: 'top',
                        selectable: obj.selectable
                    });

                    // Preserve z-index by getting object's position before removal
                    const objectIndex = canvas.getObjects().indexOf(obj);
                    canvas.remove(obj);
                    if (objectIndex >= 0) {
                        canvas.insertAt(objectIndex, newImg);
                    } else {
                        canvas.add(newImg);
                    }

                    // Create cutout layers for each edge strip
                    const newCutoutLayers = [];
                    const separationOffset = 40;
                    for (const cutout of edgeCutouts) {
                        const savedUrl = await saveCutoutToStorage(cutout.dataUrl, `${cutout.edge} edge`, 'border');
                        const stripImg = await FabricImage.fromURL(cutout.dataUrl);

                        const imgLeft = obj.originX === 'center' ? obj.left - (obj.width * obj.scaleX / 2) : obj.left;
                        const imgTop = obj.originY === 'center' ? obj.top - (obj.height * obj.scaleY / 2) : obj.top;

                        let stripLeft = imgLeft + (cutout.offsetX * obj.scaleX);
                        let stripTop = imgTop + (cutout.offsetY * obj.scaleY);

                        switch (cutout.edge) {
                            case 'top': stripTop -= separationOffset; break;
                            case 'bottom': stripTop += separationOffset; break;
                            case 'left': stripLeft -= separationOffset; break;
                            case 'right': stripLeft += separationOffset; break;
                        }

                        stripImg.set({
                            left: stripLeft,
                            top: stripTop,
                            scaleX: obj.scaleX,
                            scaleY: obj.scaleY,
                            originX: 'left',
                            originY: 'top',
                            selectable: true
                        });
                        canvas.add(stripImg);

                        newCutoutLayers.push({
                            id: `layer_${Date.now()}_${cutout.edge}`,
                            name: `${cutout.edge} edge strip`,
                            object: stripImg,
                            visible: true,
                            locked: false,
                            opacity: 1,
                            sourceUrl: savedUrl
                        });
                    }

                    canvas.renderAll();

                    setLayers(prev => [
                        ...prev.map(l => l.id === baseLayer.id ? { ...l, object: newImg, sourceUrl: maskedDataUrl } : l),
                        ...newCutoutLayers
                    ]);

                    messageApi.success(`Border punch applied! ${newCutoutLayers.length} edge strip(s) added.`);
                    setLoading(false);
                    if (!isDataUrl) URL.revokeObjectURL(blobUrl);
                };
                punchedImg.src = maskedDataUrl;
            };

            img.onerror = () => {
                messageApi.error('Failed to load image');
                setLoading(false);
                if (!isDataUrl) URL.revokeObjectURL(blobUrl);
            };

            img.src = blobUrl;

        } catch (error) {
            console.error('Border punch failed:', error);
            messageApi.error('Border punch failed');
            setLoading(false);
        }
    }, [selectedLayer, layers, selectedBorderEdges, selectedBorderPattern, initialImage, messageApi, token]);

    // Helper to apply edge pattern
    const applyEdgePattern = (ctx, edge, width, height, pattern, size) => {
        const count = edge === 'top' || edge === 'bottom'
            ? Math.ceil(width / size)
            : Math.ceil(height / size);

        for (let i = 0; i < count; i++) {
            let x, y;

            switch (edge) {
                case 'top':
                    x = i * size + size / 2;
                    y = 0;
                    break;
                case 'bottom':
                    x = i * size + size / 2;
                    y = height;
                    break;
                case 'left':
                    x = 0;
                    y = i * size + size / 2;
                    break;
                case 'right':
                    x = width;
                    y = i * size + size / 2;
                    break;
                default:
                    x = 0; y = 0;
            }

            switch (pattern) {
                case 'scalloped':
                    ctx.beginPath();
                    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
                    ctx.fill();
                    break;
                case 'wavy':
                    const waveDepth = size / 2;
                    ctx.beginPath();
                    if (edge === 'top' || edge === 'bottom') {
                        const yBase = edge === 'top' ? 0 : height;
                        const yDir = edge === 'top' ? 1 : -1;
                        ctx.ellipse(x, yBase, size / 2, waveDepth * (i % 2 === 0 ? 1 : 0.5), 0, 0, Math.PI * yDir);
                    } else {
                        const xBase = edge === 'left' ? 0 : width;
                        const xDir = edge === 'left' ? 1 : -1;
                        ctx.ellipse(xBase, y, waveDepth * (i % 2 === 0 ? 1 : 0.5), size / 2, 0, 0, Math.PI * xDir);
                    }
                    ctx.fill();
                    break;
                case 'zigzag':
                    ctx.beginPath();
                    if (edge === 'top') {
                        ctx.moveTo(i * size, 0);
                        ctx.lineTo(i * size + size / 2, size);
                        ctx.lineTo(i * size + size, 0);
                        ctx.closePath();
                    } else if (edge === 'bottom') {
                        ctx.moveTo(i * size, height);
                        ctx.lineTo(i * size + size / 2, height - size);
                        ctx.lineTo(i * size + size, height);
                        ctx.closePath();
                    } else if (edge === 'left') {
                        ctx.moveTo(0, i * size);
                        ctx.lineTo(size, i * size + size / 2);
                        ctx.lineTo(0, i * size + size);
                        ctx.closePath();
                    } else {
                        ctx.moveTo(width, i * size);
                        ctx.lineTo(width - size, i * size + size / 2);
                        ctx.lineTo(width, i * size + size);
                        ctx.closePath();
                    }
                    ctx.fill();
                    break;
                case 'postage':
                    ctx.beginPath();
                    ctx.arc(x, y, size / 4, 0, Math.PI * 2);
                    ctx.fill();
                    break;
                case 'torn':
                default:
                    // Irregular torn edge - random small chunks
                    const randSize = size / 2 + Math.random() * size / 2;
                    const randOffset = (Math.random() - 0.5) * size / 2;
                    ctx.beginPath();
                    if (edge === 'top' || edge === 'bottom') {
                        ctx.arc(x + randOffset, y, randSize / 2, 0, Math.PI * 2);
                    } else {
                        ctx.arc(x, y + randOffset, randSize / 2, 0, Math.PI * 2);
                    }
                    ctx.fill();
                    break;
            }
        }
    };

    // Apply stamp punch at click location
    const applyStampPunch = useCallback(async (canvasX, canvasY) => {
        const baseLayer = selectedLayer
            ? layers.find(l => l.id === selectedLayer)
            : layers[0];

        if (!baseLayer) {
            messageApi.error('No layer selected');
            return;
        }

        const canvas = fabricRef.current;
        const obj = baseLayer.object;
        if (!canvas || !obj) {
            messageApi.error('Canvas or object not found');
            return;
        }

        // Get image URL - prefer sourceUrl, fall back to initialImage
        const imageUrl = baseLayer.sourceUrl || initialImage;
        if (!imageUrl) {
            messageApi.error('No image source available for this layer');
            console.error('Layer has no sourceUrl:', baseLayer);
            return;
        }

        try {
            setLoading(true);

            // Convert canvas coordinates to image coordinates
            const imgLeft = obj.left - (obj.originX === 'center' ? obj.width * obj.scaleX / 2 : 0);
            const imgTop = obj.top - (obj.originY === 'center' ? obj.height * obj.scaleY / 2 : 0);
            const imgX = (canvasX - imgLeft) / obj.scaleX;
            const imgY = (canvasY - imgTop) / obj.scaleY;

            console.log('Stamp punch details:', { canvasX, canvasY, imgX, imgY, imageUrl: imageUrl.substring(0, 50) });

            // Get image - data URLs can be used directly, remote URLs need proxy
            let blobUrl;
            const isDataUrl = imageUrl.startsWith('data:');

            if (isDataUrl) {
                // Data URLs can be used directly
                blobUrl = imageUrl;
            } else {
                // Remote URLs need to go through authenticated proxy
                const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(imageUrl)}`;

                const response = await fetch(proxyUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!response.ok) {
                    console.error('Fetch failed:', response.status, response.statusText);
                    throw new Error(`Failed to fetch image: ${response.status}`);
                }
                const blob = await response.blob();
                blobUrl = URL.createObjectURL(blob);
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = async () => {
                const width = img.width;
                const height = img.height;
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = width;
                tempCanvas.height = height;
                const ctx = tempCanvas.getContext('2d');

                // Draw original image
                ctx.drawImage(img, 0, 0);

                // Punch out the shape at click location
                ctx.globalCompositeOperation = 'destination-out';
                ctx.fillStyle = '#000';

                const size = punchShapeSize / obj.scaleX; // Scale size to image coords
                const rotationRad = (punchRotation * Math.PI) / 180; // Convert to radians

                // Apply rotation around punch center
                ctx.save();
                ctx.translate(imgX, imgY);
                ctx.rotate(rotationRad);

                switch (selectedPunchShape) {
                    case 'circle':
                        ctx.beginPath();
                        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
                        ctx.fill();
                        break;
                    case 'star':
                        drawStar(ctx, 0, 0, 5, size / 2, size / 4);
                        ctx.fill();
                        break;
                    case 'heart':
                        drawHeart(ctx, 0, 0, size);
                        ctx.fill();
                        break;
                    case 'hexagon':
                        drawPolygon(ctx, 0, 0, size / 2, 6);
                        ctx.fill();
                        break;
                    case 'triangle':
                        drawPolygon(ctx, 0, 0, size / 2, 3);
                        ctx.fill();
                        break;
                    default:
                        // Handle saved goo shapes
                        if (selectedPunchShape.startsWith('goo')) {
                            const gooShape = savedGooShapes.find(s => s.id === selectedPunchShape);
                            if (gooShape) {
                                // Build a CORS-safe image source
                                let svgSrc;
                                if (gooShape.svgString) {
                                    svgSrc = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(gooShape.svgString)));
                                } else {
                                    // Proxy remote URL to avoid CORS
                                    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                                    const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(gooShape.thumbnailUrl)}`;
                                    const resp = await fetch(proxyUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                                    const blob = await resp.blob();
                                    svgSrc = URL.createObjectURL(blob);
                                }
                                const gooImg = await new Promise((resolve, reject) => {
                                    const img2 = new Image();
                                    img2.onload = () => resolve(img2);
                                    img2.onerror = () => reject(new Error('Failed to load goo SVG'));
                                    img2.src = svgSrc;
                                });
                                const aspect = gooImg.width / gooImg.height;
                                const drawW = aspect >= 1 ? size : size * aspect;
                                const drawH = aspect >= 1 ? size / aspect : size;
                                ctx.drawImage(gooImg, -drawW / 2, -drawH / 2, drawW, drawH);
                            }
                        }
                        break;
                }

                ctx.restore();

                const maskedDataUrl = tempCanvas.toDataURL('image/png');

                // === CREATE CUTOUT PIECE AS SEPARATE LAYER (cropped to shape bounds) ===
                // Calculate bounding box for the shape
                const padding = 4;
                // Calculate bounding box for the shape (account for goo aspect ratio)
                let cropSizeW = size;
                let cropSizeH = size;
                if (selectedPunchShape.startsWith('goo')) {
                    const gooShape = savedGooShapes.find(s => s.id === selectedPunchShape);
                    if (gooShape && gooPreviewCacheRef.current.img && gooPreviewCacheRef.current.id === selectedPunchShape) {
                        const cachedImg = gooPreviewCacheRef.current.img;
                        const aspect = cachedImg.width / cachedImg.height;
                        cropSizeW = aspect >= 1 ? size : size * aspect;
                        cropSizeH = aspect >= 1 ? size / aspect : size;
                    }
                }
                // Expand crop bounds to account for rotation
                const absCos = Math.abs(Math.cos(rotationRad));
                const absSin = Math.abs(Math.sin(rotationRad));
                const rotatedW = cropSizeW * absCos + cropSizeH * absSin;
                const rotatedH = cropSizeW * absSin + cropSizeH * absCos;
                const cropX = Math.max(0, Math.floor(imgX - rotatedW / 2 - padding));
                const cropY = Math.max(0, Math.floor(imgY - rotatedH / 2 - padding));
                const cropW = Math.min(width - cropX, Math.ceil(rotatedW + padding * 2));
                const cropH = Math.min(height - cropY, Math.ceil(rotatedH + padding * 2));

                const cutoutCanvas = document.createElement('canvas');
                cutoutCanvas.width = cropW;
                cutoutCanvas.height = cropH;
                const cutoutCtx = cutoutCanvas.getContext('2d');

                // First, draw the shape as a mask
                const localX = imgX - cropX;
                const localY = imgY - cropY;

                cutoutCtx.save();
                cutoutCtx.translate(localX, localY);
                cutoutCtx.rotate(rotationRad);
                cutoutCtx.fillStyle = '#000';

                switch (selectedPunchShape) {
                    case 'circle':
                        cutoutCtx.beginPath();
                        cutoutCtx.arc(0, 0, size / 2, 0, Math.PI * 2);
                        cutoutCtx.fill();
                        break;
                    case 'star':
                        drawStar(cutoutCtx, 0, 0, 5, size / 2, size / 4);
                        cutoutCtx.fill();
                        break;
                    case 'heart':
                        drawHeart(cutoutCtx, 0, 0, size);
                        cutoutCtx.fill();
                        break;
                    case 'hexagon':
                        drawPolygon(cutoutCtx, 0, 0, size / 2, 6);
                        cutoutCtx.fill();
                        break;
                    case 'triangle':
                        drawPolygon(cutoutCtx, 0, 0, size / 2, 3);
                        cutoutCtx.fill();
                        break;
                    default:
                        // Handle saved goo shapes for cutout
                        if (selectedPunchShape.startsWith('goo')) {
                            const gooShape = savedGooShapes.find(s => s.id === selectedPunchShape);
                            if (gooShape) {
                                let svgSrc;
                                if (gooShape.svgString) {
                                    svgSrc = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(gooShape.svgString)));
                                } else {
                                    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                                    const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(gooShape.thumbnailUrl)}`;
                                    const resp = await fetch(proxyUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                                    const blob = await resp.blob();
                                    svgSrc = URL.createObjectURL(blob);
                                }
                                const gooImg = await new Promise((resolve, reject) => {
                                    const img2 = new Image();
                                    img2.onload = () => resolve(img2);
                                    img2.onerror = () => reject(new Error('Failed to load goo SVG'));
                                    img2.src = svgSrc;
                                });
                                const aspect = gooImg.width / gooImg.height;
                                const drawW = aspect >= 1 ? size : size * aspect;
                                const drawH = aspect >= 1 ? size / aspect : size;
                                cutoutCtx.drawImage(gooImg, -drawW / 2, -drawH / 2, drawW, drawH);
                            }
                        }
                        break;
                }
                cutoutCtx.restore();

                // Now use source-in to keep only the image pixels where we drew the shape
                cutoutCtx.globalCompositeOperation = 'source-in';
                cutoutCtx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

                const cutoutDataUrl = cutoutCanvas.toDataURL('image/png');

                // Update the fabric object (punched image)
                const newImg = await FabricImage.fromURL(maskedDataUrl);
                newImg.set({
                    left: obj.left,
                    top: obj.top,
                    scaleX: obj.scaleX,
                    scaleY: obj.scaleY,
                    originX: obj.originX,
                    originY: obj.originY,
                    selectable: obj.selectable
                });

                // Preserve z-index by getting object's position before removal
                const objectIndex = canvas.getObjects().indexOf(obj);
                canvas.remove(obj);
                // Insert at same position to preserve z-order
                if (objectIndex >= 0) {
                    canvas.insertAt(objectIndex, newImg);
                } else {
                    canvas.add(newImg);
                }

                // Create the cutout piece as a new layer
                // Position it relative to where it was punched, plus offset for visibility
                const cutoutImg = await FabricImage.fromURL(cutoutDataUrl);
                const imgLeft = obj.left - (obj.originX === 'center' ? obj.width * obj.scaleX / 2 : 0);
                const imgTop = obj.top - (obj.originY === 'center' ? obj.height * obj.scaleY / 2 : 0);
                cutoutImg.set({
                    left: imgLeft + cropX * obj.scaleX + 30, // Offset 30px for visibility
                    top: imgTop + cropY * obj.scaleY + 30,
                    scaleX: obj.scaleX,
                    scaleY: obj.scaleY,
                    originX: 'left',
                    originY: 'top',
                    selectable: true
                });
                canvas.add(cutoutImg);
                canvas.renderAll();

                // Save cutout to storage and database
                const savedUrl = await saveCutoutToStorage(cutoutDataUrl, selectedPunchShape, 'stamp');

                // Update layers - modify original and add cutout
                const cutoutLayer = {
                    id: `layer_${Date.now()}`,
                    name: `${selectedPunchShape} cutout`,
                    object: cutoutImg,
                    visible: true,
                    locked: false,
                    opacity: 1,
                    sourceUrl: savedUrl
                };

                setLayers(prev => [
                    ...prev.map(l => l.id === baseLayer.id ? { ...l, object: newImg, sourceUrl: maskedDataUrl } : l),
                    cutoutLayer
                ]);

                messageApi.success('Punch applied! Cutout piece added as new layer.');
                setLoading(false);
                if (!isDataUrl) URL.revokeObjectURL(blobUrl); // Clean up blob URL (not needed for data URLs)
            };

            img.onerror = () => {
                messageApi.error('Failed to load image');
                setLoading(false);
                if (!isDataUrl) URL.revokeObjectURL(blobUrl);
            };

            img.src = blobUrl;

        } catch (error) {
            console.error('Stamp punch failed:', error);
            messageApi.error('Stamp punch failed');
            setLoading(false);
        }
    }, [selectedLayer, layers, selectedPunchShape, punchShapeSize, punchRotation, initialImage, messageApi, token, savedGooShapes]);

    // Sync ref with function to avoid initialization order issues
    useEffect(() => {
        applyStampPunchRef.current = applyStampPunch;
    }, [applyStampPunch]);


    // Helper to draw a star
    const drawStar = (ctx, cx, cy, spikes, outerRadius, innerRadius) => {
        let rot = Math.PI / 2 * 3;
        const step = Math.PI / spikes;
        ctx.beginPath();
        ctx.moveTo(cx, cy - outerRadius);
        for (let i = 0; i < spikes; i++) {
            ctx.lineTo(cx + Math.cos(rot) * outerRadius, cy + Math.sin(rot) * outerRadius);
            rot += step;
            ctx.lineTo(cx + Math.cos(rot) * innerRadius, cy + Math.sin(rot) * innerRadius);
            rot += step;
        }
        ctx.lineTo(cx, cy - outerRadius);
        ctx.closePath();
    };

    // Helper to draw a heart (centered at cx, cy)
    const drawHeart = (ctx, cx, cy, size) => {
        const width = size;
        const height = size;
        // Offset to center the heart vertically (heart extends from -height/2 to +height/2)
        const offsetY = -height / 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy + height / 4 + offsetY);
        ctx.bezierCurveTo(cx, cy + offsetY, cx - width / 2, cy + offsetY, cx - width / 2, cy + height / 4 + offsetY);
        ctx.bezierCurveTo(cx - width / 2, cy + height / 2 + offsetY, cx, cy + height * 0.7 + offsetY, cx, cy + height + offsetY);
        ctx.bezierCurveTo(cx, cy + height * 0.7 + offsetY, cx + width / 2, cy + height / 2 + offsetY, cx + width / 2, cy + height / 4 + offsetY);
        ctx.bezierCurveTo(cx + width / 2, cy + offsetY, cx, cy + offsetY, cx, cy + height / 4 + offsetY);
        ctx.closePath();
    };

    // Helper to draw a regular polygon
    const drawPolygon = (ctx, cx, cy, radius, sides) => {
        ctx.beginPath();
        for (let i = 0; i < sides; i++) {
            const angle = (i * 2 * Math.PI / sides) - Math.PI / 2;
            const x = cx + radius * Math.cos(angle);
            const y = cy + radius * Math.sin(angle);
            if (i === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        }
        ctx.closePath();
    };

    // Create layer from mask preview
    const confirmCutout = async () => {
        // Use the correct cutout based on inversion toggle
        const cutoutUrl = isMaskInverted ? invertedMaskPreview : maskPreview;
        if (!cutoutUrl || !fabricRef.current) return;

        try {
            setLoading(true);

            // Convert URL to data URL to avoid CORS issues with trimTransparent
            let dataUrl = cutoutUrl;
            if (!cutoutUrl.startsWith('data:')) {
                // Fetch via proxy and convert to data URL
                const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
                const proxyUrl = `${API_BASE}/storage/proxy?url=${encodeURIComponent(cutoutUrl)}`;
                const response = await fetch(proxyUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!response.ok) throw new Error('Failed to load image');
                const blob = await response.blob();
                dataUrl = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                });
            }

            // Trim transparent pixels so handles are tight to visible content
            const trimmed = await trimTransparent(dataUrl);

            const img = await FabricImage.fromURL(trimmed.dataUrl);

            const canvas = fabricRef.current;
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;

            const scale = Math.min(
                (canvasWidth * 0.8) / img.width,
                (canvasHeight * 0.8) / img.height
            );

            img.scale(scale);
            img.set({
                left: canvasWidth / 2,
                top: canvasHeight / 2,
                originX: 'center',
                originY: 'center',
                selectable: true
            });

            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();

            // Add to layers with sourceUrl for further operations
            const newLayer = {
                id: `layer_${Date.now()}`,
                name: `Cutout ${layers.length}`,
                object: img,
                sourceUrl: maskPreview, // Store URL for future segmentation
                visible: true,
                locked: false,
                opacity: 1
            };
            setLayers(prev => [...prev, newLayer]);
            setSelectedLayer(newLayer.id);
            setMaskPreview(null);
            setInvertedMaskPreview(null);
            setIsMaskInverted(false);

            // Switch to select tool so user can move/rotate the new cutout
            setActiveTool(TOOLS.SELECT);
            activeToolRef.current = TOOLS.SELECT;

            messageApi.success('Layer created!');
        } catch (error) {
            console.error('Failed to create layer:', error);
            messageApi.error('Failed to create layer');
        } finally {
            setLoading(false);
        }
    };

    // Export canvas and add to main canvas
    // useArtboard: if true, exports full artboard; if false (default), exports content bounds only
    const handleAddToCanvas = async (useArtboard = false) => {
        if (!fabricRef.current) return;

        const canvas = fabricRef.current;
        const objects = canvas.getObjects().filter(o => !o.excludeFromExport);

        if (objects.length === 0) {
            messageApi.warning('No content to export');
            return;
        }

        try {
            setLoading(true);

            let dataUrl;

            // Save current background and make transparent for export
            const originalBg = canvas.backgroundColor;
            canvas.backgroundColor = 'transparent';
            canvas.renderAll();

            if (useArtboard) {
                // Export full artboard
                dataUrl = canvas.toDataURL({
                    format: 'png',
                    quality: 1,
                    multiplier: 2
                });
            } else {
                // Export content bounds only (bounding box of all objects)
                // Use getBoundingRect which properly accounts for rotation, scale, etc.
                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

                objects.forEach(obj => {
                    // getBoundingRect returns {left, top, width, height} accounting for all transforms
                    const rect = obj.getBoundingRect(true); // true = absolute coords

                    minX = Math.min(minX, rect.left);
                    minY = Math.min(minY, rect.top);
                    maxX = Math.max(maxX, rect.left + rect.width);
                    maxY = Math.max(maxY, rect.top + rect.height);
                });

                // Ensure we have valid bounds
                const contentWidth = maxX - minX;
                const contentHeight = maxY - minY;

                console.log('Content bounds:', { minX, minY, maxX, maxY, contentWidth, contentHeight });

                if (contentWidth <= 0 || contentHeight <= 0 || !isFinite(minX) || !isFinite(minY)) {
                    messageApi.warning('No visible content to export');
                    canvas.backgroundColor = originalBg;
                    canvas.renderAll();
                    setLoading(false);
                    return;
                }

                // Create a temporary canvas to render objects with correct bounds
                // This handles objects at any position, including negative coordinates
                const tempCanvas = document.createElement('canvas');
                const multiplier = 2;
                tempCanvas.width = contentWidth * multiplier;
                tempCanvas.height = contentHeight * multiplier;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.scale(multiplier, multiplier);

                // Translate so that minX, minY becomes 0,0
                tempCtx.translate(-minX, -minY);

                // Render each exportable object to the temp canvas
                for (const obj of objects) {
                    if (!obj.visible) continue;

                    // Use Fabric's render method with our context
                    obj.render(tempCtx);
                }

                dataUrl = tempCanvas.toDataURL('image/png');
            }

            // Restore original background
            canvas.backgroundColor = originalBg;
            canvas.renderAll();

            // Save to backend
            const response = await api.post('/sam2/save', {
                image_data: dataUrl,
                canvas_id: canvasId,
                filename: `scrapbook_${Date.now()}.png`
            });

            if (response.data.success && onAddToCanvas) {
                onAddToCanvas({
                    src: response.data.url,
                    label: 'Scrapbook Export',
                    type: 'image'
                });

                // Clear layers for fresh start on next open
                if (fabricRef.current) {
                    fabricRef.current.getObjects().forEach(obj => fabricRef.current.remove(obj));
                    fabricRef.current.renderAll();
                }
                setLayers([]);
                setSelectedLayer(null);
                setMaskPreview(null);
                setInvertedMaskPreview(null);

                messageApi.success('Added to canvas!');
                onClose();
            }
        } catch (error) {
            console.error('Failed to save:', error);
            messageApi.error('Failed to add to canvas');
        } finally {
            setLoading(false);
        }
    };

    // Clear inpaint mask
    const clearInpaintMask = useCallback(() => {
        if (inpaintMaskCanvasRef.current) {
            const ctx = inpaintMaskCanvasRef.current.getContext('2d');
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, inpaintMaskCanvasRef.current.width, inpaintMaskCanvasRef.current.height);
            inpaintMaskDataRef.current = null;
        }
        // Remove overlay from canvas
        if (fabricRef.current) {
            const overlay = fabricRef.current.getObjects().find(o => o.customId === 'inpaint-mask-overlay');
            if (overlay) {
                fabricRef.current.remove(overlay);
                fabricRef.current.renderAll();
            }
        }
        messageApi.success('Mask cleared');
    }, [messageApi]);

    // Handle inpaint generation
    const handleInpaint = async () => {
        if (!fabricRef.current || !inpaintMaskDataRef.current) {
            messageApi.warning('Please paint a mask on the image first');
            return;
        }
        if (!inpaintPrompt.trim()) {
            messageApi.warning('Please enter a prompt describing what to generate');
            return;
        }

        const canvas = fabricRef.current;

        try {
            setLayerLoading('inpaint-generating', true);
            messageApi.loading('Generating inpaint...', 0);

            // Temporarily hide the mask overlay and brush preview for export
            const maskOverlay = canvas.getObjects().find(o => o.customId === 'inpaint-mask-overlay');
            if (maskOverlay) maskOverlay.visible = false;
            if (inpaintBrushPreviewRef.current) inpaintBrushPreviewRef.current.visible = false;
            canvas.renderAll();

            // Export FULL canvas (no cropping) - simpler and avoids coordinate issues
            const canvasDataUrl = canvas.toDataURL({
                format: 'png',
                quality: 1,
                multiplier: 1
            });

            // Restore overlay visibility
            if (maskOverlay) maskOverlay.visible = true;
            if (inpaintBrushPreviewRef.current) inpaintBrushPreviewRef.current.visible = true;
            canvas.renderAll();

            // Upload the full canvas image
            const uploadRes = await api.post('/storage/upload-url', {
                url: canvasDataUrl,
                bucket: 'media-generations'
            });
            const sourceImageUrl = uploadRes.data?.url;

            if (!sourceImageUrl) {
                throw new Error('Failed to upload canvas image');
            }

            // Get the FULL mask (no cropping, no inversion)
            // FLUX Pro Fill convention: White = area to inpaint, Black = area to keep
            // This matches our mask painting convention
            const fullMaskCanvas = inpaintMaskCanvasRef.current;
            const maskDataUrl = fullMaskCanvas.toDataURL('image/png');

            // Upload mask to storage
            const maskUploadRes = await api.post('/storage/upload-url', {
                url: maskDataUrl,
                bucket: 'media-generations'
            });
            const maskUrl = maskUploadRes.data?.url;

            if (!maskUrl) {
                throw new Error('Failed to upload mask');
            }

            console.log('Inpaint request:', {
                sourceImageUrl,
                maskUrl,
                prompt: inpaintPrompt,
                canvasSize: { width: canvas.width, height: canvas.height },
                maskCanvasSize: { width: fullMaskCanvas.width, height: fullMaskCanvas.height }
            });

            // Call FLUX.1 [pro] Fill API
            const response = await api.post('/fal/flux-pro/fill', {
                prompt: inpaintPrompt,
                image_url: sourceImageUrl,
                mask_url: maskUrl,
                output_format: 'png'
            });

            const requestId = response.data?.request_id;
            const modelId = response.data?.model_id || 'fal-ai/flux-pro/v1/fill';

            if (!requestId) {
                throw new Error('No request ID returned');
            }

            console.log('Inpaint submitted:', { requestId, modelId });

            // Poll for result
            let result = null;
            let attempts = 0;
            const maxAttempts = 60;

            while (!result && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 2000));

                try {
                    const statusRes = await api.get('/fal/queue/status', {
                        params: { model_id: modelId, request_id: requestId }
                    });

                    console.log('Inpaint status:', statusRes.data?.status);

                    if (statusRes.data?.status === 'COMPLETED') {
                        const resultRes = await api.get('/fal/queue/result', {
                            params: { model_id: modelId, request_id: requestId }
                        });
                        result = resultRes.data;
                        break;
                    } else if (statusRes.data?.status === 'FAILED') {
                        throw new Error('Inpaint generation failed');
                    }
                } catch (err) {
                    console.log('Status check error (will retry):', err.message);
                }

                attempts++;
            }

            if (!result) {
                throw new Error('Timeout waiting for result');
            }

            const resultUrl = result?.data?.images?.[0]?.url || result?.images?.[0]?.url;
            if (!resultUrl) {
                throw new Error('No result image received');
            }
            console.log('Inpaint result:', resultUrl);

            // Load result image and extract masked region
            const proxyUrl = `${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(resultUrl)}${token ? `&token=${token}` : ''}`;

            // Result image is same dimensions as canvas
            const extractCanvas = document.createElement('canvas');
            extractCanvas.width = canvas.width;
            extractCanvas.height = canvas.height;
            const extractCtx = extractCanvas.getContext('2d');

            // Load result image
            const tempImg = new Image();
            tempImg.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
                tempImg.onload = resolve;
                tempImg.onerror = reject;
                tempImg.src = proxyUrl;
            });
            extractCtx.drawImage(tempImg, 0, 0, canvas.width, canvas.height);

            // Get mask data and find mask bounds (using ORIGINAL mask, not inverted)
            const maskCtx = fullMaskCanvas.getContext('2d');
            const maskData = maskCtx.getImageData(0, 0, fullMaskCanvas.width, fullMaskCanvas.height);
            const extractData = extractCtx.getImageData(0, 0, canvas.width, canvas.height);

            // Track mask bounds and make non-mask areas transparent
            let maskMinX = canvas.width, maskMinY = canvas.height;
            let maskMaxX = 0, maskMaxY = 0;

            for (let i = 0; i < maskData.data.length; i += 4) {
                const pixelIndex = i / 4;
                const x = pixelIndex % canvas.width;
                const y = Math.floor(pixelIndex / canvas.width);

                if (maskData.data[i] > 128) {
                    // White pixel = inpainted area = keep visible
                    maskMinX = Math.min(maskMinX, x);
                    maskMinY = Math.min(maskMinY, y);
                    maskMaxX = Math.max(maskMaxX, x);
                    maskMaxY = Math.max(maskMaxY, y);
                } else {
                    // Black pixel = original area = make transparent
                    extractData.data[i + 3] = 0;
                }
            }
            extractCtx.putImageData(extractData, 0, 0);

            console.log('Mask bounds:', { maskMinX, maskMinY, maskMaxX, maskMaxY });

            // Trim transparent edges and add as layer
            const trimResult = await trimTransparentPixels(extractCanvas);
            if (trimResult) {
                const extractImg = new FabricImage(trimResult.canvas);
                if (extractImg) {
                    // Position at mask top-left corner (direct canvas coordinates - no offset needed)
                    extractImg.set({
                        left: maskMinX,
                        top: maskMinY,
                        originX: 'left',
                        originY: 'top',
                        selectable: true
                    });
                    canvas.add(extractImg);
                    canvas.renderAll();

                    // Add as layer
                    const extractLayer = {
                        id: `layer_${Date.now()}_inpaint`,
                        name: 'Inpainted Region',
                        object: extractImg,
                        visible: true,
                        locked: false,
                        opacity: 1,
                        sourceUrl: null
                    };
                    setLayers(prev => [...prev, extractLayer]);
                }
            }

            // Clear mask
            clearInpaintMask();
            setInpaintPrompt('');

            messageApi.destroy();
            messageApi.success('Inpaint complete!');

        } catch (error) {
            console.error('Inpaint failed:', error);
            messageApi.destroy();
            messageApi.error(`Inpaint failed: ${error.message}`);
        } finally {
            setLayerLoading('inpaint-generating', false);
        }
    };

    // Export as download
    const handleExport = (format = 'png') => {
        if (!fabricRef.current) return;

        const dataUrl = fabricRef.current.toDataURL({
            format: format,
            quality: format === 'jpeg' ? 0.9 : 1,
            multiplier: 2
        });

        const link = document.createElement('a');
        link.download = `scrapbook_${Date.now()}.${format}`;
        link.href = dataUrl;
        link.click();

        messageApi.success(`Exported as ${format.toUpperCase()}`);
    };

    // Toggle layer visibility
    const toggleLayerVisibility = (layerId) => {
        setLayers(prev => prev.map(layer => {
            if (layer.id === layerId) {
                layer.object.visible = !layer.visible;
                fabricRef.current?.renderAll();
                return { ...layer, visible: !layer.visible };
            }
            return layer;
        }));
    };

    // Delete layer
    const deleteLayer = (layerId) => {
        const layer = layers.find(l => l.id === layerId);
        if (layer && fabricRef.current) {
            fabricRef.current.remove(layer.object);
            fabricRef.current.renderAll();
            setLayers(prev => prev.filter(l => l.id !== layerId));
            if (selectedLayer === layerId) {
                setSelectedLayer(null);
            }
        }
    };

    // Toggle layer lock
    const toggleLayerLock = (layerId) => {
        setLayers(prev => prev.map(layer => {
            if (layer.id === layerId) {
                const newLocked = !layer.locked;
                layer.object.selectable = !newLocked;
                layer.object.evented = !newLocked;
                fabricRef.current?.renderAll();
                return { ...layer, locked: newLocked };
            }
            return layer;
        }));
    };

    // Recenter canvas view (zoom-to-fit entire canvas within container)
    const recenterCanvas = () => {
        if (!fabricRef.current || !containerRef.current) return;
        const canvas = fabricRef.current;
        const container = containerRef.current;

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Calculate zoom to fit canvas within container with some padding
        const padding = 40;
        const scaleX = (containerWidth - padding * 2) / canvasWidth;
        const scaleY = (containerHeight - padding * 2) / canvasHeight;
        const zoom = Math.min(scaleX, scaleY, 1); // Don't zoom beyond 100%

        // Calculate pan to center the canvas
        const panX = (containerWidth - canvasWidth * zoom) / 2;
        const panY = (containerHeight - canvasHeight * zoom) / 2;

        canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
        canvas.renderAll();
        messageApi.success('Canvas recentered');
    };

    // Change canvas aspect ratio
    const changeAspectRatio = (ratioKey) => {
        if (!fabricRef.current || !containerRef.current) return;

        setCanvasAspectRatio(ratioKey);
        const canvas = fabricRef.current;
        const container = containerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        if (ratioKey === 'free') {
            // Fill container
            canvas.setDimensions({
                width: containerWidth,
                height: containerHeight
            });
        } else {
            const preset = ASPECT_PRESETS.find(p => p.key === ratioKey);
            if (!preset) return;

            // Use fixed pixel dimensions if available, otherwise fall back to ratio
            const targetW = preset.w || containerWidth;
            const targetH = preset.h || containerHeight;
            const ratio = targetW / targetH;

            // Scale down to fit container while preserving aspect ratio
            let newWidth, newHeight;
            if (containerWidth / containerHeight > ratio) {
                newHeight = containerHeight;
                newWidth = newHeight * ratio;
            } else {
                newWidth = containerWidth;
                newHeight = newWidth / ratio;
            }

            canvas.setDimensions({
                width: Math.floor(newWidth),
                height: Math.floor(newHeight)
            });
        }

        canvas.renderAll();
    };

    // Move layer up in z-order
    const moveLayerUp = (layerId) => {
        const index = layers.findIndex(l => l.id === layerId);
        if (index < layers.length - 1 && fabricRef.current) {
            const layer = layers[index];
            fabricRef.current.bringObjectForward(layer.object);
            fabricRef.current.renderAll();
            setLayers(prev => {
                const newLayers = [...prev];
                [newLayers[index], newLayers[index + 1]] = [newLayers[index + 1], newLayers[index]];
                return newLayers;
            });
        }
    };

    // Move layer down in z-order
    const moveLayerDown = (layerId) => {
        const index = layers.findIndex(l => l.id === layerId);
        if (index > 0 && fabricRef.current) {
            const layer = layers[index];
            fabricRef.current.sendObjectBackwards(layer.object);
            fabricRef.current.renderAll();
            setLayers(prev => {
                const newLayers = [...prev];
                [newLayers[index], newLayers[index - 1]] = [newLayers[index - 1], newLayers[index]];
                return newLayers;
            });
        }
    };

    // ─── Alignment Helpers ───
    const alignObject = (alignment) => {
        const canvas = fabricRef.current;
        if (!canvas) return;
        const obj = canvas.getActiveObject();
        if (!obj) return;

        const bound = obj.getBoundingRect(true);
        const cw = canvas.width;
        const ch = canvas.height;

        switch (alignment) {
            case 'left':
                obj.set('left', obj.left - bound.left);
                break;
            case 'center-h':
                obj.set('left', obj.left + (cw / 2 - (bound.left + bound.width / 2)));
                break;
            case 'right':
                obj.set('left', obj.left + (cw - (bound.left + bound.width)));
                break;
            case 'top':
                obj.set('top', obj.top - bound.top);
                break;
            case 'center-v':
                obj.set('top', obj.top + (ch / 2 - (bound.top + bound.height / 2)));
                break;
            case 'bottom':
                obj.set('top', obj.top + (ch - (bound.top + bound.height)));
                break;
            default:
                break;
        }
        obj.setCoords();
        canvas.renderAll();
        saveSnapshot();
    };

    // ─── Clipping Masks ───
    const handleClipToShape = async () => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        // Use selectedLayerIds as source of truth
        if (selectedLayerIds.length !== 2) return;

        // Find objects from layers
        // selectedLayerIds should be in selection order (bottom to top usually if from getActiveObjects)
        // But let's verify Z-index or trust the array order from handleSelectionChange
        const objects = selectedLayerIds
            .map(id => layers.find(l => l.id === id)?.object)
            .filter(Boolean);

        if (objects.length !== 2) return;

        // Fabric usually returns selection bottom-to-top
        // So [0] is bottom (Content), [1] is top (Mask)
        const mask = objects[1];
        const content = objects[0];

        // Ensure mask is actually on top of content (check z-index via canvas)
        const maskIndex = canvas.getObjects().indexOf(mask);
        const contentIndex = canvas.getObjects().indexOf(content);

        // If sorting failed, swap based on actual index
        const finalMask = maskIndex > contentIndex ? mask : content;
        const finalContent = maskIndex > contentIndex ? content : mask;

        // Use original object as clipPath (no clone) to avoid async/state issues
        finalMask.absolutePositioned = true;

        // Assign clipPath
        finalContent.clipPath = finalMask;

        // Remove original mask (it's now part of the content)
        canvas.remove(finalMask);

        // Update selection to just the content
        canvas.discardActiveObject();
        canvas.setActiveObject(finalContent);

        canvas.requestRenderAll();
        saveSnapshot(); // History
        message.success(`Clipped with ${finalMask.type}`);
    };

    const handleReleaseClip = async () => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        const activeObject = canvas.getActiveObject();
        if (!activeObject || !activeObject.clipPath) return;

        const clipPath = activeObject.clipPath;

        // Clone clipPath to restore it as a regular object
        clipPath.clone().then((clonedMask) => {
            // Restore to canvas
            canvas.add(clonedMask);

            // Remove clip from content
            activeObject.clipPath = null;
            activeObject.dirty = true;

            canvas.requestRenderAll();
            saveSnapshot(); // History
            message.success('Clip released');
        });
    };

    // Handle clicking a layer in the panel (supports multi-select)
    const handleLayerClick = (e, layer) => {
        e.stopPropagation();
        const canvas = fabricRef.current;
        if (!canvas || !layer.object || layer.locked) return;

        const isMultiSelect = e.shiftKey || e.metaKey || e.ctrlKey;

        if (isMultiSelect) {
            const currentSelected = canvas.getActiveObjects();
            let newSelected = [...currentSelected];

            // Check if already selected
            const index = newSelected.indexOf(layer.object);
            if (index > -1) {
                // Deselect
                newSelected.splice(index, 1);
            } else {
                // Select
                newSelected.push(layer.object);
            }

            canvas.discardActiveObject();

            if (newSelected.length === 1) {
                canvas.setActiveObject(newSelected[0]);
            } else if (newSelected.length > 1) {
                const sel = new ActiveSelection(newSelected, {
                    canvas: canvas,
                });
                canvas.setActiveObject(sel);
            }
        } else {
            // Single select
            canvas.discardActiveObject();
            canvas.setActiveObject(layer.object);
        }

        canvas.requestRenderAll();
        // State update handled by 'selection:created'/'updated' event listener
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="bottom"
            height="100%"
            closable={false}
            mask={true}
            maskClosable={true}
            keyboard={false}
            rootStyle={{
                top: HEADER_HEIGHT,
                height: `calc(100vh - ${HEADER_HEIGHT}px)`
            }}
            styles={{
                wrapper: {
                    top: 0,
                    height: '100%'
                },
                mask: {
                    top: 0,
                    height: '100%'
                },
                content: {
                    borderTop: 'none',
                    boxShadow: 'none',
                    height: '100%'
                },
                body: { padding: 0, display: 'flex', flexDirection: 'column', background: '#0d1117', height: '100%' },
                header: { display: 'none' }
            }}
            zIndex={1001}
        >
            {/* Wrapper to prevent keyboard events from bubbling to editor */}
            <div
                style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                onKeyDown={(e) => {
                    // Prevent spacebar and other keys from triggering editor shortcuts
                    if (e.key === ' ' || e.key === 'Escape') {
                        e.stopPropagation();
                    }
                }}
            >
                {/* Header Toolbar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: '1px solid #30363d',
                    background: 'rgba(22, 27, 34, 0.95)'
                }}>
                    <Space>
                        <ScissorOutlined style={{ color: '#a78bfa', fontSize: 18 }} />
                    </Space>

                    {/* Tool Buttons */}
                    <Space size={4} style={{ overflowX: 'auto', flexWrap: 'nowrap', scrollbarWidth: 'none', flex: 1, marginLeft: 8 }}>
                        <Tooltip title="Select & Move">
                            <Button
                                type={activeTool === TOOLS.SELECT ? 'primary' : 'text'}
                                icon={<BorderOutlined />}
                                onClick={() => setActiveTool(TOOLS.SELECT)}
                                style={{ color: activeTool === TOOLS.SELECT ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Point Selection (SAM2)">
                            <Button
                                type={activeTool === TOOLS.POINT_SELECT ? 'primary' : 'text'}
                                icon={<AimOutlined />}
                                onClick={() => setActiveTool(TOOLS.POINT_SELECT)}
                                style={{ color: activeTool === TOOLS.POINT_SELECT ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Text Prompt (Moondream / SAM 3)">
                            <Button
                                type={activeTool === TOOLS.TEXT_SELECT ? 'primary' : 'text'}
                                icon={<FileTextOutlined />}
                                onMouseDown={(e) => {
                                    // Prevent button click from stealing focus from canvas selection
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onClick={() => setActiveTool(TOOLS.TEXT_SELECT)}
                                style={{ color: activeTool === TOOLS.TEXT_SELECT ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Depth Eraser">
                            <Button
                                type={activeTool === TOOLS.DEPTH_ERASER ? 'primary' : 'text'}
                                icon={<VerticalAlignTopOutlined />}
                                onClick={() => setActiveTool(TOOLS.DEPTH_ERASER)}
                                style={{ color: activeTool === TOOLS.DEPTH_ERASER ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Border Punch">
                            <Button
                                type={activeTool === TOOLS.BORDER_PUNCH ? 'primary' : 'text'}
                                icon={<RadiusSettingOutlined />}
                                onClick={() => setActiveTool(TOOLS.BORDER_PUNCH)}
                                style={{ color: activeTool === TOOLS.BORDER_PUNCH ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Stamp Punch">
                            <Button
                                type={activeTool === TOOLS.STAMP_PUNCH ? 'primary' : 'text'}
                                icon={<StarOutlined />}
                                onClick={() => setActiveTool(TOOLS.STAMP_PUNCH)}
                                style={{ color: activeTool === TOOLS.STAMP_PUNCH ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Goo Editor">
                            <Button
                                type={gooEditorVisible ? 'primary' : 'text'}
                                icon={<span style={{ fontSize: 14 }}>✦</span>}
                                onClick={() => setGooEditorVisible(true)}
                                style={{ color: gooEditorVisible ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Pattern Maker">
                            <Button
                                type={patternStandaloneVisible ? 'primary' : 'text'}
                                icon={<span style={{ fontSize: 14 }}>⬡</span>}
                                onClick={() => setPatternStandaloneVisible(true)}
                                style={{ color: patternStandaloneVisible ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Topographic">
                            <Button
                                type={topoLineVisible ? 'primary' : 'text'}
                                icon={<span style={{ fontSize: 14 }}>⛰</span>}
                                onClick={() => setTopoLineVisible(true)}
                                style={{ color: topoLineVisible ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Inpaint">
                            <Button
                                type={activeTool === TOOLS.INPAINT ? 'primary' : 'text'}
                                icon={<HighlightOutlined />}
                                onClick={() => setActiveTool(TOOLS.INPAINT)}
                                style={{ color: activeTool === TOOLS.INPAINT ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Type Tool">
                            <Button
                                type={activeTool === TOOLS.TYPE ? 'primary' : 'text'}
                                icon={<FontSizeOutlined />}
                                onClick={() => setActiveTool(TOOLS.TYPE)}
                                style={{ color: activeTool === TOOLS.TYPE ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Typography & Grid">
                            <Button
                                type={typographyPanelVisible ? 'primary' : 'text'}
                                icon={<span style={{ fontSize: 14, fontWeight: 800, fontFamily: 'serif' }}>Ⓣ</span>}
                                onClick={() => setTypographyPanelVisible(prev => !prev)}
                                style={{ color: typographyPanelVisible ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Tooltip title="Effects">
                            <Button
                                type={(effectsPanelVisible || activeEffect) ? 'primary' : 'text'}
                                icon={<span style={{ fontSize: 14 }}>✦</span>}
                                onClick={() => setEffectsPanelVisible(prev => !prev)}
                                style={{ color: (effectsPanelVisible || activeEffect) ? undefined : '#8b949e' }}
                            />
                        </Tooltip>
                        <Divider type="vertical" style={{ background: '#30363d', height: 24 }} />

                        {/* Alignment Controls */}
                        <Tooltip title="Align Left">
                            <Button
                                type="text"
                                onClick={() => alignObject('left')}
                                style={{ color: '#8b949e', padding: '4px 6px', minWidth: 28 }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="0" width="2" height="14" /><rect x="4" y="2" width="8" height="4" /><rect x="4" y="8" width="5" height="4" /></svg>
                            </Button>
                        </Tooltip>
                        <Tooltip title="Center Horizontally">
                            <Button
                                type="text"
                                onClick={() => alignObject('center-h')}
                                style={{ color: '#8b949e', padding: '4px 6px', minWidth: 28 }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="6" y="0" width="2" height="14" /><rect x="2" y="2" width="10" height="4" /><rect x="3" y="8" width="8" height="4" /></svg>
                            </Button>
                        </Tooltip>
                        <Tooltip title="Align Right">
                            <Button
                                type="text"
                                onClick={() => alignObject('right')}
                                style={{ color: '#8b949e', padding: '4px 6px', minWidth: 28 }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="12" y="0" width="2" height="14" /><rect x="2" y="2" width="8" height="4" /><rect x="5" y="8" width="5" height="4" /></svg>
                            </Button>
                        </Tooltip>
                        <Divider type="vertical" style={{ background: '#30363d', height: 16 }} />
                        <Tooltip title="Align Top">
                            <Button
                                type="text"
                                onClick={() => alignObject('top')}
                                style={{ color: '#8b949e', padding: '4px 6px', minWidth: 28 }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="0" width="14" height="2" /><rect x="2" y="4" width="4" height="8" /><rect x="8" y="4" width="4" height="5" /></svg>
                            </Button>
                        </Tooltip>
                        <Tooltip title="Center Vertically">
                            <Button
                                type="text"
                                onClick={() => alignObject('center-v')}
                                style={{ color: '#8b949e', padding: '4px 6px', minWidth: 28 }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="6" width="14" height="2" /><rect x="2" y="2" width="4" height="10" /><rect x="8" y="3" width="4" height="8" /></svg>
                            </Button>
                        </Tooltip>
                        <Tooltip title="Align Bottom">
                            <Button
                                type="text"
                                onClick={() => alignObject('bottom')}
                                style={{ color: '#8b949e', padding: '4px 6px', minWidth: 28 }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="12" width="14" height="2" /><rect x="2" y="2" width="4" height="8" /><rect x="8" y="5" width="4" height="5" /></svg>
                            </Button>
                        </Tooltip>
                        <Divider type="vertical" style={{ background: '#30363d', height: 24 }} />

                        {/* Custom Guides */}
                        <Popover
                            trigger="click"
                            placement="bottom"
                            getPopupContainer={() => document.body}
                            content={
                                <div style={{ width: 220 }}>
                                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                                        <Button
                                            size="small"
                                            block
                                            onClick={() => {
                                                const cw = fabricRef.current?.width || 800;
                                                setCustomGuides(prev => [...prev, { type: 'v', pos: Math.round(cw / 2) }]);
                                            }}
                                        >
                                            + Vertical
                                        </Button>
                                        <Button
                                            size="small"
                                            block
                                            onClick={() => {
                                                const ch = fabricRef.current?.height || 600;
                                                setCustomGuides(prev => [...prev, { type: 'h', pos: Math.round(ch / 2) }]);
                                            }}
                                        >
                                            + Horizontal
                                        </Button>
                                    </div>
                                    {customGuides.length === 0 && (
                                        <Text style={{ color: '#8b949e', fontSize: 11 }}>No guides added yet</Text>
                                    )}
                                    <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                                        {customGuides.map((g, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                <span style={{
                                                    fontSize: 10, color: '#00bcd4', fontWeight: 600, width: 12, textAlign: 'center'
                                                }}>{g.type === 'h' ? 'H' : 'V'}</span>
                                                <InputNumber
                                                    size="small"
                                                    value={g.pos}
                                                    min={0}
                                                    step={1}
                                                    disabled={g.locked}
                                                    style={{ flex: 1 }}
                                                    onChange={(val) => {
                                                        if (val == null) return;
                                                        setCustomGuides(prev => prev.map((gg, gi) =>
                                                            gi === i ? { ...gg, pos: val } : gg
                                                        ));
                                                    }}
                                                    suffix="px"
                                                />
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    onClick={() => setCustomGuides(prev => prev.map((gg, gi) =>
                                                        gi === i ? { ...gg, locked: !gg.locked } : gg
                                                    ))}
                                                    style={{ padding: '0 4px', minWidth: 20, color: g.locked ? '#00bcd4' : '#8b949e' }}
                                                >
                                                    {g.locked ? '🔒' : '🔓'}
                                                </Button>
                                                <Button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    onClick={() => setCustomGuides(prev => prev.filter((_, gi) => gi !== i))}
                                                    style={{ padding: '0 4px', minWidth: 20 }}
                                                >
                                                    ✕
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                    {customGuides.length > 0 && (
                                        <Button
                                            size="small"
                                            danger
                                            block
                                            style={{ marginTop: 6 }}
                                            onClick={() => setCustomGuides([])}
                                        >
                                            Clear All
                                        </Button>
                                    )}
                                </div>
                            }
                        >
                            <Tooltip title="Guides">
                                <Button
                                    type={customGuides.length > 0 ? 'primary' : 'text'}
                                    style={{
                                        color: customGuides.length > 0 ? undefined : '#8b949e',
                                        padding: '4px 6px', minWidth: 28,
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                                        <rect x="3" y="0" width="1.5" height="14" />
                                        <rect x="9" y="0" width="1.5" height="14" />
                                        <rect x="0" y="5" width="14" height="1.5" />
                                    </svg>
                                </Button>
                            </Tooltip>
                        </Popover>

                        {/* Grid Overlay */}
                        <Popover
                            trigger="click"
                            placement="bottom"
                            getPopupContainer={() => document.body}
                            content={
                                <div style={{ width: 200 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Text style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600 }}>Grid Overlay</Text>
                                        <Button
                                            size="small"
                                            type={gridEnabled ? 'primary' : 'default'}
                                            onClick={() => {
                                                setGridEnabled(prev => !prev);
                                            }}
                                            style={{ fontSize: 11 }}
                                        >
                                            {gridEnabled ? 'On' : 'Off'}
                                        </Button>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 52 }}>Spacing</Text>
                                        <InputNumber
                                            size="small"
                                            min={10}
                                            max={200}
                                            step={10}
                                            value={gridSpacing}
                                            onChange={(v) => { if (v) setGridSpacing(v); }}
                                            suffix="px"
                                            style={{ flex: 1 }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 52 }}>Opacity</Text>
                                        <Slider
                                            min={0.05}
                                            max={0.5}
                                            step={0.05}
                                            value={gridOpacity}
                                            onChange={(v) => setGridOpacity(v)}
                                            style={{ flex: 1, margin: 0 }}
                                        />
                                    </div>
                                </div>
                            }
                        >
                            <Tooltip title="Grid">
                                <Button
                                    type={gridEnabled ? 'primary' : 'text'}
                                    style={{
                                        color: gridEnabled ? undefined : '#8b949e',
                                        padding: '4px 6px', minWidth: 28,
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                                        <rect x="0" y="0" width="1" height="14" />
                                        <rect x="4.5" y="0" width="1" height="14" />
                                        <rect x="9" y="0" width="1" height="14" />
                                        <rect x="13" y="0" width="1" height="14" />
                                        <rect x="0" y="0" width="14" height="1" />
                                        <rect x="0" y="4.5" width="14" height="1" />
                                        <rect x="0" y="9" width="14" height="1" />
                                        <rect x="0" y="13" width="14" height="1" />
                                    </svg>
                                </Button>
                            </Tooltip>
                        </Popover>

                        {/* Clipping Controls */}
                        <Divider type="vertical" style={{ background: '#30363d', height: 24 }} />

                        <Tooltip title={selectedLayerIds.length === 2 ? "Clip to Shape" : "Select 2 objects (Top masks Bottom)"}>
                            <Button
                                type="text"
                                icon={<GatewayOutlined />}
                                onClick={handleClipToShape}
                                disabled={selectedLayerIds.length !== 2}
                                style={{
                                    color: selectedLayerIds.length === 2 ? '#c9d1d9' : '#484f58',
                                    padding: '4px 6px',
                                    minWidth: 28
                                }}
                            />
                        </Tooltip>
                        <Tooltip title={(selectedLayerIds.length === 1 && layers.find(l => l.id === selectedLayerIds[0])?.object?.clipPath) ? "Release Clip" : "Select clipped object to Release"}>
                            <Button
                                type="text"
                                icon={<UngroupOutlined />}
                                onClick={() => {
                                    handleReleaseClip();
                                }}
                                disabled={!(selectedLayerIds.length === 1 && layers.find(l => l.id === selectedLayerIds[0])?.object?.clipPath)}
                                style={{
                                    color: (selectedLayerIds.length === 1 && layers.find(l => l.id === selectedLayerIds[0])?.object?.clipPath) ? '#c9d1d9' : '#484f58',
                                    padding: '4px 6px',
                                    minWidth: 28
                                }}
                            />
                        </Tooltip>

                        <Divider type="vertical" style={{ background: '#30363d', height: 24 }} />

                        {/* Undo / Redo */}
                        <Tooltip title="Undo (⌘Z)">
                            <Button
                                type="text"
                                icon={<UndoOutlined />}
                                onClick={undo}
                                disabled={!canUndo}
                                style={{ color: canUndo ? '#c9d1d9' : '#484f58', padding: '4px 6px', minWidth: 28 }}
                            />
                        </Tooltip>
                        <Tooltip title="Redo (⌘⇧Z)">
                            <Button
                                type="text"
                                icon={<RedoOutlined />}
                                onClick={redo}
                                disabled={!canRedo}
                                style={{ color: canRedo ? '#c9d1d9' : '#484f58', padding: '4px 6px', minWidth: 28 }}
                            />
                        </Tooltip>

                        <Divider type="vertical" style={{ background: '#30363d', height: 24 }} />

                        {/* Viewport Controls */}
                        <Tooltip title="Fit to View">
                            <Button
                                type="text"
                                icon={<ExpandOutlined />}
                                onClick={fitToContent}
                                style={{ color: '#8b949e' }}
                            />
                        </Tooltip>

                        {/* Depth Eraser UI - show when DEPTH_ERASER is active */}
                        {activeTool === TOOLS.DEPTH_ERASER && (
                            <>
                                {!depthMapData ? (
                                    <Button
                                        type="primary"
                                        onClick={generateDepthMap}
                                        loading={depthMapLoading}
                                        style={{ background: '#f97316', marginLeft: 8 }}
                                    >
                                        {depthMapLoading ? 'Generating...' : 'Generate Depth Map'}
                                    </Button>
                                ) : (
                                    <Space style={{ marginLeft: 8 }}>
                                        <Text style={{ color: '#8b949e', fontSize: 12 }}>Depth:</Text>
                                        <Slider
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={depthThreshold}
                                            onChange={(val) => {
                                                setDepthThreshold(val);
                                                debouncedApplyDepthMask(val);
                                            }}
                                            onChangeComplete={(val) => applyDepthMask(val)}
                                            style={{ width: 150 }}
                                            tooltip={{ formatter: (val) => `${Math.round((1 - val) * 100)}% erased` }}
                                        />
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setDepthMapData(null);
                                                setDepthMapUrl(null);
                                                setDepthThreshold(1.0);
                                            }}
                                            style={{ color: '#8b949e' }}
                                        >
                                            Reset
                                        </Button>
                                    </Space>
                                )}
                            </>
                        )}

                        {/* Border Punch UI - show when BORDER_PUNCH is active */}
                        {activeTool === TOOLS.BORDER_PUNCH && (
                            <Space style={{ marginLeft: 8 }}>
                                <Select
                                    value={selectedBorderPattern}
                                    onChange={setSelectedBorderPattern}
                                    style={{ width: 130, background: '#21262d' }}
                                    dropdownStyle={{ background: '#21262d' }}
                                    options={[
                                        { value: 'wavy', label: 'Wavy Edge' },
                                        { value: 'scalloped', label: 'Scalloped' },
                                        { value: 'zigzag', label: 'Zigzag' },
                                        { value: 'torn', label: 'Torn Paper' },
                                        { value: 'postage', label: 'Postage Stamp' }
                                    ]}
                                />
                                <Text style={{ color: '#8b949e', fontSize: 12 }}>Edges:</Text>
                                {['top', 'right', 'bottom', 'left'].map(edge => (
                                    <Button
                                        key={edge}
                                        size="small"
                                        type={selectedBorderEdges[edge] ? 'primary' : 'default'}
                                        onClick={() => setSelectedBorderEdges(prev => ({ ...prev, [edge]: !prev[edge] }))}
                                        style={{
                                            width: 24,
                                            height: 24,
                                            padding: 0,
                                            fontSize: 10,
                                            background: selectedBorderEdges[edge] ? '#a78bfa' : '#21262d',
                                            borderColor: '#30363d'
                                        }}
                                    >
                                        {edge.charAt(0).toUpperCase()}
                                    </Button>
                                ))}
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={applyBorderPunch}
                                    style={{ background: '#a78bfa' }}
                                >
                                    Apply
                                </Button>
                            </Space>
                        )}

                        {/* Stamp Punch UI - show when STAMP_PUNCH is active */}
                        {activeTool === TOOLS.STAMP_PUNCH && (
                            <Space style={{ marginLeft: 8 }}>
                                <Select
                                    value={selectedPunchShape}
                                    onChange={setSelectedPunchShape}
                                    style={{ width: 120, background: '#21262d' }}
                                    dropdownStyle={{ background: '#21262d' }}
                                    options={[
                                        { value: 'circle', label: 'Circle' },
                                        { value: 'star', label: 'Star' },
                                        { value: 'heart', label: 'Heart' },
                                        { value: 'hexagon', label: 'Hexagon' },
                                        { value: 'triangle', label: 'Triangle' },
                                        ...savedGooShapes.map((s, i) => ({
                                            value: s.id,
                                            label: `Goo ${i + 1}`
                                        }))
                                    ]}
                                />
                                <Text style={{ color: '#8b949e', fontSize: 12 }}>Size:</Text>
                                <Slider
                                    min={20}
                                    max={200}
                                    value={punchShapeSize}
                                    onChange={setPunchShapeSize}
                                    style={{ width: 80 }}
                                />
                                <Text style={{ color: '#58a6ff', fontSize: 12 }}>Click on image to punch</Text>
                            </Space>
                        )}


                        {/* Text Prompt Input - show when TEXT_SELECT is active */}
                        {activeTool === TOOLS.TEXT_SELECT && (
                            <>
                                <Select
                                    value={segModel}
                                    onChange={setSegModel}
                                    style={{ width: 130, background: '#21262d' }}
                                    dropdownStyle={{ background: '#21262d' }}
                                    options={[
                                        { value: 'moondream', label: 'Moondream' },
                                        { value: 'sam3', label: 'SAM 3' },
                                        { value: 'qwenLayered', label: 'Qwen Layers' },
                                    ]}
                                />
                                {segModel !== 'qwenLayered' && (
                                    <Input
                                        placeholder="e.g., the red car"
                                        value={promptText}
                                        onChange={(e) => setPromptText(e.target.value)}
                                        onPressEnter={segModel === 'sam3' ? performSam3Segmentation : performPromptSegmentation}
                                        style={{
                                            width: 160,
                                            background: '#21262d',
                                            borderColor: '#30363d',
                                            color: '#c9d1d9'
                                        }}
                                    />
                                )}
                                {segModel === 'sam3' && (
                                    <InputNumber
                                        min={1}
                                        max={32}
                                        value={sam3MaxMasks}
                                        onChange={(v) => { if (v) setSam3MaxMasks(v); }}
                                        style={{ width: 70, background: '#21262d', borderColor: '#30363d' }}
                                        prefix={<span style={{ color: '#8b949e', fontSize: 11 }}>Masks</span>}
                                    />
                                )}
                                {segModel === 'qwenLayered' && (
                                    <InputNumber
                                        min={1}
                                        max={10}
                                        value={numLayers}
                                        onChange={(v) => { if (v) setNumLayers(v); }}
                                        style={{ width: 80, background: '#21262d', borderColor: '#30363d' }}
                                        prefix={<span style={{ color: '#8b949e', fontSize: 11 }}>Layers</span>}
                                    />
                                )}
                                <Button
                                    type="primary"
                                    onClick={
                                        segModel === 'qwenLayered' ? performQwenLayered
                                            : segModel === 'sam3' ? performSam3Segmentation
                                                : performPromptSegmentation
                                    }
                                    loading={loading}
                                    disabled={segModel !== 'qwenLayered' && !promptText.trim()}
                                    style={{
                                        background: segModel === 'qwenLayered' ? '#f59e0b'
                                            : segModel === 'sam3' ? '#a78bfa'
                                                : '#58a6ff'
                                    }}
                                >
                                    {segModel === 'qwenLayered' ? 'Decompose' : 'Cut'}
                                </Button>
                            </>
                        )}

                        {/* Segmentation Actions */}
                        {segmentPoints.length > 0 && (
                            <>
                                <Button
                                    type="primary"
                                    onClick={performSegmentation}
                                    loading={loading}
                                    style={{ background: '#a78bfa' }}
                                >
                                    Cut ({segmentPoints.length} points)
                                </Button>
                                <Tooltip title="Clear all points">
                                    <Button
                                        type="text"
                                        danger
                                        onClick={() => setSegmentPoints([])}
                                        style={{ color: '#f85149' }}
                                    >
                                        Clear
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                    </Space>

                    {/* Canvas Controls */}
                    <Space size={4}>
                        <Divider type="vertical" style={{ background: '#30363d', height: 24 }} />
                        <Tooltip title="Recenter Canvas">
                            <Button
                                type="text"
                                icon={<FullscreenOutlined />}
                                onClick={recenterCanvas}
                                style={{ color: '#8b949e' }}
                            />
                        </Tooltip>
                        <Select
                            value={canvasAspectRatio}
                            onChange={changeAspectRatio}
                            style={{ width: 150 }}
                            dropdownStyle={{ background: '#161b22', border: '1px solid #30363d' }}
                            options={(() => {
                                const groups = {};
                                ASPECT_PRESETS.forEach(p => {
                                    const g = p.group || 'Other';
                                    if (!groups[g]) groups[g] = [];
                                    const dimLabel = p.w ? ` (${p.w}×${p.h})` : '';
                                    groups[g].push({ value: p.key, label: `${p.label}${dimLabel}` });
                                });
                                return Object.entries(groups).map(([label, options]) => ({
                                    label, options
                                }));
                            })()}
                        />
                    </Space>

                    {/* Right Actions */}
                    <Space>
                        <Tooltip title="Export PNG">
                            <Button
                                type="text"
                                icon={<DownloadOutlined />}
                                onClick={() => handleExport('png')}
                                style={{ color: '#8b949e' }}
                            />
                        </Tooltip>
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'content',
                                        label: 'Content Only',
                                        onClick: () => handleAddToCanvas(false)
                                    },
                                    {
                                        key: 'artboard',
                                        label: 'Full Artboard',
                                        onClick: () => handleAddToCanvas(true)
                                    }
                                ]
                            }}
                            trigger={['click']}
                        >
                            <Button
                                type="primary"
                                icon={<PictureOutlined />}
                                loading={loading}
                            >
                                Add to Canvas ▾
                            </Button>
                        </Dropdown>
                    </Space>
                </div>

                {/* Main Content - Canvas with Floating Resource Panels */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
                    {/* Canvas Area */}
                    <div
                        ref={containerRef}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        style={{
                            flex: 1,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isDragging ? 'rgba(88, 166, 255, 0.1)' : '#0d1117',
                            border: isDragging ? '2px dashed #58a6ff' : '2px solid transparent',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {/* Floating Resource Panels - Bottom Left, Expand Upward */}
                        <div style={{
                            position: 'absolute',
                            bottom: 16,
                            left: 16,
                            top: 16,
                            display: 'flex',
                            flexDirection: 'column-reverse',
                            gap: 8,
                            zIndex: 100,
                            pointerEvents: 'none'
                        }}>
                            {/* Midjourney Panel - Bottom */}
                            <div style={{
                                pointerEvents: 'auto',
                                width: 300,
                                flex: midjourneyCollapsed ? '0 0 auto' : '1 1 0',
                                minHeight: midjourneyCollapsed ? 'auto' : 0,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <MidjourneyPanel
                                    canvasId={canvasId}
                                    onMediaClick={() => { }}
                                    collapsed={midjourneyCollapsed}
                                    onToggleCollapse={() => setMidjourneyCollapsed(!midjourneyCollapsed)}
                                    onAddToCanvas={handleAddToScrapbookCanvas}
                                />
                            </div>

                            {/* Pinterest Panel */}
                            <div style={{
                                pointerEvents: 'auto',
                                width: 300,
                                flex: pinterestCollapsed ? '0 0 auto' : '1 1 0',
                                minHeight: pinterestCollapsed ? 'auto' : 0,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <PinterestPanel
                                    canvasId={canvasId}
                                    onMediaClick={() => { }}
                                    collapsed={pinterestCollapsed}
                                    onToggleCollapse={() => setPinterestCollapsed(!pinterestCollapsed)}
                                    onAddToCanvas={handleAddToScrapbookCanvas}
                                />
                            </div>

                            {/* Assets Panel - Full UI with Tabs */}
                            <div style={{
                                pointerEvents: 'auto',
                                width: 300,
                                flex: resourceAssetsCollapsed ? '0 0 auto' : '1 1 0',
                                minHeight: resourceAssetsCollapsed ? 'auto' : 0,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{
                                    background: 'rgba(22, 27, 34, 0.95)',
                                    border: '1px solid #30363d',
                                    borderRadius: 12,
                                    backdropFilter: 'blur(8px)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: resourceAssetsCollapsed ? 'auto' : '100%',
                                    width: 300,
                                    overflow: 'hidden'
                                }}>
                                    {/* Header */}
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            borderBottom: resourceAssetsCollapsed ? 'none' : '1px solid #30363d',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setResourceAssetsCollapsed(!resourceAssetsCollapsed)}
                                    >
                                        <Space>
                                            <FolderOpenOutlined style={{ color: '#58a6ff' }} />
                                            <Text style={{ color: '#fff', fontWeight: 600 }}>Assets</Text>
                                        </Space>
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={resourceAssetsCollapsed ? <ExpandOutlined /> : <CompressOutlined />}
                                            onClick={(e) => { e.stopPropagation(); setResourceAssetsCollapsed(!resourceAssetsCollapsed); }}
                                            style={{ color: '#8b949e' }}
                                        />
                                    </div>

                                    {!resourceAssetsCollapsed && (
                                        <>
                                            {/* Main Tabs: Library / Inspiration */}
                                            <div style={{
                                                display: 'flex',
                                                gap: 0,
                                                borderBottom: '1px solid #30363d'
                                            }}>
                                                {[
                                                    { key: 'library', label: 'Library' },
                                                    { key: 'inspiration', label: 'Inspiration' },
                                                    { key: 'sref', label: 'Sref Styles' }
                                                ].map(tab => (
                                                    <button
                                                        key={tab.key}
                                                        onClick={() => setAssetsTab(tab.key)}
                                                        style={{
                                                            flex: 1,
                                                            padding: '10px 8px',
                                                            background: 'transparent',
                                                            border: 'none',
                                                            borderBottom: assetsTab === tab.key ? '2px solid #58a6ff' : '2px solid transparent',
                                                            color: assetsTab === tab.key ? '#58a6ff' : '#8b949e',
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Library Tab */}
                                            {assetsTab === 'library' && (
                                                <>
                                                    {/* Type Filter Tabs */}
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: 0,
                                                        borderBottom: '1px solid #30363d'
                                                    }}>
                                                        {[
                                                            { key: 'all', label: 'All' },
                                                            { key: 'images', label: 'Images' },
                                                            { key: 'videos', label: 'Videos' }
                                                        ].map(tab => (
                                                            <button
                                                                key={tab.key}
                                                                onClick={() => setAssetTypeFilter(tab.key)}
                                                                style={{
                                                                    flex: 1,
                                                                    padding: '8px 4px',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    borderBottom: assetTypeFilter === tab.key ? '2px solid #58a6ff' : '2px solid transparent',
                                                                    color: assetTypeFilter === tab.key ? '#c9d1d9' : '#6e7681',
                                                                    fontSize: 11,
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {tab.label}
                                                            </button>
                                                        ))}
                                                    </div>

                                                    {/* Source Filter */}
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: 4,
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #21262d',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        {[
                                                            { key: 'all', label: 'All' },
                                                            { key: 'generated', label: 'Generated' },
                                                            { key: 'uploads', label: 'Uploads' },
                                                            { key: 'cutouts', label: 'Cutouts' }
                                                        ].map(filter => (
                                                            <button
                                                                key={filter.key}
                                                                onClick={() => setSourceFilter(filter.key)}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    background: sourceFilter === filter.key ? 'rgba(88, 166, 255, 0.15)' : 'transparent',
                                                                    border: sourceFilter === filter.key ? '1px solid #58a6ff' : '1px solid #30363d',
                                                                    borderRadius: 6,
                                                                    color: sourceFilter === filter.key ? '#58a6ff' : '#8b949e',
                                                                    fontSize: 10,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {filter.label}
                                                            </button>
                                                        ))}
                                                    </div>

                                                    {/* Canvas Filter */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #21262d'
                                                    }}>
                                                        <Text style={{ color: '#8b949e', fontSize: 10 }}>Canvas:</Text>
                                                        <Select
                                                            size="small"
                                                            value={canvasFilter}
                                                            onChange={setCanvasFilter}
                                                            style={{ flex: 1, minWidth: 120 }}
                                                            options={[
                                                                { value: 'all', label: 'All Canvases' },
                                                                ...(canvasesData || []).map(c => ({
                                                                    value: c.id,
                                                                    label: c.title || c.name || `Canvas ${c.id.slice(0, 6)}`
                                                                }))
                                                            ]}
                                                        />
                                                    </div>

                                                    {/* Asset Grid */}
                                                    <div style={{
                                                        flex: 1,
                                                        overflowY: 'auto',
                                                        padding: 8
                                                    }}>
                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                                            gap: 6
                                                        }}>
                                                            {filteredMedia.length === 0 ? (
                                                                <div style={{ gridColumn: 'span 3', textAlign: 'center', color: '#484f58', padding: 20, fontSize: 11 }}>
                                                                    No media found
                                                                </div>
                                                            ) : (
                                                                filteredMedia.map((item) => (
                                                                    <div
                                                                        key={item.id}
                                                                        draggable
                                                                        onDragStart={(e) => {
                                                                            e.dataTransfer.setData('application/json', JSON.stringify({
                                                                                ...item,
                                                                                src: item.url,
                                                                                type: item.type || 'image'
                                                                            }));
                                                                            e.dataTransfer.effectAllowed = 'move';
                                                                        }}
                                                                        onClick={() => {
                                                                            if (item.type === 'image' || !item.type) {
                                                                                const isCutout = item.provider === 'scrapbook' ||
                                                                                    item.model?.includes('punch') ||
                                                                                    item.prompt?.toLowerCase().includes('cutout');
                                                                                addImageAsLayer(item.url, item.name || 'Asset', isCutout);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            position: 'relative',
                                                                            width: '100%',
                                                                            paddingBottom: '100%',
                                                                            borderRadius: 6,
                                                                            overflow: 'hidden',
                                                                            cursor: item.type === 'image' || !item.type ? 'pointer' : 'not-allowed',
                                                                            border: '1px solid #30363d',
                                                                            background: '#0d1117',
                                                                            transition: 'all 0.15s',
                                                                            opacity: item.type === 'image' || !item.type ? 1 : 0.6
                                                                        }}
                                                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#58a6ff'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                                                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.transform = 'scale(1)'; }}
                                                                    >
                                                                        {item.type === 'video' ? (
                                                                            <video src={item.url} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} muted preload="none" />
                                                                        ) : item.type === 'audio' ? (
                                                                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1c2128', color: '#eb2f96', fontSize: 20 }}>🎵</div>
                                                                        ) : (
                                                                            <img src={item.url} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
                                                                        )}
                                                                        {item.type && item.type !== 'image' && (
                                                                            <div style={{ position: 'absolute', top: 2, right: 2, padding: '1px 4px', background: item.type === 'video' ? 'rgba(24, 144, 255, 0.8)' : 'rgba(235, 47, 150, 0.8)', borderRadius: 3, fontSize: 8, color: '#fff', fontWeight: 600, textTransform: 'uppercase' }}>
                                                                                {item.type}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                        <div ref={mediaSentinelRef} style={{ height: 20 }} />
                                                        {isFetchingMoreMedia && (
                                                            <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                                                                <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* Inspiration Tab */}
                                            {assetsTab === 'inspiration' && (
                                                <>
                                                    {/* Folder Filter */}
                                                    <div style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #21262d'
                                                    }}>
                                                        <Select
                                                            value={inspirationFolderFilter}
                                                            onChange={setInspirationFolderFilter}
                                                            options={inspirationFolders}
                                                            style={{ width: '100%' }}
                                                            size="small"
                                                        />
                                                    </div>

                                                    {/* Inspiration Grid */}
                                                    <div style={{
                                                        flex: 1,
                                                        overflowY: 'auto',
                                                        padding: 8
                                                    }}>
                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                                            gap: 6
                                                        }}>
                                                            {filteredInspiration.length === 0 ? (
                                                                <div style={{ gridColumn: 'span 3', textAlign: 'center', color: '#484f58', padding: 20, fontSize: 11 }}>
                                                                    No inspiration media
                                                                </div>
                                                            ) : (
                                                                filteredInspiration.map((item) => (
                                                                    <div
                                                                        key={item.id}
                                                                        draggable
                                                                        onDragStart={(e) => {
                                                                            e.dataTransfer.setData('application/json', JSON.stringify({
                                                                                ...item,
                                                                                src: item.url,
                                                                                type: item.type || 'image'
                                                                            }));
                                                                            e.dataTransfer.effectAllowed = 'move';
                                                                        }}
                                                                        onClick={() => {
                                                                            if (item.type === 'image' || !item.type) {
                                                                                addImageAsLayer(item.url, item.label || 'Inspiration');
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            position: 'relative',
                                                                            width: '100%',
                                                                            paddingBottom: '100%',
                                                                            borderRadius: 6,
                                                                            overflow: 'hidden',
                                                                            cursor: 'pointer',
                                                                            border: '1px solid #30363d',
                                                                            background: '#0d1117',
                                                                            transition: 'all 0.15s'
                                                                        }}
                                                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#58a6ff'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                                                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.transform = 'scale(1)'; }}
                                                                    >
                                                                        <img src={item.url} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                        <div ref={inspirationSentinelRef} style={{ height: 20 }} />
                                                        {isFetchingMoreInspiration && (
                                                            <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                                                                <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* Sref Styles Tab */}
                                            {assetsTab === 'sref' && (
                                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                                    <SrefStylesTab
                                                        onMediaClick={(imageIndex, mediaItems) => {
                                                            // Show fullscreen preview for sref images
                                                            if (mediaItems && mediaItems.length > 0) {
                                                                setSrefPreviewIndex(imageIndex);
                                                                setSrefPreviewList(mediaItems);
                                                                setSrefPreviewVisible(true);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Drop zone overlay - always rendered, visibility controlled by CSS */}
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(88, 166, 255, 0.15)',
                            zIndex: 5,
                            pointerEvents: 'none',
                            opacity: isDragging ? 1 : 0,
                            transition: 'opacity 0.15s ease'
                        }}>
                            <div style={{
                                textAlign: 'center',
                                color: '#58a6ff'
                            }}>
                                <PictureOutlined style={{ fontSize: 48, marginBottom: 12 }} />
                                <div style={{ fontSize: 16, fontWeight: 600 }}>Drop image here</div>
                            </div>
                        </div>

                        {/* Empty state - always rendered, visibility controlled by CSS */}
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            opacity: (!loading && layers.length === 0 && !isDragging) ? 1 : 0,
                            transition: 'opacity 0.15s ease'
                        }}>
                            <div style={{ textAlign: 'center', color: '#484f58' }}>
                                <PictureOutlined style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }} />
                                <div style={{ fontSize: 14 }}>Drag & drop images from Assets</div>
                                <div style={{ fontSize: 12, marginTop: 4 }}>or click an image tile to add</div>
                            </div>
                        </div>

                        <canvas ref={canvasRef} />

                        {/* Point indicators - positioned relative to canvas element within container */}
                        {segmentPoints.map((point, idx) => {
                            // Get the actual canvas element offset within the container
                            const canvasEl = canvasRef.current;
                            const canvasLeft = canvasEl ? canvasEl.offsetLeft : 0;
                            const canvasTop = canvasEl ? canvasEl.offsetTop : 0;

                            // Transform scene coordinates to screen coordinates using viewport transform
                            const canvas = fabricRef.current;
                            let screenX = point.x;
                            let screenY = point.y;
                            if (canvas && canvas.viewportTransform) {
                                const vpt = canvas.viewportTransform;
                                // Apply viewport transform: x' = a*x + c*y + e, y' = b*x + d*y + f
                                screenX = vpt[0] * point.x + vpt[2] * point.y + vpt[4];
                                screenY = vpt[1] * point.x + vpt[3] * point.y + vpt[5];
                            }

                            return (
                                <div
                                    key={idx}
                                    style={{
                                        position: 'absolute',
                                        left: canvasLeft + screenX - 6,
                                        top: canvasTop + screenY - 6,
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        background: point.label === 1 ? '#22c55e' : '#ef4444',
                                        border: '2px solid white',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        pointerEvents: 'none',
                                        zIndex: 20
                                    }}
                                />
                            );
                        })}

                        {/* Cutout Preview - Left of layers panel */}
                        {maskPreview && (
                            <div style={{
                                position: 'absolute',
                                right: 252,
                                top: 16,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                zIndex: 25,
                                animation: 'popIn 0.15s ease-out'
                            }}>
                                {/* Preview Image */}
                                <div style={{
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    border: isMaskInverted
                                        ? '2px solid #a855f7'
                                        : '2px solid #22c55e',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                                }}>
                                    <img
                                        src={isMaskInverted ? invertedMaskPreview : maskPreview}
                                        alt="Cutout Preview"
                                        style={{
                                            display: 'block',
                                            width: 140,
                                            height: 'auto',
                                            maxHeight: 160,
                                            objectFit: 'contain',
                                            background: 'repeating-conic-gradient(#21262d 0% 25%, #161b22 0% 50%) 50% / 8px 8px'
                                        }}
                                    />
                                </div>

                                {/* Controls */}
                                <div style={{
                                    display: 'flex',
                                    gap: 4,
                                    background: '#161b22',
                                    borderRadius: 6,
                                    padding: 4,
                                    border: '1px solid #30363d'
                                }}>
                                    <Tooltip title="Subject">
                                        <Button
                                            type="text"
                                            onClick={() => setIsMaskInverted(false)}
                                            icon={<ScissorOutlined />}
                                            style={{
                                                width: 28,
                                                height: 28,
                                                padding: 0,
                                                background: !isMaskInverted ? '#22c55e' : 'transparent',
                                                color: !isMaskInverted ? '#fff' : '#8b949e',
                                                border: 'none',
                                                borderRadius: 4
                                            }}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Background">
                                        <Button
                                            type="text"
                                            onClick={() => setIsMaskInverted(true)}
                                            icon={<PictureOutlined />}
                                            style={{
                                                width: 28,
                                                height: 28,
                                                padding: 0,
                                                background: isMaskInverted ? '#a855f7' : 'transparent',
                                                color: isMaskInverted ? '#fff' : '#8b949e',
                                                border: 'none',
                                                borderRadius: 4
                                            }}
                                        />
                                    </Tooltip>
                                    <div style={{ width: 1, background: '#30363d', margin: '2px 2px' }} />
                                    <Tooltip title="Add as Layer">
                                        <Button
                                            type="text"
                                            onClick={confirmCutout}
                                            loading={loading}
                                            icon={<CheckOutlined />}
                                            style={{
                                                width: 28,
                                                height: 28,
                                                padding: 0,
                                                background: isMaskInverted ? '#a855f7' : '#22c55e',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 4
                                            }}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                        <Button
                                            type="text"
                                            onClick={() => {
                                                setMaskPreview(null);
                                                setInvertedMaskPreview(null);
                                                setIsMaskInverted(false);
                                            }}
                                            icon={<CloseOutlined />}
                                            style={{
                                                width: 28,
                                                height: 28,
                                                padding: 0,
                                                background: '#21262d',
                                                color: '#8b949e',
                                                border: 'none',
                                                borderRadius: 4
                                            }}
                                        />
                                    </Tooltip>
                                </div>

                                <style>{`
                                @keyframes popIn {
                                    from { opacity: 0; transform: scale(0.95); }
                                    to { opacity: 1; transform: scale(1); }
                                }
                            `}</style>
                            </div>
                        )}

                        {/* Inpaint Prompt UI - Bottom Right */}
                        {activeTool === TOOLS.INPAINT && (
                            <div style={{
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                width: 320,
                                background: 'rgba(22, 27, 34, 0.95)',
                                border: '1px solid #30363d',
                                borderRadius: 8,
                                padding: 12,
                                zIndex: 25,
                                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)'
                            }}>
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                                        Inpaint Prompt
                                    </Text>
                                    <Input.TextArea
                                        placeholder="Describe what to generate in the masked area..."
                                        value={inpaintPrompt}
                                        onChange={(e) => setInpaintPrompt(e.target.value)}
                                        autoSize={{ minRows: 2, maxRows: 4 }}
                                        style={{
                                            background: '#0d1117',
                                            border: '1px solid #30363d',
                                            color: '#c9d1d9',
                                            borderRadius: 4
                                        }}
                                    />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Text style={{ color: '#8b949e', fontSize: 11 }}>
                                            Brush Size: {inpaintBrushSize}px
                                        </Text>
                                        <Tooltip title="Clear Mask">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<ClearOutlined />}
                                                onClick={clearInpaintMask}
                                                style={{ color: '#8b949e' }}
                                            />
                                        </Tooltip>
                                    </div>
                                    <Slider
                                        min={10}
                                        max={150}
                                        value={inpaintBrushSize}
                                        onChange={setInpaintBrushSize}
                                        style={{ margin: 0 }}
                                    />
                                </div>
                                <Button
                                    type="primary"
                                    block
                                    onClick={handleInpaint}
                                    loading={loading}
                                    style={{ background: '#a855f7', borderColor: '#a855f7' }}
                                >
                                    Generate Inpaint
                                </Button>
                            </div>
                        )}

                        {/* Floating Assets Panel Overlay */}
                        {showAssets && (
                            <div style={{
                                position: 'absolute',
                                left: 16,
                                top: 16,
                                bottom: 16,
                                width: 360,
                                background: 'rgba(22, 27, 34, 0.98)',
                                borderRadius: 12,
                                border: '1px solid #30363d',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                                display: 'flex',
                                flexDirection: 'column',
                                zIndex: 20,
                                overflow: 'hidden'
                            }}>
                                {/* Header */}
                                <div style={{
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #30363d',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Text style={{ color: '#c9d1d9', fontSize: 14, fontWeight: 600 }}>Assets</Text>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseOutlined />}
                                        onClick={() => setShowAssets(false)}
                                        style={{ color: '#8b949e' }}
                                    />
                                </div>

                                {/* Main Tabs: Library / Inspiration */}
                                <div style={{
                                    display: 'flex',
                                    gap: 0,
                                    borderBottom: '1px solid #30363d'
                                }}>
                                    {[
                                        { key: 'library', label: 'Library' },
                                        { key: 'inspiration', label: 'Inspiration' },
                                        { key: 'sref', label: 'Sref Styles' }
                                    ].map(tab => (
                                        <button
                                            key={tab.key}
                                            onClick={() => setAssetsTab(tab.key)}
                                            style={{
                                                flex: 1,
                                                padding: '10px 8px',
                                                background: 'transparent',
                                                border: 'none',
                                                borderBottom: assetsTab === tab.key ? '2px solid #58a6ff' : '2px solid transparent',
                                                color: assetsTab === tab.key ? '#58a6ff' : '#8b949e',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Library Tab Content */}
                                {assetsTab === 'library' && (
                                    <>
                                        {/* Type Tabs */}
                                        <div style={{
                                            display: 'flex',
                                            gap: 0,
                                            borderBottom: '1px solid #30363d'
                                        }}>
                                            {[
                                                { key: 'all', label: 'All' },
                                                { key: 'images', label: 'Images' },
                                                { key: 'videos', label: 'Videos' },
                                                { key: 'audio', label: 'Audio' }
                                            ].map(tab => (
                                                <button
                                                    key={tab.key}
                                                    onClick={() => setAssetTypeFilter(tab.key)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '10px 8px',
                                                        background: 'transparent',
                                                        border: 'none',
                                                        borderBottom: assetTypeFilter === tab.key ? '2px solid #58a6ff' : '2px solid transparent',
                                                        color: assetTypeFilter === tab.key ? '#58a6ff' : '#8b949e',
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Source Filter */}
                                        <div style={{
                                            padding: '8px 12px',
                                            borderBottom: '1px solid #21262d',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8
                                        }}>
                                            <Text style={{ color: '#8b949e', fontSize: 11 }}>Source:</Text>
                                            <select
                                                value={sourceFilter}
                                                onChange={(e) => setSourceFilter(e.target.value)}
                                                style={{
                                                    flex: 1,
                                                    padding: '4px 8px',
                                                    background: '#21262d',
                                                    border: '1px solid #30363d',
                                                    borderRadius: 4,
                                                    color: '#c9d1d9',
                                                    fontSize: 11,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="all">All Sources</option>
                                                <option value="generated">Generated</option>
                                                <option value="uploads">Uploads</option>
                                                <option value="cutouts">Cutouts</option>
                                            </select>
                                            <Text style={{ color: '#484f58', fontSize: 10 }}>
                                                {filteredMedia.length} items
                                            </Text>
                                        </div>
                                    </>
                                )}

                                {/* Inspiration Tab Content - Folder Filter */}
                                {assetsTab === 'inspiration' && (
                                    <div style={{
                                        padding: '8px 12px',
                                        borderBottom: '1px solid #21262d',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}>
                                        <Text style={{ color: '#8b949e', fontSize: 11 }}>Folder:</Text>
                                        <select
                                            value={inspirationFolderFilter}
                                            onChange={(e) => setInspirationFolderFilter(e.target.value)}
                                            style={{
                                                flex: 1,
                                                padding: '4px 8px',
                                                background: '#21262d',
                                                border: '1px solid #30363d',
                                                borderRadius: 4,
                                                color: '#c9d1d9',
                                                fontSize: 11,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {inspirationFolders.map(folder => (
                                                <option key={folder.value} value={folder.value}>{folder.label}</option>
                                            ))}
                                        </select>
                                        <Text style={{ color: '#484f58', fontSize: 10 }}>
                                            {filteredInspiration.length} items
                                        </Text>
                                    </div>
                                )}

                                {/* Media Grid */}
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    padding: 12
                                }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: 12
                                    }}>
                                        {/* Library Tab Grid */}
                                        {assetsTab === 'library' && (
                                            filteredMedia.length === 0 ? (
                                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#484f58', padding: 20, fontSize: 12 }}>
                                                    No media found
                                                </div>
                                            ) : (
                                                filteredMedia.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        draggable
                                                        onDragStart={(e) => {
                                                            e.dataTransfer.setData('application/json', JSON.stringify({
                                                                ...item,
                                                                src: item.url,
                                                                type: item.type || 'image'
                                                            }));
                                                            e.dataTransfer.effectAllowed = 'move';
                                                        }}
                                                        onClick={() => {
                                                            if (item.type === 'image' || !item.type) {
                                                                // Detect if this is a cutout by checking provider/model/prompt
                                                                const isCutout = item.provider === 'scrapbook' ||
                                                                    item.model?.includes('punch') ||
                                                                    item.prompt?.toLowerCase().includes('cutout');
                                                                addImageAsLayer(item.url, item.name || 'Asset', isCutout);
                                                            }
                                                        }}
                                                        style={{
                                                            position: 'relative',
                                                            width: '100%',
                                                            paddingBottom: '100%',
                                                            borderRadius: 8,
                                                            overflow: 'hidden',
                                                            border: '1px solid #30363d',
                                                            cursor: item.type === 'image' || !item.type ? 'pointer' : 'not-allowed',
                                                            transition: 'all 0.2s ease',
                                                            opacity: item.type === 'image' || !item.type ? 1 : 0.6
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.borderColor = '#58a6ff';
                                                            e.currentTarget.style.transform = 'scale(1.03)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.borderColor = '#30363d';
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        {item.type === 'video' ? (
                                                            <video
                                                                src={item.url}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                                muted
                                                                preload="none"
                                                            />
                                                        ) : item.type === 'audio' ? (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: 'linear-gradient(135deg, #1c2128 0%, #161b22 100%)',
                                                                color: '#eb2f96',
                                                                fontSize: 24
                                                            }}>
                                                                🎵
                                                            </div>
                                                        ) : (
                                                            <img
                                                                src={item.url}
                                                                alt={item.name || 'Asset'}
                                                                loading="lazy"
                                                                decoding="async"
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                                draggable={false}
                                                            />
                                                        )}
                                                        {/* Type badge for non-images */}
                                                        {item.type && item.type !== 'image' && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 4,
                                                                right: 4,
                                                                padding: '2px 6px',
                                                                background: item.type === 'video' ? 'rgba(24, 144, 255, 0.8)' : 'rgba(235, 47, 150, 0.8)',
                                                                borderRadius: 4,
                                                                fontSize: 9,
                                                                color: '#fff',
                                                                fontWeight: 600,
                                                                textTransform: 'uppercase'
                                                            }}>
                                                                {item.type}
                                                            </div>
                                                        )}
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            padding: '6px 8px',
                                                            background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)'
                                                        }}>
                                                            <div style={{
                                                                color: '#fff',
                                                                fontSize: 10,
                                                                fontWeight: 500,
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {item.name || 'Asset'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )
                                        )}

                                        {/* Library scroll sentinel */}
                                        {assetsTab === 'library' && (
                                            <>
                                                <div ref={mediaSentinelRef} style={{ height: 20, gridColumn: '1 / -1' }} />
                                                {isFetchingMoreMedia && (
                                                    <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: 12 }}>
                                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Inspiration Tab Grid */}
                                        {assetsTab === 'inspiration' && (
                                            filteredInspiration.length === 0 ? (
                                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#484f58', padding: 20, fontSize: 12 }}>
                                                    No inspiration media yet. Upload from the Dashboard!
                                                </div>
                                            ) : (
                                                filteredInspiration.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        draggable
                                                        onDragStart={(e) => {
                                                            e.dataTransfer.setData('application/json', JSON.stringify({
                                                                ...item,
                                                                src: item.url,
                                                                type: item.type || 'image'
                                                            }));
                                                            e.dataTransfer.effectAllowed = 'move';
                                                        }}
                                                        onClick={() => {
                                                            if (item.type === 'image' || !item.type) {
                                                                addImageAsLayer(item.url, item.label || 'Inspiration');
                                                            }
                                                        }}
                                                        style={{
                                                            position: 'relative',
                                                            width: '100%',
                                                            paddingBottom: '100%',
                                                            borderRadius: 8,
                                                            overflow: 'hidden',
                                                            border: '1px solid #30363d',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.borderColor = '#f0883e';
                                                            e.currentTarget.style.transform = 'scale(1.03)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.borderColor = '#30363d';
                                                            e.currentTarget.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        <img
                                                            src={item.url}
                                                            alt={item.label || 'Inspiration'}
                                                            loading="lazy"
                                                            decoding="async"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                            draggable={false}
                                                        />
                                                        {/* Folder badge */}
                                                        {item.folder && item.folder !== 'uncategorized' && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 4,
                                                                right: 4,
                                                                padding: '2px 6px',
                                                                background: 'rgba(240, 136, 62, 0.85)',
                                                                borderRadius: 4,
                                                                fontSize: 9,
                                                                color: '#fff',
                                                                fontWeight: 600,
                                                                textTransform: 'capitalize'
                                                            }}>
                                                                {item.folder}
                                                            </div>
                                                        )}
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            padding: '6px 8px',
                                                            background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)'
                                                        }}>
                                                            <div style={{
                                                                color: '#fff',
                                                                fontSize: 10,
                                                                fontWeight: 500,
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {item.label || 'Inspiration'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )
                                        )}

                                        {/* Inspiration scroll sentinel */}
                                        {assetsTab === 'inspiration' && (
                                            <>
                                                <div ref={inspirationSentinelRef} style={{ height: 20, gridColumn: '1 / -1' }} />
                                                {isFetchingMoreInspiration && (
                                                    <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: 12 }}>
                                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Sref Styles Tab */}
                                        {assetsTab === 'sref' && (
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <SrefStylesTab
                                                    onMediaClick={(imageIndex, mediaItems) => {
                                                        // Show fullscreen preview for sref images
                                                        if (mediaItems && mediaItems.length > 0) {
                                                            setSrefPreviewIndex(imageIndex);
                                                            setSrefPreviewList(mediaItems);
                                                            setSrefPreviewVisible(true);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Floating Layers Panel Overlay */}
                        <div style={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            width: 280,
                            maxHeight: 'calc(100% - 32px)',
                            background: 'rgba(22, 27, 34, 0.98)',
                            borderRadius: 12,
                            border: '1px solid #30363d',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            flexDirection: 'column',
                            zIndex: 20,
                            overflow: 'hidden'
                        }}>
                            <div style={{ padding: '14px 16px', borderBottom: '1px solid #30363d' }}>
                                <Text style={{ color: '#c9d1d9', fontSize: 14, fontWeight: 600 }}>Layers</Text>
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                                {layers.slice().reverse().map((layer, reversedIndex) => {
                                    const actualIndex = layers.length - 1 - reversedIndex;
                                    const isSelected = selectedLayerIds.includes(layer.id) || selectedLayer === layer.id;
                                    return (
                                        <div
                                            key={layer.id}
                                            onClick={(e) => handleLayerClick(e, layer)}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                padding: '8px 12px',
                                                borderRadius: 6,
                                                background: isSelected ? 'rgba(167, 139, 250, 0.2)' : 'transparent',
                                                border: isSelected ? '1px solid #a78bfa' : '1px solid transparent',
                                                cursor: 'pointer',
                                                marginBottom: 4
                                            }}
                                        >
                                            {/* Layer header row */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Tooltip title={layer.visible ? 'Hide' : 'Show'}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={layer.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                        onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}
                                                        style={{ color: layer.visible ? '#8b949e' : '#484f58', padding: 0, width: 20, height: 20 }}
                                                    />
                                                </Tooltip>
                                                <Tooltip title={layer.locked ? 'Unlock' : 'Lock'}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={layer.locked ? <LockOutlined /> : <UnlockOutlined />}
                                                        onClick={(e) => { e.stopPropagation(); toggleLayerLock(layer.id); }}
                                                        style={{ color: layer.locked ? '#f0883e' : '#8b949e', padding: 0, width: 20, height: 20 }}
                                                    />
                                                </Tooltip>
                                                <Text
                                                    style={{
                                                        flex: 1,
                                                        color: layer.visible ? '#c9d1d9' : '#484f58',
                                                        fontSize: 12,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {layer.name}
                                                </Text>
                                                {loadingLayers[layer.id] && (
                                                    <Spin size="small" style={{ marginLeft: 4 }} />
                                                )}
                                                {/* Reorder buttons */}
                                                <Tooltip title="Move Up">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        disabled={actualIndex === layers.length - 1}
                                                        onClick={(e) => { e.stopPropagation(); moveLayerUp(layer.id); }}
                                                        style={{ color: '#8b949e', padding: 0, width: 18, height: 18, fontSize: 10 }}
                                                    >
                                                        ▲
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Move Down">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        disabled={actualIndex === 0}
                                                        onClick={(e) => { e.stopPropagation(); moveLayerDown(layer.id); }}
                                                        style={{ color: '#8b949e', padding: 0, width: 18, height: 18, fontSize: 10 }}
                                                    >
                                                        ▼
                                                    </Button>
                                                </Tooltip>
                                                {layer.name !== 'Background' && (
                                                    <Tooltip title="Delete">
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<DeleteOutlined />}
                                                            onClick={(e) => { e.stopPropagation(); deleteLayer(layer.id); }}
                                                            style={{ color: '#f85149', padding: 0, width: 20, height: 20 }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {/* Opacity slider - show if this specific layer is selected */}
                                            {isSelected && (
                                                <div style={{ marginTop: 8, paddingLeft: 4 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Text style={{ color: '#6e7681', fontSize: 10, minWidth: 40 }}>Opacity</Text>
                                                        <Slider
                                                            min={0}
                                                            max={100}
                                                            value={Math.round((layer.opacity || 1) * 100)}
                                                            onChange={(val) => {
                                                                const newOpacity = val / 100;
                                                                layer.object.opacity = newOpacity;
                                                                fabricRef.current?.renderAll();
                                                                setLayers(prev => prev.map(l =>
                                                                    l.id === layer.id ? { ...l, opacity: newOpacity } : l
                                                                ));
                                                            }}
                                                            style={{ flex: 1, margin: 0 }}
                                                            tooltip={{ formatter: (val) => `${val}%` }}
                                                        />
                                                    </div>
                                                    {/* Flip controls */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                                        <Text style={{ color: '#6e7681', fontSize: 10, minWidth: 40 }}>Flip</Text>
                                                        <Tooltip title="Flip Horizontal">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (layer.object) {
                                                                        layer.object.set('flipX', !layer.object.flipX);
                                                                        fabricRef.current?.renderAll();
                                                                    }
                                                                }}
                                                                style={{
                                                                    color: layer.object?.flipX ? '#a78bfa' : '#8b949e',
                                                                    fontSize: 11,
                                                                    padding: '2px 8px',
                                                                    height: 24,
                                                                    border: layer.object?.flipX ? '1px solid #a78bfa' : '1px solid #30363d',
                                                                    borderRadius: 4
                                                                }}
                                                            >
                                                                ↔ X
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title="Flip Vertical">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (layer.object) {
                                                                        layer.object.set('flipY', !layer.object.flipY);
                                                                        fabricRef.current?.renderAll();
                                                                    }
                                                                }}
                                                                style={{
                                                                    color: layer.object?.flipY ? '#a78bfa' : '#8b949e',
                                                                    fontSize: 11,
                                                                    padding: '2px 8px',
                                                                    height: 24,
                                                                    border: layer.object?.flipY ? '1px solid #a78bfa' : '1px solid #30363d',
                                                                    borderRadius: 4
                                                                }}
                                                            >
                                                                ↕ Y
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {layers.length === 0 && (
                                    <div style={{ textAlign: 'center', color: '#484f58', padding: 20, fontSize: 12 }}>
                                        No layers yet
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Goo Editor Panel - positioned absolute within the content area */}
                <GooEditorPanel
                    visible={gooEditorVisible}
                    onClose={() => setGooEditorVisible(false)}
                    onSaveSVG={async (svgString, svgDataUrl) => {
                        try {
                            const uploadRes = await api.post('/storage/upload-url', {
                                url: svgDataUrl,
                                bucket: 'media-generations'
                            });
                            const url = uploadRes.data?.url || svgDataUrl;

                            await api.post('/media-generations', {
                                generation_id: `goo-editor-${Date.now()}`,
                                canvas_id: canvasId,
                                type: 'image',
                                url,
                                provider: 'scrapbook',
                                model: 'goo-editor',
                                prompt: 'Goo Shape',
                                metadata: { format: 'svg' }
                            });

                            messageApi.success('Goo SVG saved to assets!');
                        } catch (error) {
                            console.error('Failed to save goo SVG:', error);
                            messageApi.error('Failed to save SVG');
                        }
                    }}
                    onSaveToPunches={async (svgString, thumbnailUrl) => {
                        try {
                            // Upload SVG to storage
                            const uploadRes = await api.post('/storage/upload-url', {
                                url: thumbnailUrl,
                                bucket: 'media-generations'
                            });
                            const url = uploadRes.data?.url || thumbnailUrl;

                            const genId = `goo-punch-${Date.now()}`;

                            // Save to media-generations collection
                            await api.post('/media-generations', {
                                generation_id: genId,
                                canvas_id: canvasId,
                                type: 'image',
                                url,
                                provider: 'scrapbook',
                                model: 'goo-punch',
                                prompt: 'Goo Punch Shape',
                                metadata: { format: 'svg', svgString }
                            });

                            const newShape = {
                                id: genId,
                                svgString,
                                thumbnailUrl: url
                            };
                            setSavedGooShapes(prev => [...prev, newShape]);
                            setSelectedPunchShape(newShape.id);
                            setActiveTool(TOOLS.STAMP_PUNCH);
                            messageApi.success('Goo shape saved to punch list!');
                        } catch (error) {
                            console.error('Failed to save goo punch shape:', error);
                            messageApi.error('Failed to save punch shape');
                        }
                    }}
                />

                {/* Typography Presets Panel */}
                <TypographyPresetsPanel
                    visible={typographyPanelVisible && !activeEffect}
                    onClose={() => setTypographyPanelVisible(false)}
                    fabricRef={fabricRef}
                    typoGrid={typoGrid}
                    onTypoGridChange={(newGrid) => {
                        setTypoGrid(newGrid);
                        fabricRef.current?.requestRenderAll();
                    }}
                    onLayersAdd={(newLayers) => {
                        setLayers(prev => [...prev, ...newLayers]);
                        if (newLayers.length > 0) {
                            setSelectedLayer(newLayers[newLayers.length - 1].id);
                        }
                    }}
                />

                {/* Type Controls Panel */}
                <TypeControlsPanel
                    visible={(activeTool === TOOLS.TYPE || !!selectedTextObject) && !activeEffect}
                    textObject={selectedTextObject}
                    onClose={() => {
                        setSelectedTextObject(null);
                        if (activeTool === TOOLS.TYPE) setActiveTool(TOOLS.SELECT);
                    }}
                    onUpdate={(prop, value) => {
                        const obj = selectedTextObject;
                        if (!obj || !fabricRef.current) return;
                        const canvas = fabricRef.current;

                        // Effects properties don't need to exit editing mode
                        const isEffect = ['stroke', 'strokeWidth', 'shadow', 'textBackgroundColor'].includes(prop);

                        // Exit editing mode before changing content/style properties to avoid stale state
                        const wasEditing = obj.isEditing;
                        if (wasEditing && !isEffect) obj.exitEditing();

                        if (prop === 'textTransform') {
                            obj._textTransform = value;
                            const rawText = obj._rawText || obj.text;
                            obj._rawText = rawText;
                            if (value === 'uppercase') {
                                obj.set('text', rawText.toUpperCase());
                            } else if (value === 'lowercase') {
                                obj.set('text', rawText.toLowerCase());
                            } else {
                                obj.set('text', rawText);
                            }
                        } else {
                            obj.set(prop, value);
                        }
                        // Clear internal text cache, recalculate dimensions, re-render
                        if (obj._clearCache) obj._clearCache();
                        obj.dirty = true;
                        if (obj.initDimensions) obj.initDimensions();
                        obj.setCoords();
                        canvas.requestRenderAll();

                        // Re-enter editing if we were editing
                        if (wasEditing) {
                            obj.enterEditing();
                        }
                    }}
                />

                {/* Effects Panel (drawer) */}
                <EffectsPanel
                    visible={effectsPanelVisible && !activeEffect}
                    onClose={() => setEffectsPanelVisible(false)}
                    hasSelectedLayer={!!selectedLayer}
                    onSelectEffect={(effectId) => {
                        if (!selectedLayer) {
                            messageApi.warning('Select a layer first');
                            return;
                        }
                        setActiveEffect(effectId);
                        setEffectsPanelVisible(false);
                    }}
                />

                {/* Shared effect props */}
                {(() => {
                    const getSourceImageUrl = () => {
                        if (!selectedLayer) return null;
                        const layer = layers.find(l => l.id === selectedLayer);
                        if (!layer || !layer.object) return null;
                        const el = layer.object.getElement?.() || layer.object._element;
                        if (el && el.src) return el.src;
                        // For trimmed cutouts (canvas element, no .src), export actual content
                        if (el && el.toDataURL) {
                            return el.toDataURL('image/png');
                        }
                        return layer.sourceUrl || null;
                    };
                    const getProxyUrl = () => {
                        if (!selectedLayer) return null;
                        const layer = layers.find(l => l.id === selectedLayer);
                        if (!layer || !layer.object) return null;
                        const el = layer.object.getElement?.() || layer.object._element;
                        if (el && el.src) return el.src;
                        // For trimmed cutouts (canvas element, no .src), export actual content
                        if (el && el.toDataURL) {
                            return el.toDataURL('image/png');
                        }
                        if (layer.sourceUrl && (layer.sourceUrl.includes('storage.googleapis.com') || layer.sourceUrl.includes('firebasestorage'))) {
                            const tokenParam = token ? `&token=${token}` : '';
                            return `${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(layer.sourceUrl)}${tokenParam}`;
                        }
                        return layer.sourceUrl || null;
                    };
                    const handleEffectApply = async (dataUrl, effectName) => {
                        if (!fabricRef.current || !selectedLayer) return;
                        try {
                            const layer = layers.find(l => l.id === selectedLayer);
                            if (!layer) return;
                            const newImg = await FabricImage.fromURL(dataUrl, { crossOrigin: 'anonymous' });
                            if (!newImg) return;
                            const canvas = fabricRef.current;
                            const oldObj = layer.object;
                            // Match the visual (on-canvas) size of the old object
                            const visualW = oldObj.getScaledWidth();
                            const visualH = oldObj.getScaledHeight();
                            newImg.set({
                                left: oldObj.left,
                                top: oldObj.top,
                                scaleX: visualW / newImg.width,
                                scaleY: visualH / newImg.height,
                                angle: oldObj.angle,
                                originX: oldObj.originX,
                                originY: oldObj.originY,
                                flipX: oldObj.flipX,
                                flipY: oldObj.flipY,
                                opacity: oldObj.opacity,
                                selectable: true,
                            });
                            const zIndex = canvas.getObjects().indexOf(oldObj);
                            canvas.remove(oldObj);
                            canvas.insertAt(zIndex, newImg);
                            canvas.setActiveObject(newImg);
                            canvas.renderAll();
                            setLayers(prev => prev.map(l =>
                                l.id === selectedLayer
                                    ? { ...l, object: newImg, sourceUrl: dataUrl }
                                    : l
                            ));
                            setActiveEffect(null);
                            messageApi.success(`${effectName} applied!`);
                        } catch (error) {
                            console.error(`Failed to apply ${effectName}:`, error);
                            messageApi.error(`Failed to apply ${effectName}`);
                        }
                    };

                    const sharedProps = {
                        sourceImageUrl: getSourceImageUrl(),
                        proxyUrl: getProxyUrl(),
                        onClose: () => setActiveEffect(null),
                    };

                    return (
                        <>
                            <HalftoneEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'halftone'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Halftone')}
                                canvasId={canvasId}
                            />
                            <DitherEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'dither'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Dither')}
                                canvasId={canvasId}
                            />
                            <GradientMapEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'gradient-map'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Gradient Map')}
                                canvasId={canvasId}
                            />
                            <ChromaticAberrationEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'chromatic'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Chromatic Aberration')}
                            />
                            <GrainEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'grain'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Grain')}
                            />
                            <GlitchEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'glitch'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Glitch')}
                            />
                            <AsciiEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'ascii'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'ASCII Art')}
                                canvasId={canvasId}
                            />
                            <PatternEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'pattern'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Pattern')}
                                canvasId={canvasId}
                            />
                            <TopoLineEditorPanel
                                {...sharedProps}
                                visible={activeEffect === 'topographic'}
                                onApply={(dataUrl) => handleEffectApply(dataUrl, 'Topographic')}
                                canvasId={canvasId}
                            />
                        </>
                    );
                })()}

                {/* Standalone Pattern Maker */}
                <PatternEditorPanel
                    visible={patternStandaloneVisible}
                    onClose={() => setPatternStandaloneVisible(false)}
                    sourceImageUrl={null}
                    proxyUrl={null}
                    canvasId={canvasId}
                    onAddAsLayer={async (dataUrl) => {
                        addImageAsLayer(dataUrl, 'Pattern');
                        setPatternStandaloneVisible(false);
                    }}
                />

                {/* Standalone Topo Line Generator */}
                <TopoLineEditorPanel
                    visible={topoLineVisible}
                    onClose={() => setTopoLineVisible(false)}
                    sourceImageUrl={null}
                    proxyUrl={null}
                    canvasId={canvasId}
                    onAddAsLayer={async (dataUrl) => {
                        addImageAsLayer(dataUrl, 'Topographic');
                        setTopoLineVisible(false);
                    }}
                />

                {/* Sref Preview Modal */}
                <MediaPreviewModal
                    visible={srefPreviewVisible}
                    onClose={() => setSrefPreviewVisible(false)}
                    initialIndex={srefPreviewIndex}
                    mediaItems={srefPreviewList}
                />
            </div>
        </Drawer>
    );
};

export default ScrapbookPanel;
